import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import AssistenteFinanceiro from '../components/AssistenteFinanceiro';
import {
  PieChart, 
  Pie, 
  Cell, 
  Tooltip, 
  Legend, 
  ResponsiveContainer,
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid,
  AreaChart,
  Area,
  LineChart,
  Line,
  ReferenceLine,
  ComposedChart,
} from 'recharts';
import { notificar } from '../utils/notify';
import '../styles/fmp.css';
import FluxoCaixaDiarioChart from '../components/FluxoCaixaDiarioChart';
import logoComece from '../assets/logo-comece.svg';

const formatarValor = (valor) => {
  if (!valor && valor !== 0) return 'R$ 0,00';
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(parseFloat(valor));
};

// Remove os códigos numéricos do início do nome da categoria
// Ex.: "2.10.99 - 2.2.4.1. ERP" -> "ERP" | "1.05.98 - (x) 1.1.3. Assinatur..." -> "Assinatur..."
const limparCategoria = (cat) => {
  if (!cat) return '';
  const limpo = String(cat).replace(/^(\s*(\(x\))?\s*[\d.]+\s*[-.]?\s*)+/i, '').trim();
  return limpo || String(cat);
};

// Breve resumo de cada imposto, para quem não conhece a sigla
const descricaoImposto = (nome) => {
  const n = (nome || '').toLowerCase();
  if (/simples|\bdas\b/.test(n)) return 'Simples Nacional (DAS): guia única que reúne vários tributos (IRPJ, CSLL, PIS, COFINS, ISS/ICMS, INSS) das micro e pequenas empresas.';
  if (/icms/.test(n)) return 'ICMS: imposto estadual sobre circulação de mercadorias e alguns serviços (transporte, comunicação, energia).';
  if (/iss|issqn/.test(n)) return 'ISS: imposto municipal sobre a prestação de serviços.';
  if (/cofins/.test(n)) return 'COFINS: contribuição federal sobre o faturamento, destinada à seguridade social.';
  if (/pis|pasep/.test(n)) return 'PIS: contribuição federal sobre o faturamento (seguro-desemprego e abono salarial).';
  if (/csll/.test(n)) return 'CSLL: contribuição social sobre o lucro líquido da empresa (federal).';
  if (/irpj|irrf|imposto de renda|\bir\b/.test(n)) return 'Imposto de Renda: tributo federal sobre o lucro (IRPJ) ou retido na fonte (IRRF).';
  if (/inss|previd|\bcpp\b/.test(n)) return 'INSS/Previdência: contribuição previdenciária sobre a folha de pagamento e o pró-labore.';
  if (/fgts/.test(n)) return 'FGTS: depósito mensal do empregador na conta do trabalhador (8% da remuneração).';
  if (/ipi/.test(n)) return 'IPI: imposto federal sobre produtos industrializados.';
  if (/ipva/.test(n)) return 'IPVA: imposto estadual sobre a propriedade de veículos.';
  if (/iptu/.test(n)) return 'IPTU: imposto municipal sobre a propriedade de imóveis urbanos.';
  if (/taxa/.test(n)) return 'Taxa: cobrança pública por um serviço específico (ex.: alvará, licenças, emissões).';
  return 'Tributo/imposto recolhido pela empresa no período.';
};

// Retorna [iniYM, fimYM] no formato 'AAAA-MM' para os últimos N meses (até o mês atual)
const rangeUltimosMeses = (n) => {
  const hoje = new Date();
  const fim = `${hoje.getFullYear()}-${String(hoje.getMonth() + 1).padStart(2, '0')}`;
  const ini = new Date(hoje.getFullYear(), hoje.getMonth() - (n - 1), 1);
  const iniYM = `${ini.getFullYear()}-${String(ini.getMonth() + 1).padStart(2, '0')}`;
  return [iniYM, fim];
};

const CORES_PIZZA = ['#00205b', '#00b5cc', '#f1be48', '#9063cd', '#1fa971', '#e5484d', '#7d8aa3'];
const MESES3 = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];

// Janela contínua de n meses terminando em `ref` (Date), com rótulos "mmm/aa"
const janelaMesesAte = (ref, n) => {
  const arr = [];
  for (let k = n - 1; k >= 0; k--) {
    const d = new Date(ref.getFullYear(), ref.getMonth() - k, 1);
    const ym = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    arr.push({ ym, label: `${MESES3[d.getMonth()]}/${String(d.getFullYear()).slice(-2)}` });
  }
  return arr;
};
// Últimos n meses até o mês atual
const janelaMeses = (n) => janelaMesesAte(new Date(), n);

// Agrega as métricas de uma conta de investimento apenas nos meses do conjunto ymSet
const agregarJanelaInvestimento = (conta, ymSet) => {
  const meses = (conta.dados_grafico_mensal || []).filter((d) => ymSet.has(d.mes));
  const soma = (k) => meses.reduce((s, d) => s + (d[k] || 0), 0);
  const totalEntradas = soma('entradas');
  const totalSaidas = soma('saidas');
  const totalRendimentos = soma('rendimentos');
  const totalImpostos = soma('impostos');
  const rendLiquido = totalRendimentos - totalImpostos;
  const ymDe = (dt) => { const p = (dt || '').split('/'); return p.length === 3 ? `${p[2]}-${p[1]}` : ''; };
  const movs = (conta.movimentos?.lista || []).filter((m) => ymSet.has(ymDe(m.data)));
  const contar = (tipo) => movs.filter((m) => m.tipo_movimento === tipo).length;
  const base = (conta.saldo_disponivel || 0) - rendLiquido;
  const rentab = base > 0 ? (rendLiquido / base) * 100 : 0;
  return {
    totalEntradas, totalSaidas, totalRendimentos, totalImpostos, rendLiquido, rentab,
    qtdTotal: movs.length,
    qtdEntradas: contar('Entradas'), qtdSaidas: contar('Saídas'),
    qtdRendimentos: contar('Rendimentos'), qtdImpostos: contar('Impostos'),
  };
};

// Nomes de tributos comuns — usados como fallback quando o cliente não tem um grupo
// "Impostos" classificado no DRE (ex.: contas com codigo_dre vazio). Evita "das"/"iss" soltos
// que dariam falso-positivo usando limites de palavra e a expressão "simples nacional".
const RE_IMPOSTO = /\b(impostos?|tributos?|pis|cofins|issqn|iss|icms|irpj|irrf|csll|inss|ipi|iof|darf|simples\s+nacional)\b/i;

// Encargo de folha que NÃO entra no card de impostos, por cliente.
// Pedido do Eleandro em 15/10/2026: INSS patronal é despesa com pessoal, não imposto.
// Só dá para separar onde a conta é exclusiva de INSS — é o caso da Deskbee (cliente 9,
// categoria 2.22.98 "2.6.1.2. INSS", R$ 152.560,35 em 2026). Nos demais clientes a conta
// é "INSS/IRRF" (encargo + imposto retido na MESMA categoria), e excluir levaria o IRRF
// junto; por isso a regra é por cliente e não global.
const EXCLUIR_DO_CARD_IMPOSTOS = {
  9: /\binss\b/i,
};

// Siglas tributárias que o RE_IMPOSTO não cobre, por cliente. Não entram no regex
// global para não mudar o card de quem não foi conferido.
// Deskbee (16/10/2026): CSRF (5952) é a outra fatia do DARF de IRRF — o título de
// R$ 3.291,31 de 18/09 é 53,5% IRRF + 46,5% CSRF, e sem isto os R$ 1.530,36 do CSRF
// sumiam do card. O FGTS (R$ 57.371,66 em 2026) fica de fora de propósito: é encargo
// de folha, mesmo critério que tirou o INSS daqui.
const INCLUIR_NO_CARD_IMPOSTOS = {
  9: /\bcsrf\b/i,
};

// Extrai as categorias-folha de impostos de uma resposta do dre-horizontal.
// Soma DUAS fontes e deduplica por código:
// 1) o GRUPO dedicado de impostos do DRE (nó com filhos rotulado "imposto"), quando existir;
// 2) as folhas cujo NOME parece um tributo (RE_IMPOSTO), em qualquer grupo do DRE.
//
// As duas são necessárias, e antes isto era um "ou" — se o grupo existisse, o nome nem era
// consultado. Na Deskbee (15/10/2026) o grupo "Impostos" só tem DAS, CSLL e IRPJ, que são
// trimestrais: PIS, COFINS, ISSQN, INSS e IRRF estão classificados em "Deduções de Receita",
// "Despesas com Pessoal" e "Outros Tributos" e sumiam do gráfico. Resultado: 4 dos 9 meses de
// 2026 apareciam zerados, e o total mostrava R$ 171 mil de R$ 926 mil efetivamente recolhidos.
// O caminho inverso também importa: o DAS só entra pelo grupo, porque RE_IMPOSTO evita `\bdas\b`
// de proposito (casaria com "despesas DAS vendas").
// O filtro por valor (>0) a jusante remove contas sem movimento/duplicadas.
const extrairFolhasImpostos = (resp, clienteId) => {
  if (!resp || !resp.tipos) return [];
  const excluir = EXCLUIR_DO_CARD_IMPOSTOS[Number(clienteId)];
  const incluir = INCLUIR_NO_CARD_IMPOSTOS[Number(clienteId)];
  const ehTributo = (rotulo) => RE_IMPOSTO.test(rotulo) || (incluir && incluir.test(rotulo));
  // No DRE departamentalizado (clientes com pilares) a folha física é o DEPARTAMENTO
  // (nó com `departamento`, label = nome do depto) e a categoria é o pai. O "tipo de
  // imposto" é a categoria: parar nela, que já traz `valores` somados dos departamentos.
  // Sem isso o card mostrava "Alocação / Residentes" como imposto e só a fatia de um depto.
  const ehFolhaLogica = (n) => !n.filhos || !n.filhos.length || n.filhos.some((f) => f && f.departamento);
  // O nó-categoria do DRE departamentalizado tem `codigo` interno ("cat::…"); o código
  // REAL da categoria (que o drill-down de lançamentos precisa) está nas folhas-departamento.
  const comCodigoReal = (n) => {
    const filho = (n.filhos || []).find((f) => f && f.departamento && f.codigo != null);
    return filho ? { ...n, codigo: filho.codigo } : n;
  };
  const coletar = (n, acc) => { if (!n) return; if (ehFolhaLogica(n)) acc.push(comCodigoReal(n)); else n.filhos.forEach((f) => coletar(f, acc)); };

  // 1) grupo dedicado de impostos (nó COM filhos)
  let grupo = null;
  const buscarGrupo = (n) => {
    if (n && n.filhos && n.filhos.length && n.label && /imposto/i.test(n.label)) {
      if (!grupo || Math.abs(n.total || 0) > Math.abs(grupo.total || 0)) grupo = n;
    }
    (n && n.filhos ? n.filhos : []).forEach(buscarGrupo);
  };
  (resp.tipos || []).forEach(buscarGrupo);
  const doGrupo = [];
  if (grupo) coletar(grupo, doGrupo);

  // 2) folhas com nome de tributo, em qualquer grupo do DRE
  const todas = [];
  (resp.tipos || []).forEach((n) => coletar(n, todas));
  const porNome = todas.filter((f) => ehTributo(String(f.label || '')));

  // as duas fontes juntas, dedup por código (ou pelo label quando não houver código)
  const vistos = new Set();
  return [...doGrupo, ...porNome].filter((f) => {
    if (excluir && excluir.test(String(f.label || ''))) return false;
    const k = String(f.codigo != null ? f.codigo : f.label);
    if (vistos.has(k)) return false;
    vistos.add(k);
    return true;
  });
};

// Saldo com count-up suave (reais + centavos) — estilo Mercado Pago. Estado isolado
// para a animação a 60fps não re-renderizar (nem re-animar) o resto do dashboard.
// Roleta de um dígito: gira de baixo pra cima (spins voltas) e assenta no alvo.
const ReelDigito = ({ value, delay = 0, spins = 2, duration = 1.6 }) => {
  const [rolou, setRolou] = useState(false);
  useEffect(() => {
    const id = requestAnimationFrame(() => requestAnimationFrame(() => setRolou(true)));
    return () => cancelAnimationFrame(id);
  }, []);
  const totalCels = spins * 10 + 10; // várias voltas 0..9 + uma final
  const alvoIndex = spins * 10 + value;
  const translate = rolou ? alvoIndex : 0;
  const cels = [];
  for (let i = 0; i < totalCels; i++) cels.push(i % 10);
  return (
    <span style={{ display: 'inline-flex', height: '1em', lineHeight: 1, overflow: 'hidden', alignItems: 'flex-start' }}>
      <span
        style={{
          display: 'flex',
          flexDirection: 'column',
          transform: `translateY(-${translate}em)`,
          transition: `transform ${duration}s cubic-bezier(0.16, 1, 0.3, 1)`,
          transitionDelay: `${delay}ms`,
          willChange: 'transform',
        }}
      >
        {cels.map((d, i) => (
          <span key={i} style={{ height: '1em', lineHeight: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{d}</span>
        ))}
      </span>
    </span>
  );
};

// Saldo com efeito odômetro: cada dígito "sobe" e entra outro (sensação de dinheiro entrando).
const SaldoAnimado = ({ total }) => {
  if (total == null) return <>—</>;

  const texto = formatarValor(total); // ex.: "R$ 1.316.905,11"
  const reduzir = typeof window !== 'undefined'
    && window.matchMedia
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (reduzir) return <>{texto}</>;

  const totalDigitos = (texto.match(/\d/g) || []).length;
  let idxDigito = 0;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', fontVariantNumeric: 'tabular-nums' }}>
      {texto.split('').map((ch, i) => {
        if (ch >= '0' && ch <= '9') {
          const d = idxDigito;
          idxDigito += 1;
          // Os dois últimos dígitos (centavos) giram bem mais devagar e entram por último
          const posDeTras = totalDigitos - d; // 1 = último, 2 = penúltimo
          const ehCentavo = posDeTras <= 2;
          const delay = ehCentavo ? d * 85 + (posDeTras === 2 ? 500 : 1000) : d * 85;
          const duracao = ehCentavo ? (posDeTras === 2 ? 3.0 : 3.8) : 1.6;
          const voltas = ehCentavo ? 3 : 2;
          return <ReelDigito key={i} value={Number(ch)} delay={delay} duration={duracao} spins={voltas} />;
        }
        return (
          <span key={i} style={{ display: 'inline-flex', height: '1em', lineHeight: 1, alignItems: 'center', whiteSpace: 'pre' }}>{ch}</span>
        );
      })}
    </span>
  );
};

// Donut isolado: mantém o estado de hover LOCAL, evitando re-render (e re-animação)
// dos demais gráficos do dashboard ao passar o mouse sobre as fatias/legenda.
const DonutDespesas = ({ dados, total, cores, rotulo }) => {
  const [ativa, setAtiva] = useState(null);

  if (!dados || dados.length === 0) {
    return (
      <div className="text-center py-5 text-muted">
        <i className="fas fa-chart-pie fa-3x mb-3" style={{ color: '#cbd5e1' }}></i>
        <p>Sem categorias de despesa no período</p>
      </div>
    );
  }

  return (
    <div className="d-flex align-items-center justify-content-center gap-4 flex-wrap" style={{ minHeight: 360 }}>
      <div className="fmp-donut" style={{ position: 'relative', width: 290, height: 290, flex: 'none' }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={dados}
              dataKey="valor"
              nameKey="nome"
              cx="50%"
              cy="50%"
              innerRadius={80}
              outerRadius={116}
              paddingAngle={2}
              cornerRadius={6}
              stroke="none"
              onMouseEnter={(_, idx) => setAtiva(idx)}
              onMouseLeave={() => setAtiva(null)}
            >
              {dados.map((entry, index) => (
                <Cell key={`pz-${index}`} fill={cores[index % cores.length]} style={{ cursor: 'pointer' }} />
              ))}
            </Pie>
          </PieChart>
        </ResponsiveContainer>
        <div style={{ position: 'absolute', inset: 0, display: 'grid', placeContent: 'center', textAlign: 'center', pointerEvents: 'none', padding: '0 30px' }}>
          {ativa != null && dados[ativa] ? (
            <>
              <span className="text-truncate" style={{ fontSize: 11.5, color: '#475467', fontWeight: 600, maxWidth: '140px' }}>
                {dados[ativa].nome}
              </span>
              <span style={{ fontFamily: "'Unbounded', sans-serif", fontWeight: 700, fontSize: 16, color: cores[ativa % cores.length] }}>
                {formatarValor(dados[ativa].valor)}
              </span>
              <span style={{ fontSize: 12, color: '#7d8aa3', fontWeight: 600 }}>
                {(total > 0 ? (dados[ativa].valor / total) * 100 : 0).toFixed(1)}% do total
              </span>
            </>
          ) : (
            <>
              <span style={{ fontSize: 11, color: '#7d8aa3', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.08em' }}>{rotulo || 'Despesas'}</span>
              <span style={{ fontFamily: "'Unbounded', sans-serif", fontWeight: 700, fontSize: 16, color: '#0e1726' }}>
                {formatarValor(total)}
              </span>
            </>
          )}
        </div>
      </div>
      <div style={{ width: 250, minWidth: 200 }}>
        {dados.map((entry, index) => {
          const pct = total > 0 ? (entry.valor / total) * 100 : 0;
          return (
            <div
              key={`lz-${index}`}
              className="d-flex align-items-center justify-content-between"
              style={{
                fontSize: 13,
                padding: '4px 8px',
                borderRadius: 8,
                background: ativa === index ? '#f4f6fb' : 'transparent',
                transition: 'background .15s'
              }}
              onMouseEnter={() => setAtiva(index)}
              onMouseLeave={() => setAtiva(null)}
            >
              <span className="d-flex align-items-center gap-2" style={{ color: '#475467', minWidth: 0 }}>
                <i style={{ width: 11, height: 11, borderRadius: 3, background: cores[index % cores.length], flex: 'none', display: 'inline-block' }}></i>
                <span className="text-truncate" style={{ maxWidth: '150px' }}>{entry.nome}</span>
              </span>
              <b style={{ fontVariantNumeric: 'tabular-nums', color: '#0e1726' }}>{pct.toFixed(1)}%</b>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Gráfico de impostos empilhado por tipo — isolado para o hover da legenda não
// re-renderizar (nem re-animar) o resto do dashboard.
const GraficoImpostos = ({ linhas, tipos, onSegmentClick }) => {
  const [leg, setLeg] = useState(null);

  const renderTip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;
    const itens = payload.filter((p) => (p.value || 0) > 0);
    if (!itens.length) return null;
    const total = itens.reduce((s, p) => s + (p.value || 0), 0);
    return (
      <div style={{ background: '#0e1726', borderRadius: 12, padding: '12px 14px', minWidth: 235, boxShadow: '0 16px 34px -10px rgba(4,18,44,0.6)', border: '1px solid rgba(146,162,196,0.18)' }}>
        <div style={{ color: '#9bacca', fontSize: 12, fontWeight: 600, marginBottom: 8, textTransform: 'uppercase', letterSpacing: '.04em' }}>
          {label} · Impostos
        </div>
        {itens.map((p, i) => {
          const t = tipos.find((x) => x.codigo === p.dataKey);
          return (
            <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 18, padding: '3px 0' }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#dde6f6', fontSize: 13 }}>
                <span style={{ width: 9, height: 9, borderRadius: '50%', background: p.color || p.fill || (t && t.cor) || '#888', display: 'inline-block', flex: 'none' }}></span>
                {t ? t.nome : p.dataKey}
              </span>
              <b style={{ color: '#fff', fontSize: 13, fontVariantNumeric: 'tabular-nums' }}>{formatarValor(p.value)}</b>
            </div>
          );
        })}
        <div style={{ borderTop: '1px solid rgba(146,162,196,0.22)', marginTop: 8, paddingTop: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ color: '#9bacca', fontSize: 12.5 }}>Total do mês</span>
          <b style={{ color: '#fff', fontSize: 14, fontVariantNumeric: 'tabular-nums' }}>{formatarValor(total)}</b>
        </div>
      </div>
    );
  };

  return (
    <ResponsiveContainer width="100%" height={360}>
      <BarChart data={linhas} margin={{ top: 16, right: 16, left: 8, bottom: 4 }} barCategoryGap="28%">
        <CartesianGrid strokeDasharray="3 3" stroke="#eef1f6" vertical={false} />
        <XAxis dataKey="mes" tick={{ fontSize: 12, fill: '#7d8aa3' }} />
        <YAxis tickFormatter={(value) => formatarValor(value)} tick={{ fontSize: 11, fill: '#7d8aa3' }} />
        <Tooltip content={renderTip} cursor={{ fill: 'rgba(0,32,91,0.06)' }} />
        <Legend
          onMouseEnter={(o) => setLeg(o && (o.dataKey || o.value))}
          onMouseLeave={() => setLeg(null)}
          formatter={(key) => {
            const t = tipos.find((x) => x.codigo === key);
            const nome = t ? t.nome : key;
            const apagado = leg && leg !== key;
            const resumo = descricaoImposto(nome);
            return (
              <span title={resumo} style={{ cursor: 'help', opacity: apagado ? 0.35 : 1, transition: 'opacity .15s', fontWeight: leg === key ? 700 : 400 }}>
                {nome}
                <i className="fas fa-info-circle ms-1" style={{ fontSize: 11, color: '#9bacca' }}></i>
              </span>
            );
          }}
        />
        {tipos.map((t, idx) => (
          <Bar
            key={t.codigo}
            dataKey={t.codigo}
            name={t.codigo}
            stackId="imp"
            fill={t.cor}
            fillOpacity={leg && leg !== t.codigo ? 0.2 : 1}
            maxBarSize={90}
            radius={idx === tipos.length - 1 ? [6, 6, 0, 0] : [0, 0, 0, 0]}
            style={{ cursor: 'pointer' }}
            onClick={(d) => onSegmentClick(t, d && d.payload ? d.payload : d)}
          />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
};

// Evolução acumulada dos impostos pagos ao longo do período (área suave)
const EvolucaoImpostos = ({ linhas, tipos }) => {
  let acc = 0;
  const dados = (linhas || []).map((l) => {
    const mesTotal = (tipos || []).reduce((s, t) => s + (l[t.codigo] || 0), 0);
    acc += mesTotal;
    return { mes: l.mes, mesTotal, acumulado: acc };
  });
  const totalFinal = acc;

  const fmtEixo = (v) => {
    if (Math.abs(v) >= 1000000) return `R$ ${(v / 1000000).toFixed(1).replace('.', ',')}M`;
    if (Math.abs(v) >= 1000) return `R$ ${(v / 1000).toFixed(0)}k`;
    return `R$ ${v}`;
  };

  const renderTip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;
    const p = payload[0].payload || {};
    return (
      <div style={{ background: '#0e1726', borderRadius: 12, padding: '12px 14px', minWidth: 205, boxShadow: '0 16px 34px -10px rgba(4,18,44,0.6)', border: '1px solid rgba(146,162,196,0.18)' }}>
        <div style={{ color: '#9bacca', fontSize: 12, fontWeight: 600, marginBottom: 8, textTransform: 'uppercase', letterSpacing: '.04em' }}>{label}</div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 18, padding: '3px 0' }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#dde6f6', fontSize: 13 }}>
            <span style={{ width: 9, height: 9, borderRadius: '50%', background: '#e5484d', display: 'inline-block' }}></span>Pago no mês
          </span>
          <b style={{ color: '#fff', fontSize: 13, fontVariantNumeric: 'tabular-nums' }}>{formatarValor(p.mesTotal || 0)}</b>
        </div>
        <div style={{ borderTop: '1px solid rgba(146,162,196,0.22)', marginTop: 8, paddingTop: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ color: '#9bacca', fontSize: 12.5 }}>Acumulado</span>
          <b style={{ color: '#f1be48', fontSize: 14, fontVariantNumeric: 'tabular-nums' }}>{formatarValor(p.acumulado || 0)}</b>
        </div>
      </div>
    );
  };

  return (
    <div style={{ background: 'linear-gradient(180deg,#fafbfe 0%,#fff 60%)', border: '1px solid #eef1f6', borderRadius: 14, padding: '16px 12px 8px' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', padding: '0 8px', marginBottom: 4 }}>
        <span style={{ fontSize: 12, color: '#7d8aa3' }}>Total acumulado no período</span>
        <span style={{ fontSize: 20, fontWeight: 700, color: '#00205b', fontFamily: "'Unbounded', sans-serif", fontVariantNumeric: 'tabular-nums' }}>{formatarValor(totalFinal)}</span>
      </div>
      <ResponsiveContainer width="100%" height={300}>
        <AreaChart data={dados} margin={{ top: 14, right: 18, left: 4, bottom: 4 }}>
          <defs>
            <linearGradient id="gradEvolImp" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#e5484d" stopOpacity={0.32} />
              <stop offset="90%" stopColor="#e5484d" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="4 4" stroke="#eef1f6" vertical={false} />
          <XAxis dataKey="mes" tick={{ fontSize: 11.5, fill: '#98a4b8' }} axisLine={false} tickLine={false} dy={6} />
          <YAxis tickFormatter={fmtEixo} tick={{ fontSize: 11, fill: '#98a4b8' }} axisLine={false} tickLine={false} width={54} />
          <Tooltip content={renderTip} cursor={{ stroke: '#c9d4e8', strokeWidth: 1.5, strokeDasharray: '5 5' }} />
          <Area
            type="monotone"
            dataKey="acumulado"
            stroke="#e5484d"
            strokeWidth={3}
            fill="url(#gradEvolImp)"
            dot={{ r: 3.5, fill: '#fff', stroke: '#e5484d', strokeWidth: 2 }}
            activeDot={{ r: 6, fill: '#e5484d', stroke: '#fff', strokeWidth: 2.5 }}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

const DashboardCliente = () => {
  // PEGA O ID DO CLIENTE DA URL
  const { cliente_id } = useParams();
  const navigate = useNavigate();

  // Estados - Dados do Cliente
  const [dadosCliente, setDadosCliente] = useState(null);
  const [contasCliente, setContasCliente] = useState([]);
  
  // Estados - Dados Financeiros
  const [dadosCompletos, setDadosCompletos] = useState(null);
  const [dadosFluxoDiario, setDadosFluxoDiario] = useState(null);
  const [dadosInvestimentos, setDadosInvestimentos] = useState(null);
  const [dadosDRE, setDadosDRE] = useState(null);
  
  // Estados - Loading
  const [carregando, setCarregando] = useState(false);
  const [carregandoFluxoDiario, setCarregandoFluxoDiario] = useState(false);
  const [carregandoInvestimentos, setCarregandoInvestimentos] = useState(false);
  const [carregandoDRE, setCarregandoDRE] = useState(false);
  
  // Estados - UI
  const [mensagem, setMensagem] = useState('');
  useEffect(() => { notificar(mensagem); }, [mensagem]);

  // Busca a lista COMPLETA de categorias (ano corrente + anterior), independente do período,
  // só para popular o select de filtro. Não altera os gráficos (que seguem o período).
  useEffect(() => {
    if (!cliente_id) return undefined;
    const token = localStorage.getItem('access');
    const hoje = new Date();
    const ini = `${hoje.getFullYear() - 1}-01-01`;
    const fim = `${hoje.getFullYear()}-12-31`;
    let ativo = true;
    axios.get(`${process.env.REACT_APP_API_BASE}/api/dre-cliente/`, {
      params: { cliente_id: cliente_id, data_inicio: ini, data_fim: fim },
      headers: { 'Authorization': `Bearer ${token}` },
      timeout: 120000,
    }).then((resp) => {
      if (ativo && resp.data?.success && resp.data.dados) {
        setTodasCategorias({
          despesas: resp.data.dados.dados_barras_despesas || [],
          receitas: resp.data.dados.dados_barras_receitas || [],
        });
      }
    }).catch(() => {});
    return () => { ativo = false; };
  }, [cliente_id]);
  const [modalFluxoDiario, setModalFluxoDiario] = useState(false);
  const [diaDetalhes, setDiaDetalhes] = useState(null);
  const [modalInvestimento, setModalInvestimento] = useState(false);
  const [contaSelecionada, setContaSelecionada] = useState(null);
  const [modalCategoria, setModalCategoria] = useState(false);
  const [categoriaSelecionada, setCategoriaSelecionada] = useState(null);
  const [modalRelatorio, setModalRelatorio] = useState(false);
  const [relatorioDados, setRelatorioDados] = useState({ titulo: '', tipo: '', itens: [] });
  const [categoriaFiltro, setCategoriaFiltro] = useState(''); // categoria_completa selecionada (filtro dos gráficos do DRE)
  const [todasCategorias, setTodasCategorias] = useState({ despesas: [], receitas: [] }); // lista completa (ano), independente do período
  const [impostosChart, setImpostosChart] = useState({ linhas: [], tipos: [], total: 0, ok: false }); // série pronta (pode cruzar anos)
  const [impostosMeses, setImpostosMeses] = useState(6);     // atalho ativo (3/6/12) ou 0 = período custom
  const [impostosIni, setImpostosIni] = useState('');        // 'AAAA-MM' início
  const [impostosFim, setImpostosFim] = useState('');        // 'AAAA-MM' fim
  const [carregandoImpostos, setCarregandoImpostos] = useState(false);
  // Liga/desliga os insights de IA da página (salvo na coluna `ia` de clientes_bpo)
  const [insightsIA, setInsightsIA] = useState(true);
  const toggleInsightsIA = async () => {
    const nv = !insightsIA;
    setInsightsIA(nv); // otimista
    const token = localStorage.getItem('access');
    try {
      await axios.post(
        `${process.env.REACT_APP_API_BASE}/api/cliente-bpo/${cliente_id}/ia/`,
        { ia: nv ? 'Sim' : 'Não' },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      setDadosCliente((prev) => (prev ? { ...prev, ia: nv ? 'Sim' : 'Não' } : prev));
    } catch (e) {
      setInsightsIA(!nv); // reverte se falhar
      setMensagem('Não foi possível salvar a preferência de IA.');
    }
  };
  // Card Faturado x Recebido
  const [fatRec, setFatRec] = useState({ linhas: [], totalFat: 0, totalRec: 0, ok: false });
  const [fatRecMeses, setFatRecMeses] = useState(6);
  const [fatRecIni, setFatRecIni] = useState('');
  const [fatRecFim, setFatRecFim] = useState('');
  const [carregandoFatRec, setCarregandoFatRec] = useState(false);

  // Card Faturamento Comparativo (ano atual x ano anterior)
  const FATCOMP_VAZIO = { meses: [], ok: false, anoAtual: null, anoAnterior: null, totalAtual: 0, totalAnterior: 0, variacaoPct: null, ytdAtual: 0, ytdAnterior: 0, ytdVariacaoPct: null, ytdCorte: '' };
  const [fatComp, setFatComp] = useState(FATCOMP_VAZIO);
  const [fatCompBase, setFatCompBase] = useState('competencia'); // 'competencia' | 'pagamento'
  const [fatCompTipo, setFatCompTipo] = useState('barras'); // 'barras' | 'linhas'
  const [carregandoFatComp, setCarregandoFatComp] = useState(false);

  // Card Despesas Comparativo (ano atual x ano anterior)
  const [despComp, setDespComp] = useState(FATCOMP_VAZIO);
  const [despCompBase, setDespCompBase] = useState('competencia'); // 'competencia' | 'pagamento'
  const [despCompTipo, setDespCompTipo] = useState('barras'); // 'barras' | 'linhas'
  const [carregandoDespComp, setCarregandoDespComp] = useState(false);
  
  // Estados - Filtros DRE
  const [dataInicioDRE, setDataInicioDRE] = useState('');
  const [dataFimDRE, setDataFimDRE] = useState('');
  
  const isFirstRender = useRef(true);
  const isLoading = useRef(false);

  // EFFECT: Carregar dados do cliente ao montar
  useEffect(() => {
    if (isFirstRender.current && cliente_id) {
      isFirstRender.current = false;
      // console.log(`Dashboard Cliente ID: ${cliente_id}`);
      carregarDadosCliente();
    }

    return () => {
      // console.log('Componente desmontado');
    };
  }, [cliente_id]);

  // ========================================
  // FUNÇÃO: Carregar dados do cliente
  // ========================================
  const carregarDadosCliente = async () => {
    setCarregando(true);
    const token = localStorage.getItem('access');

    try {
      // console.log(`Buscando dados do cliente ID: ${cliente_id}`);
      
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE}/api/cliente-bpo/${cliente_id}/`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        // Cliente Conta Azul nesta página (Omie) = rota errada (ex.: redirect
        // antigo do login) — manda pro dashboard certo antes de renderizar
        if ((response.data.cliente?.sistema || '').toLowerCase() === 'conta_azul') {
          navigate(`/dashboard-conta-azul/${cliente_id}`, { replace: true });
          return;
        }
        setDadosCliente(response.data.cliente);
        setInsightsIA((response.data.cliente?.ia || 'Sim') !== 'Não');
        setContasCliente(response.data.contas);
        
        // console.log('Dados do cliente:', response.data.cliente);
        // console.log('Contas do cliente:', response.data.contas);
        
        // Iniciar carregamento dos módulos financeiros
        setTimeout(() => {
          carregarDadosFinanceiros();
        }, 500);
      }
    } catch (error) {
      console.error('Erro ao carregar cliente:', error);
      
      if (error.response?.status === 404) {
        setMensagem('Cliente não encontrado');
        setTimeout(() => navigate('/Clientes_bpo'), 3000);
      } else if (error.response?.status === 401) {
        setMensagem('Sessão expirada. Redirecionando...');
        setTimeout(() => navigate('/login'), 2000);
      } else {
        setMensagem('Erro ao carregar dados do cliente');
      }
    } finally {
      setCarregando(false);
    }
  };

  // ========================================
  // FUNÇÃO: Carregar dados financeiros principais
  // ========================================
  const carregarDadosFinanceiros = async () => {
    if (isLoading.current) {
      // console.log('Já está carregando dados financeiros');
      return;
    }

    isLoading.current = true;
    setCarregando(true);

    const token = localStorage.getItem('access');

    try {
      // console.log('Iniciando carregamento dos dados financeiros...');
      
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE}/api/extrato-bancario-cliente/`,
        {
          params: { cliente_id: cliente_id },
          headers: { 'Authorization': `Bearer ${token}` },
          timeout: 900000
        }
      );

      if (response.data.success) {
        setDadosCompletos(response.data.dados);
        setMensagem('Dados carregados com sucesso!');
        
        // console.log('Dados principais carregados, iniciando módulos...');
        
        // Carregar módulos sequencialmente
        setTimeout(async () => {
          try {
            await carregarFluxoDiario();
            await carregarInvestimentos();
            
            // Carregar DRE (mês atual: 1º ao último dia do mês corrente)
            const hoje = new Date();
            const dataInicio = new Date(hoje.getFullYear(), hoje.getMonth(), 1).toISOString().split('T')[0];
            const dataFim = new Date(hoje.getFullYear(), hoje.getMonth() + 1, 0).toISOString().split('T')[0];

            setDataInicioDRE(dataInicio);
            setDataFimDRE(dataFim);

            await carregarDRE(dataInicio, dataFim);
            
            // console.log('Todos os módulos carregados com sucesso');
          } catch (error) {
            console.error('Erro ao carregar módulos:', error);
          }
        }, 1000);
        
        setTimeout(() => setMensagem(''), 5000);
      } else {
        setMensagem('Erro ao carregar dados das contas');
      }

    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      
      if (error.code === 'ECONNABORTED') {
        setMensagem('Timeout: Sistema demorou mais que 60 segundos.');
      } else if (error.response?.status === 401) {
        setMensagem('Sessão expirada. Redirecionando...');
        setTimeout(() => navigate('/login'), 2000);
      } else {
        setMensagem('Erro de conexão.');
      }
    } finally {
      setCarregando(false);
      isLoading.current = false;
    }
  };

  // ========================================
  // FUNÇÃO: Carregar Fluxo de Caixa Diário
  // ========================================
  const carregarFluxoDiario = async () => {
    if (carregandoFluxoDiario) {
      // console.log('Fluxo Diário já está carregando');
      return;
    }
    
    setCarregandoFluxoDiario(true);
    
    const token = localStorage.getItem('access');

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE}/api/fluxo-caixa-diario-cliente/`,
        {
          params: { cliente_id: cliente_id },
          headers: { 'Authorization': `Bearer ${token}` },
          timeout: 95000
        }
      );

      if (response.data.success) {
        // console.log('Dados Fluxo Diário recebidos:', response.data.dados);
        setDadosFluxoDiario(response.data.dados);
      } else {
        console.error('Erro ao carregar Fluxo Diário:', response.data.erro);
      }

    } catch (error) {
      console.error('Erro ao carregar Fluxo Diário:', error);
    } finally {
      setCarregandoFluxoDiario(false);
    }
  };

  // ========================================
  // FUNÇÃO: Carregar Análise de Investimentos
  // ========================================
  const carregarInvestimentos = async () => {
    if (carregandoInvestimentos) {
      // console.log('Investimentos já está carregando');
      return;
    }
    
    setCarregandoInvestimentos(true);
    
    const token = localStorage.getItem('access');

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE}/api/analise-investimentos-cliente/`,
        {
          params: { cliente_id: cliente_id },
          headers: { 'Authorization': `Bearer ${token}` },
          timeout: 95000
        }
      );

      if (response.data.success) {
        // console.log('Dados Investimentos recebidos:', response.data.dados);
        setDadosInvestimentos(response.data.dados);
      } else {
        console.error('Erro ao carregar Investimentos:', response.data.erro);
      }

    } catch (error) {
      console.error('Erro ao carregar Investimentos:', error);
    } finally {
      setCarregandoInvestimentos(false);
    }
  };

  // ========================================
  // FUNÇÃO: Carregar DRE
  // ========================================
  const carregarDRE = async (inicio, fim) => {
    if (carregandoDRE) {
      // console.log('DRE já está carregando');
      return;
    }
    
    setCarregandoDRE(true);
    
    const token = localStorage.getItem('access');

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE}/api/dre-cliente/`,
        {
          params: { 
            cliente_id: cliente_id,
            data_inicio: inicio, 
            data_fim: fim 
          },
          headers: { 'Authorization': `Bearer ${token}` },
          timeout: 95000
        }
      );

      if (response.data.success) {
        // console.log('Dados DRE recebidos:', response.data.dados);
        setDadosDRE(response.data.dados);
      } else {
        console.error('Erro ao carregar DRE:', response.data.erro);
      }

    } catch (error) {
      console.error('Erro ao carregar DRE:', error);
    } finally {
      setCarregandoDRE(false);
    }
  };

  // ========================================
  // FUNÇÃO: Carregar Impostos (via DRE horizontal — valores mensais por conta)
  // ========================================
  const carregarImpostos = async (iniYM, fimYM) => {
    if (!cliente_id || !iniYM || !fimYM) return;
    setCarregandoImpostos(true);
    const token = localStorage.getItem('access');
    let [a1, m1] = iniYM.split('-').map(Number);
    let [a2, m2] = fimYM.split('-').map(Number);
    if (a1 > a2 || (a1 === a2 && m1 > m2)) { [a1, m1, a2, m2] = [a2, m2, a1, m1]; } // garante ordem

    // dre-horizontal é por ano -> quebra o intervalo em segmentos por ano
    const segs = [];
    for (let y = a1; y <= a2; y++) {
      segs.push({ ano: y, mi: (y === a1 ? m1 : 1), mf: (y === a2 ? m2 : 12) });
    }
    try {
      const resps = await Promise.all(segs.map((s) =>
        axios.get(`${process.env.REACT_APP_API_BASE}/api/dre-horizontal/`, {
          params: { cliente_id: cliente_id, ano: s.ano, mes_ini: s.mi, mes_fim: s.mf },
          headers: { 'Authorization': `Bearer ${token}` },
          timeout: 95000,
        }).then((r) => ({ seg: s, data: r.data?.success ? r.data : null })).catch(() => ({ seg: s, data: null }))
      ));

      const tiposMap = new Map(); // codigo -> nome (união de todos os anos)
      const linhas = [];
      resps.forEach(({ seg, data }) => {
        const folhas = extrairFolhasImpostos(data, cliente_id);
        folhas.forEach((f) => { if (!tiposMap.has(String(f.codigo))) tiposMap.set(String(f.codigo), limparCategoria(f.label || f.codigo)); });
        for (let m = seg.mi; m <= seg.mf; m++) {
          const row = { key: `${seg.ano}-${m}`, mes: `${MESES3[m - 1]}/${String(seg.ano).slice(2)}`, mesNum: m, ano: seg.ano };
          folhas.forEach((f) => { row[String(f.codigo)] = Math.abs(f.valores?.[String(m)] || 0); });
          linhas.push(row);
        }
      });

      let tipos = Array.from(tiposMap.entries()).map(([codigo, nome], idx) => ({ codigo, nome, cor: CORES_PIZZA[idx % CORES_PIZZA.length] }));
      tipos = tipos.filter((t) => linhas.some((r) => (r[t.codigo] || 0) > 0));
      const total = linhas.reduce((s, r) => s + tipos.reduce((a, t) => a + (r[t.codigo] || 0), 0), 0);
      setImpostosChart({ linhas, tipos, total, ok: tipos.length > 0 });
    } catch (error) {
      console.error('Erro ao carregar impostos:', error);
      setImpostosChart({ linhas: [], tipos: [], total: 0, ok: false, erro: 'Erro ao carregar impostos' });
    } finally {
      setCarregandoImpostos(false);
    }
  };

  useEffect(() => {
    if (!cliente_id) return;
    const [ini, fim] = rangeUltimosMeses(6);
    setImpostosIni(ini); setImpostosFim(fim); setImpostosMeses(6);
    carregarImpostos(ini, fim);
    // eslint-disable-next-line
  }, [cliente_id]);

  // Faturado (notas emitidas) x Recebido (títulos recebidos), por mês
  const carregarFatRec = async (iniYM, fimYM) => {
    if (!cliente_id || !iniYM || !fimYM) return;
    setCarregandoFatRec(true);
    const token = localStorage.getItem('access');
    let [a1, m1] = iniYM.split('-').map(Number);
    let [a2, m2] = fimYM.split('-').map(Number);
    if (a1 > a2 || (a1 === a2 && m1 > m2)) { [a1, m1, a2, m2] = [a2, m2, a1, m1]; }
    const segs = [];
    for (let y = a1; y <= a2; y++) segs.push({ ano: y, mi: (y === a1 ? m1 : 1), mf: (y === a2 ? m2 : 12) });
    try {
      const resps = await Promise.all(segs.map((s) =>
        axios.get(`${process.env.REACT_APP_API_BASE}/api/faturado-recebido/`, {
          params: { cliente_id: cliente_id, ano: s.ano, mes_ini: s.mi, mes_fim: s.mf },
          headers: { 'Authorization': `Bearer ${token}` }, timeout: 60000,
        }).then((r) => ({ seg: s, data: r.data?.success ? r.data : null })).catch(() => ({ seg: s, data: null }))
      ));
      const linhas = [];
      resps.forEach(({ seg, data }) => {
        const porMes = {};
        (data?.meses || []).forEach((x) => { porMes[x.mes] = x; });
        for (let m = seg.mi; m <= seg.mf; m++) {
          const x = porMes[m] || {};
          linhas.push({ key: `${seg.ano}-${m}`, mes: `${MESES3[m - 1]}/${String(seg.ano).slice(2)}`, mesNum: m, ano: seg.ano, faturado: x.faturado || 0, recebido: x.recebido || 0, atrasado: x.atrasado || 0 });
        }
      });
      const totalFat = linhas.reduce((s, l) => s + l.faturado, 0);
      const totalRec = linhas.reduce((s, l) => s + l.recebido, 0);
      const totalAtraso = linhas.reduce((s, l) => s + l.atrasado, 0);
      setFatRec({ linhas, totalFat, totalRec, totalAtraso, ok: linhas.some((l) => l.faturado > 0 || l.recebido > 0) });
    } catch (e) {
      setFatRec({ linhas: [], totalFat: 0, totalRec: 0, ok: false, erro: 'Erro ao carregar' });
    } finally {
      setCarregandoFatRec(false);
    }
  };

  useEffect(() => {
    if (!cliente_id) return;
    const [ini, fim] = rangeUltimosMeses(6);
    setFatRecIni(ini); setFatRecFim(fim); setFatRecMeses(6);
    carregarFatRec(ini, fim);
    // eslint-disable-next-line
  }, [cliente_id]);

  // Faturamento comparativo (ano atual x ano anterior), por competência ou pagamento
  useEffect(() => {
    if (!cliente_id) return undefined;
    let ativo = true;
    const token = localStorage.getItem('access');
    setCarregandoFatComp(true);
    axios.get(`${process.env.REACT_APP_API_BASE}/api/faturamento-comparativo/`, {
      params: { cliente_id: cliente_id, base: fatCompBase },
      headers: { 'Authorization': `Bearer ${token}` },
      timeout: 60000,
    }).then((r) => {
      if (!ativo) return;
      if (r.data?.success) {
        const meses = (r.data.meses || []).map((m) => ({ ...m, nome: MESES3[m.mes - 1] }));
        setFatComp({
          meses,
          ok: meses.some((m) => (m.atual || 0) > 0 || (m.anterior || 0) > 0),
          anoAtual: r.data.ano_atual,
          anoAnterior: r.data.ano_anterior,
          totalAtual: r.data.total_atual || 0,
          totalAnterior: r.data.total_anterior || 0,
          variacaoPct: r.data.variacao_pct,
          ytdAtual: r.data.ytd_atual || 0,
          ytdAnterior: r.data.ytd_anterior || 0,
          ytdVariacaoPct: r.data.ytd_variacao_pct,
          ytdCorte: r.data.ytd_corte || '',
        });
      } else {
        setFatComp(FATCOMP_VAZIO);
      }
    }).catch(() => {
      if (ativo) setFatComp(FATCOMP_VAZIO);
    }).finally(() => { if (ativo) setCarregandoFatComp(false); });
    return () => { ativo = false; };
  }, [cliente_id, fatCompBase]);

  // Despesas comparativas (ano atual x ano anterior), por competência ou pagamento
  useEffect(() => {
    if (!cliente_id) return undefined;
    let ativo = true;
    const token = localStorage.getItem('access');
    setCarregandoDespComp(true);
    axios.get(`${process.env.REACT_APP_API_BASE}/api/faturamento-comparativo/`, {
      params: { cliente_id: cliente_id, base: despCompBase, tipo: 'despesas' },
      headers: { 'Authorization': `Bearer ${token}` },
      timeout: 60000,
    }).then((r) => {
      if (!ativo) return;
      if (r.data?.success) {
        const meses = (r.data.meses || []).map((m) => ({ ...m, nome: MESES3[m.mes - 1] }));
        setDespComp({
          meses,
          ok: meses.some((m) => (m.atual || 0) > 0 || (m.anterior || 0) > 0),
          anoAtual: r.data.ano_atual,
          anoAnterior: r.data.ano_anterior,
          totalAtual: r.data.total_atual || 0,
          totalAnterior: r.data.total_anterior || 0,
          variacaoPct: r.data.variacao_pct,
          ytdAtual: r.data.ytd_atual || 0,
          ytdAnterior: r.data.ytd_anterior || 0,
          ytdVariacaoPct: r.data.ytd_variacao_pct,
          ytdCorte: r.data.ytd_corte || '',
        });
      } else {
        setDespComp(FATCOMP_VAZIO);
      }
    }).catch(() => {
      if (ativo) setDespComp(FATCOMP_VAZIO);
    }).finally(() => { if (ativo) setCarregandoDespComp(false); });
    return () => { ativo = false; };
  }, [cliente_id, despCompBase]);

  // Clique nas barras dos comparativos anuais -> lançamentos daquele mês/ano (reusa o modal)
  const abrirLancComparativo = async (tipoCard, base, ano, linha) => {
    if (!ano || !linha) return;
    const rotulo = tipoCard === 'despesas' ? 'Despesas' : 'Faturamento';
    const titulo = `${rotulo} — ${linha.nome}/${String(ano).slice(2)} (${base === 'pagamento' ? 'pagamento' : 'competência'})`;
    setImpostosLanc({ aberto: true, titulo, carregando: true, itens: [], total: 0 });
    const token = localStorage.getItem('access');
    try {
      const r = await axios.get(`${process.env.REACT_APP_API_BASE}/api/faturamento-comparativo-lancamentos/`, {
        params: { cliente_id: cliente_id, base, tipo: tipoCard, ano, mes: linha.mes },
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const itens = (r.data?.lancamentos || []).sort((a, b) => String(a.data || '').localeCompare(String(b.data || '')));
      const total = itens.reduce((s, l) => s + Math.abs(l.valor || 0), 0);
      setImpostosLanc({ aberto: true, titulo, carregando: false, itens, total });
    } catch (e) {
      setImpostosLanc({ aberto: true, titulo, carregando: false, itens: [], total: 0 });
    }
  };

  // Clique nas barras do card Faturado x Recebido -> lançamentos (reusa o modal de impostos)
  const abrirLancFatRec = async (tipo, linha) => {
    const titulo = `${tipo === 'faturado' ? 'Faturado (emitido)' : 'Recebido'} — ${linha.mes}`;
    setImpostosLanc({ aberto: true, titulo, carregando: true, itens: [], total: 0 });
    const token = localStorage.getItem('access');
    try {
      const r = await axios.get(`${process.env.REACT_APP_API_BASE}/api/faturado-recebido-lancamentos/`, {
        params: { cliente_id: cliente_id, ano: linha.ano, mes: linha.mesNum, tipo },
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const itens = (r.data?.lancamentos || []).sort((a, b) => String(a.data || '').localeCompare(String(b.data || '')));
      const total = itens.reduce((s, l) => s + Math.abs(l.valor || 0), 0);
      setImpostosLanc({ aberto: true, titulo, carregando: false, itens, total });
    } catch (e) {
      setImpostosLanc({ aberto: true, titulo, carregando: false, itens: [], total: 0 });
    }
  };

  // Clique nos TOTAIS do card (Recebido / Em atraso) -> relatório do período inteiro
  const abrirLancFatRecPeriodo = async (tipo) => {
    const rotulo = tipo === 'recebido' ? 'Recebido' : 'Em atraso';
    setImpostosLanc({ aberto: true, titulo: `${rotulo} — período`, carregando: true, itens: [], total: 0 });
    const token = localStorage.getItem('access');
    // segmenta o período exibido (fatRec.linhas) por ano
    const porAno = {};
    (fatRec.linhas || []).forEach((l) => {
      if (!porAno[l.ano]) porAno[l.ano] = { ano: l.ano, mi: l.mesNum, mf: l.mesNum };
      porAno[l.ano].mi = Math.min(porAno[l.ano].mi, l.mesNum);
      porAno[l.ano].mf = Math.max(porAno[l.ano].mf, l.mesNum);
    });
    const segs = Object.values(porAno);
    try {
      const resps = await Promise.all(segs.map((s) =>
        axios.get(`${process.env.REACT_APP_API_BASE}/api/faturado-recebido-lancamentos/`, {
          params: { cliente_id: cliente_id, ano: s.ano, mes_ini: s.mi, mes_fim: s.mf, tipo },
          headers: { 'Authorization': `Bearer ${token}` }, timeout: 60000,
        }).then((r) => r.data?.lancamentos || []).catch(() => [])
      ));
      const itens = resps.flat().sort((a, b) => String(a.data || '').localeCompare(String(b.data || '')));
      const total = itens.reduce((s, l) => s + Math.abs(l.valor || 0), 0);
      setImpostosLanc({ aberto: true, titulo: `${rotulo} — período`, carregando: false, itens, total });
    } catch (e) {
      setImpostosLanc({ aberto: true, titulo: `${rotulo} — período`, carregando: false, itens: [], total: 0 });
    }
  };

  // Lançamentos ao clicar numa barra do card de Impostos
  const [impostosLanc, setImpostosLanc] = useState({ aberto: false, titulo: '', carregando: false, itens: [], total: 0 });
  // mostra colunas NF/Vencimento no modal só quando os lançamentos as têm (fatRec sim; impostos não)
  const impLancTemTributo = (impostosLanc.itens || []).some((i) => i.tributo);
  const impLancTemVenc = (impostosLanc.itens || []).some((i) => i.vencimento);
  const impLancTemNF = (impostosLanc.itens || []).some((i) => i.nf);

  // Tooltip do card Faturado x Recebido: mostra sempre os dois + "A receber"
  const renderTooltipFatRec = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;
    const row = payload[0].payload || {};
    const fat = row.faturado || 0;
    const rec = row.recebido || 0;
    const aReceber = Math.max(fat - rec, 0);
    const linha = (cor, nome, val) => (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 18, padding: '3px 0' }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#dde6f6', fontSize: 13 }}>
          <span style={{ width: 9, height: 9, borderRadius: '50%', background: cor, display: 'inline-block', flex: 'none' }}></span>{nome}
        </span>
        <b style={{ color: '#fff', fontSize: 13, fontVariantNumeric: 'tabular-nums' }}>{formatarValor(val)}</b>
      </div>
    );
    return (
      <div style={{ background: '#0e1726', borderRadius: 12, padding: '12px 14px', minWidth: 250, boxShadow: '0 16px 34px -10px rgba(4,18,44,0.6)', border: '1px solid rgba(146,162,196,0.18)' }}>
        <div style={{ color: '#9bacca', fontSize: 12, fontWeight: 600, marginBottom: 8, textTransform: 'uppercase', letterSpacing: '.04em' }}>{label}</div>
        {linha('#6b9bd8', 'Faturado (emitido)', fat)}
        {linha('#1fa971', 'Recebido', rec)}
        <div style={{ borderTop: '1px solid rgba(146,162,196,0.22)', marginTop: 8, paddingTop: 8 }}>
          {linha('#f1be48', 'A receber', aReceber)}
        </div>
      </div>
    );
  };

  // Clicar em QUALQUER fatia abre o mês inteiro, com todos os tributos.
  // Antes abria só a categoria da fatia clicada, e o modal mostrava um lançamento
  // solto que não fechava com a barra — a barra de set/26 da Deskbee tem 7 tributos
  // vindos de 29 títulos, e clicar nela devolvia só o ISSQN. Cada linha agora diz a
  // que tributo pertence, e o total do modal bate com a altura da barra.
  const abrirLancImpostos = async (tipoClicado, linha) => {
    const tipos = (impostosChart.tipos || []);
    const titulo = `Impostos — ${linha.mes}`;
    setImpostosLanc({ aberto: true, titulo, carregando: true, itens: [], total: 0 });
    const token = localStorage.getItem('access');
    try {
      const listas = await Promise.all(tipos.map(async (t) => {
        // só busca tributo que tem valor neste mês, para não disparar pedido à toa
        if (!(Math.abs(linha[String(t.codigo)] || 0) > 0)) return [];
        try {
          const r = await axios.get(`${process.env.REACT_APP_API_BASE}/api/dre-horizontal-lancamentos/`, {
            params: {
              cliente_id: cliente_id, ano: linha.ano, mes_ini: linha.mesNum, mes_fim: linha.mesNum,
              codigo_categoria: t.codigo,
            },
            headers: { 'Authorization': `Bearer ${token}` },
          });
          return (r.data?.lancamentos || []).map((l) => ({ ...l, tributo: t.nome, cor: t.cor }));
        } catch (e) {
          return [];
        }
      }));
      const itens = listas.flat().sort((a, b) => (
        String(a.tributo || '').localeCompare(String(b.tributo || ''))
        || String(a.data || '').localeCompare(String(b.data || ''))
      ));
      const total = itens.reduce((s, l) => s + Math.abs(l.valor || 0), 0);
      setImpostosLanc({ aberto: true, titulo, carregando: false, itens, total });
    } catch (e) {
      setImpostosLanc({ aberto: true, titulo, carregando: false, itens: [], total: 0 });
    }
  };

  // ========================================
  // FUNÇÕES: Filtros DRE
  // ========================================
  const aplicarFiltroDRE = () => {
    if (dataInicioDRE && dataFimDRE) {
      carregarDRE(dataInicioDRE, dataFimDRE);
    } else {
      setMensagem('Selecione ambas as datas');
      setTimeout(() => setMensagem(''), 3000);
    }
  };

  const limparFiltroDRE = () => {
    // Reset volta para o mês atual (1º ao último dia)
    const hoje = new Date();
    const dataInicio = new Date(hoje.getFullYear(), hoje.getMonth(), 1).toISOString().split('T')[0];
    const dataFim = new Date(hoje.getFullYear(), hoje.getMonth() + 1, 0).toISOString().split('T')[0];

    setDataInicioDRE(dataInicio);
    setDataFimDRE(dataFim);
    carregarDRE(dataInicio, dataFim);
  };

  // Abre o relatório completo (todas as categorias, do menor ao maior valor)
  const abrirRelatorio = (tipo) => {
    const base = tipo === 'despesas'
      ? (dadosDRE?.dados_barras_despesas || [])
      : (dadosDRE?.dados_barras_receitas || []);
    const itens = [...base].sort((a, b) => b.valor - a.valor); // maior → menor
    setRelatorioDados({
      titulo: tipo === 'despesas' ? 'Relatório completo — Despesas' : 'Relatório completo — Receitas',
      tipo,
      itens
    });
    setModalRelatorio(true);
  };

  // ========================================
  // FUNÇÕES: Modais
  // ========================================
  const mostrarDetalhesDia = (data) => {
    if (data && data.movimentos && data.movimentos.length > 0) {
      setDiaDetalhes(data);
      setModalFluxoDiario(true);
    }
  };

  const mostrarDetalhesInvestimento = (conta) => {
    setContaSelecionada(conta);
    setModalInvestimento(true);
  };

  // ========================================
  // FUNÇÕES: Auxiliares
  // ========================================
  const formatarMesAno = (data) => {
    if (!data) return 'Mês Atual';

    const texto = new Intl.DateTimeFormat('pt-BR', {
      month: 'long',
      year: 'numeric'
    }).format(new Date(data));

    return texto.charAt(0).toUpperCase() + texto.slice(1);
  };

  const obterVariacaoReal = () => {
    if (!dadosCompletos?.analises?.variacao_percentual_real && dadosCompletos?.analises?.variacao_percentual_real !== 0) return 0;
    return dadosCompletos.analises.variacao_percentual_real;
  };

  // ========================================
  // DADOS PARA GRÁFICOS
  // ========================================
  const COLORS = ['#00205b', '#00b5cc', '#f1be48', '#d94f63', '#4a9c5d', '#8c6bc8', '#f0883b', '#6c757d', '#3d7ab8', '#a8842c'];

  const dadosGraficoBarras = dadosCompletos ?
    dadosCompletos.extratos
      .filter(e => e.status === 'sucesso')
      .map(e => ({
        nome: e.conta_info.nome_curto || e.conta_info.nome,
        saldo: e.saldos.saldo_disponivel,
        tipo: e.conta_info.tipo,
        color: e.conta_info.cor
      })) : [];

  // Ordenado do maior para o menor (ranking) para o gráfico horizontal
  const dadosGraficoBarrasOrdenado = [...dadosGraficoBarras].sort((a, b) => b.saldo - a.saldo);

  // Rótulo da barra: nome DENTRO (branco) quando cabe; se a barra for curta, nome vai
  // para fora. O valor em R$ fica na ponta. Em saldo NEGATIVO (barra cresce p/ a esquerda)
  // os rótulos são espelhados para a esquerda.
  const renderBarraContaLabel = (props) => {
    const { x, y, width, height, index } = props;
    const item = dadosGraficoBarrasOrdenado[index];
    if (!item) return null;

    const nome = item.nome || '';
    const valor = formatarValor(item.saldo);
    const negativo = item.saldo < 0;
    const cy = y + height / 2;

    // Bordas reais da barra (negativa: width vem negativo e x fica no zero)
    const len = Math.abs(width);
    const left = Math.min(x, x + width);
    const right = Math.max(x, x + width);

    const nomeW = nome.length * 7.5 + 16;
    const valorW = valor.length * 7 + 16;
    const cabeNome = len > nomeW + 8;
    const cabeAmbos = len > nomeW + valorW;

    if (!negativo) {
      // POSITIVO: barra cresce p/ a direita → rótulos à direita
      return (
        <g style={{ fontFamily: "'Inter Tight', sans-serif" }}>
          {cabeNome ? (
            <text x={left + 14} y={cy} dy={5} fill="#ffffff" fontSize={13} fontWeight={700}>{nome}</text>
          ) : (
            <text x={right + 8} y={cy} dy={5} fill="#475467" fontSize={13} fontWeight={600}>{nome}</text>
          )}
          {cabeAmbos ? (
            <text x={right - 14} y={cy} dy={5} textAnchor="end" fill="#ffffff" fontSize={12} fontWeight={700}
              style={{ fontVariantNumeric: 'tabular-nums' }}>{valor}</text>
          ) : (
            <text x={right + (cabeNome ? 10 : nomeW + 14)} y={cy} dy={5} fill="#0e1726" fontSize={12} fontWeight={700}
              style={{ fontVariantNumeric: 'tabular-nums' }}>{valor}</text>
          )}
        </g>
      );
    }

    // NEGATIVO: barra cresce p/ a esquerda → rótulos espelhados à esquerda
    return (
      <g style={{ fontFamily: "'Inter Tight', sans-serif" }}>
        {cabeNome ? (
          <text x={right - 14} y={cy} dy={5} textAnchor="end" fill="#ffffff" fontSize={13} fontWeight={700}>{nome}</text>
        ) : (
          <text x={left - 8} y={cy} dy={5} textAnchor="end" fill="#475467" fontSize={13} fontWeight={600}>{nome}</text>
        )}
        {cabeAmbos ? (
          <text x={left + 14} y={cy} dy={5} fill="#ffffff" fontSize={12} fontWeight={700}
            style={{ fontVariantNumeric: 'tabular-nums' }}>{valor}</text>
        ) : (
          <text x={left - 8 - (cabeNome ? 0 : nomeW + 6)} y={cy} dy={5} textAnchor="end" fill="#dc3545" fontSize={12} fontWeight={700}
            style={{ fontVariantNumeric: 'tabular-nums' }}>{valor}</text>
        )}
      </g>
    );
  };

  // Top 5 (por valor) das categorias de despesas e receitas do DRE
  const dadosDespesasTop5 = (dadosDRE?.dados_barras_despesas || [])
    .slice().sort((a, b) => b.valor - a.valor).slice(0, 5);
  const dadosReceitasTop5 = (dadosDRE?.dados_barras_receitas || [])
    .slice().sort((a, b) => b.valor - a.valor).slice(0, 5);

  // Iguala o nº de linhas dos dois gráficos do DRE (preenche o lado menor com linhas
  // vazias/invisíveis) para que as colunas fiquem idênticas em altura e espessura de barra.
  const linhasDRE = Math.max(dadosDespesasTop5.length, dadosReceitasTop5.length, 1);
  const padToN = (arr, n) => {
    const out = [...arr];
    while (out.length < n) out.push({ categoria: '', categoria_completa: '', valor: 0, quantidade: 0, _placeholder: true });
    return out;
  };
  const dadosDespesasGrafico = padToN(dadosDespesasTop5, linhasDRE);
  const dadosReceitasGrafico = padToN(dadosReceitasTop5, linhasDRE);

  // Donut de composição das despesas por categoria (top 6 + "Outros")
  const dadosPizzaCategorias = (() => {
    const base = (dadosDRE?.dados_barras_despesas || []).slice().sort((a, b) => b.valor - a.valor);
    const top = base.slice(0, 6).map((it) => ({ nome: limparCategoria(it.categoria_completa || it.categoria), valor: it.valor }));
    const outros = base.slice(6).reduce((s, it) => s + (it.valor || 0), 0);
    if (outros > 0) top.push({ nome: 'Outros', valor: outros });
    return top;
  })();

  // ---- Filtro por categoria: reflete nos gráficos do DRE ----
  // Lista COMPLETA (ano) para o select e para saber tipo/nome da categoria escolhida.
  const catsOptDespesas = todasCategorias.despesas.length ? todasCategorias.despesas : (dadosDRE?.dados_barras_despesas || []);
  const catsOptReceitas = todasCategorias.receitas.length ? todasCategorias.receitas : (dadosDRE?.dados_barras_receitas || []);
  const todasCategoriasCombinado = [
    ...catsOptDespesas.map((c) => ({ ...c, _tipo: 'despesa' })),
    ...catsOptReceitas.map((c) => ({ ...c, _tipo: 'receita' })),
  ];
  // Dados do PERÍODO (para os valores exibidos nos gráficos)
  const catsPeriodo = [
    ...(dadosDRE?.dados_barras_despesas || []).map((c) => ({ ...c, _tipo: 'despesa' })),
    ...(dadosDRE?.dados_barras_receitas || []).map((c) => ({ ...c, _tipo: 'receita' })),
  ];
  const catFull = categoriaFiltro ? todasCategoriasCombinado.find((c) => c.categoria_completa === categoriaFiltro) : null;
  const catPeriodo = categoriaFiltro ? catsPeriodo.find((c) => c.categoria_completa === categoriaFiltro) : null;
  const catSel = catFull ? {
    categoria_completa: catFull.categoria_completa,
    categoria: catFull.categoria,
    _tipo: catFull._tipo,
    valor: catPeriodo ? catPeriodo.valor : 0,
    quantidade: catPeriodo ? catPeriodo.quantidade : 0,
  } : null;
  const totalTipoSel = catSel
    ? (catSel._tipo === 'despesa' ? (dadosDRE?.resumo_dre?.total_despesas || 0) : (dadosDRE?.resumo_dre?.total_receitas || 0))
    : (dadosDRE?.resumo_dre?.total_despesas || 0);

  // Séries dos gráficos considerando o filtro (só mostra a barra se houver valor no período)
  const despesasGraficoView = catSel ? (catSel._tipo === 'despesa' && catSel.valor > 0 ? [catSel] : []) : dadosDespesasGrafico;
  const receitasGraficoView = catSel ? (catSel._tipo === 'receita' && catSel.valor > 0 ? [catSel] : []) : dadosReceitasGrafico;
  const linhasView = catSel ? 1 : linhasDRE;

  // Donut: categoria selecionada x demais (do mesmo tipo)
  const pizzaView = catSel
    ? [
        { nome: limparCategoria(catSel.categoria_completa || catSel.categoria), valor: catSel.valor },
        { nome: 'Demais', valor: Math.max(totalTipoSel - catSel.valor, 0) },
      ]
    : dadosPizzaCategorias;

  // Evolução: quando filtrado, agrega os movimentos da categoria por mês
  const evolucaoView = (() => {
    if (!catSel) return dadosDRE?.dados_evolucao || [];
    const movs = (dadosDRE?.detalhes_por_categoria?.[categoriaFiltro]?.movimentos)
      || (dadosDRE?.detalhes_por_categoria_receita?.[categoriaFiltro]?.movimentos)
      || [];
    const parseMes = (d) => {
      if (!d) return null;
      const s = String(d);
      let m = s.match(/^(\d{2})\/(\d{2})\/(\d{4})/);
      if (m) return `${m[3]}-${m[2]}`;
      m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (m) return `${m[1]}-${m[2]}`;
      return null;
    };
    const porMes = {};
    movs.forEach((mv) => {
      const mes = parseMes(mv.data);
      if (!mes) return;
      porMes[mes] = (porMes[mes] || 0) + Math.abs(mv.valor || 0);
    });
    const ehReceita = catSel._tipo === 'receita';
    return Object.keys(porMes).sort().map((mes) => ({
      mes,
      receitas: ehReceita ? porMes[mes] : 0,
      despesas: ehReceita ? 0 : porMes[mes],
      resultado: ehReceita ? porMes[mes] : -porMes[mes],
    }));
  })();

  // ---- Card de Impostos: impostos pagos por mês, empilhados por TIPO de imposto ----


  // Fábrica de rótulo para as barras horizontais do DRE: categoria DENTRO (branco) se
  // couber, senão FORA; valor em R$ na ponta. (valores sempre positivos → barra p/ direita)
  const criarRotuloDRE = (dados) => (props) => {
    const { x, y, width, height, index } = props;
    const item = dados[index];
    if (!item || item._placeholder) return null;

    const nome = limparCategoria(item.categoria);
    const valor = formatarValor(item.valor);
    const cy = y + height / 2;
    const len = Math.max(width, 0);
    const nomeW = nome.length * 6.6 + 16;
    const valorW = valor.length * 7 + 16;
    const cabeNome = len > nomeW + 8;
    const cabeAmbos = len > nomeW + valorW;

    return (
      <g style={{ fontFamily: "'Inter Tight', sans-serif", pointerEvents: 'none' }}>
        {cabeNome ? (
          <text x={x + 14} y={cy} dy={4} fill="#ffffff" fontSize={12} fontWeight={700}>{nome}</text>
        ) : (
          <text x={x + len + 8} y={cy} dy={4} fill="#475467" fontSize={12} fontWeight={600}>{nome}</text>
        )}
        {cabeAmbos ? (
          <text x={x + len - 14} y={cy} dy={4} textAnchor="end" fill="#ffffff" fontSize={11.5} fontWeight={700}
            style={{ fontVariantNumeric: 'tabular-nums' }}>{valor}</text>
        ) : (
          <text x={x + len + (cabeNome ? 10 : nomeW + 14)} y={cy} dy={4} fill="#0e1726" fontSize={11.5} fontWeight={700}
            style={{ fontVariantNumeric: 'tabular-nums' }}>{valor}</text>
        )}
      </g>
    );
  };

  const dadosGraficoPieCategoria = dadosCompletos && dadosCompletos.analises.resumo_por_categoria ? 
    Object.entries(dadosCompletos.analises.resumo_por_categoria)
      .filter(([categoria, dados]) => dados.saldo_disponivel !== 0)
      .map(([categoria, dados]) => ({
        nome: dados.nome,
        valor: Math.abs(dados.saldo_disponivel),
        color: dados.cor,
        eh_negativo: dados.saldo_disponivel < 0
      }))
    : [];

  const obterDadosEvolucao = () => {
    if (!dadosCompletos?.historico_real?.evolucao_saldo) return [];
    
    return dadosCompletos.historico_real.evolucao_saldo.map(item => ({
      nome: item.nome,
      saldo: item.saldo,
      eh_previsao: item.eh_previsao || false
    }));
  };

  const obterDadosFluxoCaixa = () => {
    if (!dadosCompletos?.historico_real?.fluxo_caixa) return [];
    
    return dadosCompletos.historico_real.fluxo_caixa.map(item => ({
      nome: item.nome,
      entradas: item.entradas,
      saidas: item.saidas,
      saldo_liquido: item.saldo_liquido,
      eh_previsao: item.eh_previsao || false
    }));
  };

  const obterPrimeiraPrevisao = () => {
    const dados = obterDadosFluxoCaixa();
    const primeira = dados.find(d => d.eh_previsao);
    return primeira ? primeira.nome : null;
  };

  // Sparkline do card de saldo consolidado (a partir da evolução real)
  const obterSparkConsol = () => {
    const vals = obterDadosEvolucao()
      .filter(e => !e.eh_previsao)
      .map(e => e.saldo);
    if (vals.length < 2) return null;

    const min = Math.min(...vals);
    const max = Math.max(...vals);
    const range = max - min || 1;
    const W = 320;
    const H = 54;

    const pts = vals.map((v, i) => {
      const x = (i / (vals.length - 1)) * W;
      const y = H - 6 - ((v - min) / range) * (H - 12);
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    });

    const line = pts.map((p, i) => (i === 0 ? 'M' : 'L') + p).join(' ');
    const area = `${line} L${W},${H} L0,${H} Z`;
    return { line, area };
  };

  // ========================================
  // COMPONENTES DE TOOLTIP
  // ========================================
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const valor = payload[0].value;
      const corValor = valor >= 0 ? '#00C49F' : '#dc3545';
      
      return (
        <div style={{
          backgroundColor: '#002058',
          border: '1px solid #002058',
          borderRadius: '8px',
          padding: '12px 16px',
          boxShadow: '0 4px 12px rgba(0, 32, 88, 0.4)'
        }}>
          <p style={{
            color: '#ffffff',
            margin: '0 0 8px 0',
            fontWeight: 600,
            fontSize: '13px'
          }}>
            {label}
          </p>
          <p style={{
            color: '#ffffff',
            margin: 0,
            fontWeight: 500,
            fontSize: '17px'
          }}>
            Saldo: <span style={{ 
              color: corValor, 
              fontWeight: 600,
              marginLeft: '8px'
            }}>
              {formatarValor(valor)}
            </span>
          </p>
        </div>
      );
    }
    return null;
  };

  const CustomTooltipPie = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const valor = payload[0].value;
      const nome = payload[0].payload.nome;
      const ehNegativo = payload[0].payload.eh_negativo;
      const corValor = ehNegativo ? '#dc3545' : '#00C49F';
      
      return (
        <div style={{
          backgroundColor: '#002058',
          border: '1px solid #002058',
          borderRadius: '8px',
          padding: '12px 16px',
          boxShadow: '0 4px 12px rgba(0, 32, 88, 0.4)'
        }}>
          <p style={{
            color: '#ffffff',
            margin: '0 0 8px 0',
            fontWeight: 600,
            fontSize: '13px'
          }}>
            {nome}
          </p>
          <p style={{
            color: '#ffffff',
            margin: 0,
            fontWeight: 500,
            fontSize: '17px'
          }}>
            Valor: <span style={{ 
              color: corValor, 
              fontWeight: 600,
              marginLeft: '8px'
            }}>
              {ehNegativo ? '-' : ''}{formatarValor(valor)}
            </span>
          </p>
        </div>
      );
    }
    return null;
  };

  const CustomDot = (props) => {
    const { cx, cy, payload, fill } = props;
    
    if (payload.eh_previsao) {
      return (
        <g>
          <circle
            cx={cx}
            cy={cy}
            r={6}
            fill="white"
            stroke={fill}
            strokeWidth={2}
            strokeDasharray="3 3"
          />
          <circle
            cx={cx}
            cy={cy}
            r={3}
            fill={fill}
          />
        </g>
      );
    }
    
    return (
      <circle
        cx={cx}
        cy={cy}
        r={7}
        fill={fill}
        stroke="white"
        strokeWidth={2}
      />
    );
  };

  // ========================================
  // RENDERIZAÇÃO CONDICIONAL - LOADING
  // ========================================
  if (carregando && !dadosCliente) {
    return (
      <div className="das-page fmp-dash-page">
        <div className="container-fluid mt-5">
          <div className="text-center">
            <div className="spinner-border text-primary" style={{width: '3rem', height: '3rem'}}>
              <span className="visually-hidden">Carregando...</span>
            </div>
            <p className="mt-3 text-muted">
              <i className="fas fa-rocket me-2"></i>
              Carregando dados do cliente...
            </p>
            <div className="progress mt-3" style={{height: '6px', maxWidth: '300px', margin: '0 auto'}}>
              <div className="progress-bar progress-bar-striped progress-bar-animated" style={{width: '100%'}}></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ========================================
  // RENDERIZAÇÃO CONDICIONAL - CLIENTE NÃO ENCONTRADO
  // ========================================
  if (!dadosCliente) {
    return (
      <div className="das-page fmp-dash-page">
        <div className="container-fluid mt-5">
          <div className="alert alert-danger text-center">
            <i className="fas fa-exclamation-triangle fa-3x mb-3"></i>
            <h4>Cliente não encontrado</h4>
            <p>O cliente ID {cliente_id} não foi encontrado ou você não tem permissão para acessá-lo.</p>
            <button 
              className="btn btn-primary mt-3"
              onClick={() => navigate('/Clientes_bpo')}
            >
              <i className="fas fa-arrow-left me-2"></i>
              Voltar para Lista de Clientes
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ========================================
  // RENDERIZAÇÃO PRINCIPAL
  // ========================================
  return (
    <div className={`das-page fmp-dash-page ${insightsIA ? '' : 'ia-off'}`}>
      <div className="container-fluid mt-4">
        {/* ========================================
 {/* ========================================
    HEADER DINÂMICO
======================================== */}
<div className="row mb-4">
  <div className="col-12">
    <div className="card fmp-dash-hero">
      <div className="fmp-dash-grid" aria-hidden="true"></div>
      <div className="fmp-dash-aurora" aria-hidden="true">
        <span className="blob b1"></span>
        <span className="blob b2"></span>
        <span className="blob b3"></span>
      </div>
      <div className="card-body">
        {/* Topo: marca + plano */}
        <div className="fmp-dash-top">
          <div className="fmp-dash-brand">
            <img className="fmp-dash-brand-logo" src={logoComece} alt="Comece" />
            <span className="fmp-dash-brand-sep" aria-hidden="true"></span>
            <span className="fmp-dash-brand-tag">FMP</span>
          </div>
          <div className="fmp-dash-topright">
            <button
              type="button"
              className={`fmp-ia-toggle ${insightsIA ? 'on' : 'off'}`}
              onClick={toggleInsightsIA}
              title={insightsIA ? 'Insights de IA ativados — clique para desativar' : 'Insights de IA desativados — clique para ativar'}
              aria-pressed={insightsIA}
            >
              <i className="fas fa-brain"></i>
              <span className="lbl">IA</span>
              <span className="knob" aria-hidden="true"></span>
            </button>
            {dadosCliente.tipo_plano && (
              <span className={`fmp-dash-plan ${dadosCliente.tipo_plano === 'Premium' ? '' : 'demo'}`}>
                <i className={`fas ${dadosCliente.tipo_plano === 'Premium' ? 'fa-crown' : 'fa-flask'} me-1`}></i>
                {dadosCliente.tipo_plano === 'Premium' ? 'Versão Premium' : 'Versão Demo'}
              </span>
            )}
          </div>
        </div>

        {/* Grade: info do cliente + saldo consolidado */}
        <div className="fmp-dash-head">
          {/* Esquerda - Info Cliente */}
          <div>
            <span className="fmp-dash-eyebrow">Dashboard Financeiro</span>
            <h1 className="fmp-dash-title">
              {dadosCliente.nome} <span className="grad">— a saúde do seu financeiro em uma tela</span>
            </h1>
            <div className="fmp-dash-metarow">
              <span><i className="fas fa-building"></i>CNPJ: {dadosCliente.cnpj}</span>
              {dadosCliente.regime_tributario && (
                <span><i className="fas fa-balance-scale"></i>Regime: {dadosCliente.regime_tributario}</span>
              )}
              <span><i className="fas fa-database"></i>Sistema: {dadosCliente.sistema?.toUpperCase() || 'N/A'}</span>
              {contasCliente && contasCliente.length > 0 && (
                <span><i className="fas fa-university"></i>{contasCliente.length} conta(s) monitorada(s)</span>
              )}
              {dadosCliente.nome_operador && (
                <span><i className="fas fa-user-tie"></i>Operador: {dadosCliente.nome_operador}</span>
              )}
            </div>
          </div>

          {/* Direita - Saldo Consolidado */}
          <div className="fmp-dash-consol">
            <div className="ph">
              <span className="t">Saldo consolidado · todas as contas</span>
              {dadosCompletos && (
                <span className={`pill ${obterVariacaoReal() >= 0 ? 'green' : 'red'}`}>
                  {obterVariacaoReal() >= 0 ? '▲ +' : '▼ '}{obterVariacaoReal().toFixed(1).replace('.', ',')}% no mês
                </span>
              )}
            </div>
            <div className="big">
              {dadosCompletos ? <SaldoAnimado total={dadosCompletos.analises.total_saldo_disponivel} /> : '—'}
            </div>
            <div className="sub">
              {dadosCompletos
                ? `${dadosCompletos.analises.contas_ativas} de ${dadosCompletos.analises.total_contas} contas ativas`
                : 'Calculando saldo consolidado…'}
            </div>
            {(() => {
              const sp = dadosCompletos ? obterSparkConsol() : null;
              return sp ? (
                <svg className="spark" viewBox="0 0 320 54" preserveAspectRatio="none" aria-hidden="true">
                  <defs>
                    <linearGradient id="dashConsolGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0" stopColor="#3fd093" stopOpacity="0.42" />
                      <stop offset="1" stopColor="#3fd093" stopOpacity="0" />
                    </linearGradient>
                  </defs>
                  <path d={sp.area} fill="url(#dashConsolGrad)" />
                  <path d={sp.line} fill="none" stroke="#3fd093" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              ) : null;
            })()}
          </div>
        </div>

         {/* Botões de módulos */}
<div className="fmp-dash-mods">
  <button
    type="button"
    className="fmp-dash-mod"
    onClick={() => navigate('/Config_contas_omie/' + cliente_id)}
    title="Configurações de Contas"
  >
    <i className="fas fa-cogs"></i>Contas
  </button>

  {dadosCliente.mod_projetos !== 'Não' && (
  <button
    type="button"
    className="fmp-dash-mod"
    onClick={() => navigate('/Projetos_cliente/' + cliente_id)}
    title="Projetos"
  >
    <i className="fas fa-project-diagram"></i>Projetos
  </button>
  )}

  {dadosCliente.mod_saving !== 'Não' && (
  <button
    type="button"
    className="fmp-dash-mod"
    onClick={() => navigate('/Saving_cliente/' + cliente_id)}
    title="Saving — Controle de Pagamentos"
  >
    <i className="fas fa-piggy-bank"></i>Saving
  </button>
  )}

  {dadosCliente.mod_projecao_orcamentaria !== 'Não' && (
  <button
    type="button"
    className="fmp-dash-mod"
    onClick={() => navigate('/Planejamento_orcamentario/' + cliente_id)}
    title="Projeção Orçamentária"
  >
    <i className="fas fa-chart-pie"></i>Projeção Orçamentária
  </button>
  )}

  {dadosCliente.mod_dre_vertical !== 'Não' && (
  <button
    type="button"
    className="fmp-dash-mod"
    onClick={() => navigate('/Dre_vertical/' + cliente_id)}
    title="DRE Vertical (Demonstração do Resultado)"
  >
    <i className="fas fa-file-alt"></i>DRE Vertical
  </button>
  )}

  {dadosCliente.mod_dre_horizontal !== 'Não' && (
  <button
    type="button"
    className="fmp-dash-mod"
    onClick={() => navigate('/Dre_horizontal/' + cliente_id)}
    title="DRE Horizontal (meses nas colunas)"
  >
    <i className="fas fa-table-cells"></i>DRE Horizontal
  </button>
  )}

  {dadosCliente.mod_contas_pagar !== 'Não' && (
  <button
    type="button"
    className="fmp-dash-mod"
    onClick={() => navigate('/Apagar_cliente/' + cliente_id)}
    title="Contas a Pagar"
  >
    <i className="fas fa-file-invoice-dollar"></i>Contas a Pagar
  </button>
  )}

  {dadosCliente.mod_contas_receber !== 'Não' && (
  <button
    type="button"
    className="fmp-dash-mod"
    onClick={() => navigate('/Receber_cliente/' + cliente_id)}
    title="Contas a Receber"
  >
    <i className="fas fa-hand-holding-usd"></i>Contas a Receber
  </button>
  )}

  {dadosCliente.mod_posicao !== 'Não' && (
  <button
    type="button"
    className="fmp-dash-mod"
    onClick={() => navigate('/Posicao_cliente/' + cliente_id)}
    title="Posição Financeira"
  >
    <i className="fas fa-chart-pie"></i>Posição
  </button>
  )}

  {dadosCliente.mod_fechamento !== 'Não' && (
  <button
    type="button"
    className="fmp-dash-mod"
    onClick={() => navigate('/Fechamento_cliente/' + cliente_id)}
    title="Fechamento Mensal"
  >
    <i className="fas fa-calculator"></i>Fechamento
  </button>
  )}

  {dadosCliente.mod_envio_notas !== 'Não' && (
  <button
    type="button"
    className="fmp-dash-mod"
    onClick={() => navigate('/EnvioNotas_cliente/' + cliente_id)}
    title="Envio de Notas Fiscais"
  >
    <i className="fas fa-file-invoice-dollar"></i>Envio de Notas
  </button>
  )}

  {String(cliente_id) === '19' && (
    <button
      type="button"
      className="fmp-dash-mod"
      onClick={() => navigate('/Posicao_dia/19')}
      title="Posição do Dia"
    >
      <i className="fas fa-calendar-day"></i>Posição do Dia
    </button>
  )}
</div>
      </div>
    </div>
  </div>
</div>

        {/* Conteúdo do dashboard — sobe um pouco para dentro do hero (efeito da demo) */}
        <div className="fmp-dash-body">
        {/* Mensagens agora são exibidas como toast (react-toastify) */}

        {/* ========================================
            LOADING INICIAL DOS DADOS FINANCEIROS
        ======================================== */}
        {/* spinner enquanto NÃO houver dados — sem depender do flag 'carregando',
            que fica false por ~500ms entre o cadastro e os dados financeiros */}
        {!dadosCompletos && (
          <div className="row">
            <div className="col-12">
              <div className="text-center mt-5 mb-5">
                <div className="spinner-border text-primary" style={{width: '3rem', height: '3rem'}}>
                  <span className="visually-hidden">Carregando...</span>
                </div>
                <p className="mt-3 text-muted">
                  <i className="fas fa-rocket me-2"></i>
                  Inicializando Dashboard... Aguarde até 60 segundos
                </p>
                <div className="progress mt-3" style={{height: '6px', maxWidth: '300px', margin: '0 auto'}}>
                  <div className="progress-bar progress-bar-striped progress-bar-animated" style={{width: '100%'}}></div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ========================================
            CONTEÚDO PRINCIPAL - DADOS CARREGADOS
        ======================================== */}
        {dadosCompletos && (
          <>
            {/* Estatísticas dos Movimentos (considerado insight de IA) */}
            {insightsIA && dadosCompletos.estatisticas_movimentos && (
              <div className="row mb-4">
                <div className="col-12">
                  <div className="card border-0 shadow-sm">
                    <div className="card-header bg-white">
                      <h5 className="card-title mb-0">
                        <i className="fas fa-chart-bar me-2 text-info"></i>
                        Estatísticas Reais - Últimos {dadosCompletos.estatisticas_movimentos.meses_analisados} Meses
                      </h5>
                    </div>
                    <div className="card-body">
                      <div className="row">
                        <div className="col-md-2 text-center border-end">
                          <h6 className="text-muted small mb-1">Movimentos</h6>
                          <h5 className="text-primary mb-0">
                            <i className="fas fa-exchange-alt me-1"></i>
                            {dadosCompletos.estatisticas_movimentos.total_movimentos}
                          </h5>
                        </div>
                        <div className="col-md-2 text-center border-end">
                          <h6 className="text-muted small mb-1">Total Entradas</h6>
                          <h6 className="text-success mb-0">
                            {formatarValor(dadosCompletos.estatisticas_movimentos.total_entradas)}
                          </h6>
                        </div>
                        <div className="col-md-2 text-center border-end">
                          <h6 className="text-muted small mb-1">Total Saídas</h6>
                          <h6 className="text-danger mb-0">
                            {formatarValor(dadosCompletos.estatisticas_movimentos.total_saidas)}
                          </h6>
                        </div>
                        <div className="col-md-2 text-center border-end">
                          <h6 className="text-muted small mb-1">Saldo Líquido</h6>
                          <h6 className={`mb-0 ${dadosCompletos.estatisticas_movimentos.saldo_liquido_periodo >= 0 ? 'text-success' : 'text-danger'}`}>
                            {formatarValor(dadosCompletos.estatisticas_movimentos.saldo_liquido_periodo)}
                          </h6>
                        </div>
                        <div className="col-md-2 text-center border-end">
                          <h6 className="text-muted small mb-1">Média Entradas</h6>
                          <small className="text-info">
                            {formatarValor(dadosCompletos.estatisticas_movimentos.media_entradas_mensal)}
                          </small>
                        </div>
                        <div className="col-md-2 text-center">
                          <h6 className="text-muted small mb-1">Média Saídas</h6>
                          <small className="text-warning">
                            {formatarValor(dadosCompletos.estatisticas_movimentos.media_saidas_mensal)}
                          </small>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Análise de Tendências (IA) */}
            {insightsIA && dadosCompletos?.analises?.tendencias_ia &&
             Object.keys(dadosCompletos.analises.tendencias_ia).length > 0 && (
              <div className="row mb-4">
                <div className="col-12">
                  <div className="card border-0 shadow-sm">
                    <div className="card-header bg-white">
                      <h5 className="card-title mb-0">
                        <i className="fas fa-brain me-2"></i>
                        Análise Preditiva - Comparação Histórico vs Previsão IA
                      </h5>
                    </div>
                    <div className="card-body">
                      <div className="row">
                        <div className="col-md-4 text-center border-end">
                          <div className="mb-2">
                            <i className={`fas fa-arrow-${dadosCompletos.analises.tendencias_ia.entradas?.direcao === 'crescimento' ? 'up' : 'down'} fa-2x ${dadosCompletos.analises.tendencias_ia.entradas?.direcao === 'crescimento' ? 'text-success' : 'text-danger'}`}></i>
                          </div>
                          <h6 className="text-muted small">TENDÊNCIA - ENTRADAS</h6>
                          <h4 className={`mb-1 ${dadosCompletos.analises.tendencias_ia.entradas?.direcao === 'crescimento' ? 'text-success' : 'text-danger'}`}>
                            {dadosCompletos.analises.tendencias_ia.entradas?.tendencia_percentual >= 0 ? '+' : ''}
                            {dadosCompletos.analises.tendencias_ia.entradas?.tendencia_percentual.toFixed(1)}%
                          </h4>
                          <small className="text-muted d-block">
                            <strong>Histórico:</strong> {formatarValor(dadosCompletos.analises.tendencias_ia.entradas?.media_historica)}
                          </small>
                          <small className="text-primary">
                            <strong>IA prevê:</strong> {formatarValor(dadosCompletos.analises.tendencias_ia.entradas?.media_prevista)}
                          </small>
                        </div>
                        
                        <div className="col-md-4 text-center border-end">
                          <div className="mb-2">
                            <i className={`fas fa-arrow-${dadosCompletos.analises.tendencias_ia.saidas?.direcao === 'crescimento' ? 'up' : 'down'} fa-2x ${dadosCompletos.analises.tendencias_ia.saidas?.direcao === 'crescimento' ? 'text-danger' : 'text-success'}`}></i>
                          </div>
                          <h6 className="text-muted small">TENDÊNCIA - SAÍDAS</h6>
                          <h4 className={`mb-1 ${dadosCompletos.analises.tendencias_ia.saidas?.direcao === 'crescimento' ? 'text-danger' : 'text-success'}`}>
                            {dadosCompletos.analises.tendencias_ia.saidas?.tendencia_percentual >= 0 ? '+' : ''}
                            {dadosCompletos.analises.tendencias_ia.saidas?.tendencia_percentual.toFixed(1)}%
                          </h4>
                          <small className="text-muted d-block">
                            <strong>Histórico:</strong> {formatarValor(dadosCompletos.analises.tendencias_ia.saidas?.media_historica)}
                          </small>
                          <small className="text-warning">
                            <strong>IA prevê:</strong> {formatarValor(dadosCompletos.analises.tendencias_ia.saidas?.media_prevista)}
                          </small>
                        </div>
                        
                        <div className="col-md-4 text-center">
                          <div className="mb-2">
                            <i className={`fas fa-balance-scale fa-2x ${dadosCompletos.analises.tendencias_ia.saldo?.direcao === 'crescimento' ? 'text-success' : 'text-danger'}`}></i>
                          </div>
                          <h6 className="text-muted small">TENDÊNCIA - SALDO LÍQUIDO</h6>
                          <h4 className={`mb-1 ${dadosCompletos.analises.tendencias_ia.saldo?.direcao === 'crescimento' ? 'text-success' : 'text-danger'}`}>
                            {dadosCompletos.analises.tendencias_ia.saldo?.tendencia_percentual >= 0 ? '+' : ''}
                            {dadosCompletos.analises.tendencias_ia.saldo?.tendencia_percentual.toFixed(1)}%
                          </h4>
                          <small className="text-muted d-block">
                            <strong>Histórico:</strong> {formatarValor(dadosCompletos.analises.tendencias_ia.saldo?.medio_historico)}
                          </small>
                          <small className="text-info">
                            <strong>IA prevê:</strong> {formatarValor(dadosCompletos.analises.tendencias_ia.saldo?.medio_previsto)}
                          </small>
                        </div>
                      </div>
                      
                      <hr />
                      
                      <div className="alert mb-0 border-start border-4 fmp-insight">
                        <div className="row align-items-center">
                          <div className="col-12">
                            <strong style={{fontSize: '15.5px'}}><i className="fas fa-lightbulb text-warning me-2"></i>Insight IA:</strong>
                            <p className="mb-0" style={{fontSize: '15.5px', lineHeight: 1.65, marginTop: '6px'}}>
  {dadosCompletos.analises.tendencias_ia.saldo?.direcao === 'crescimento' 
    ? `Tendência positiva identificada! Com base na análise dos dados históricos e padrões de comportamento financeiro, a inteligência artificial projeta um aumento aproximado de ${dadosCompletos.analises.tendencias_ia.saldo?.tendencia_percentual.toFixed(1)}% no saldo líquido médio mensal. Esse cenário indica uma evolução saudável das suas finanças, possivelmente impulsionada por maior controle de despesas ou aumento de receitas. Recomenda-se manter as estratégias atuais e, se possível, reforçar investimentos ou criar reservas para potencializar ainda mais esse crescimento.`
    
    : `Atenção ao cenário financeiro! A análise preditiva da inteligência artificial aponta uma possível redução de ${Math.abs(dadosCompletos.analises.tendencias_ia.saldo?.tendencia_percentual).toFixed(1)}% no saldo líquido médio mensal. Essa tendência pode estar relacionada ao aumento de despesas, queda de receitas ou desequilíbrios no fluxo financeiro. É altamente recomendável revisar gastos recorrentes, reavaliar despesas programadas e, se necessário, ajustar o planejamento financeiro para evitar impactos mais significativos no futuro.`}
</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Mensagem quando não há dados futuros */}
            {dadosCompletos && (!dadosCompletos.analises?.tendencias_ia || 
              Object.keys(dadosCompletos.analises.tendencias_ia).length === 0) && (
              <div className="row mb-4">
                <div className="col-12">
                  <div className="card border-0 shadow-sm">
                    <div className="card-body text-center py-4">
                      <i className="fas fa-info-circle fa-3x text-info mb-3"></i>
                      <h5 className="text-muted">Análise Preditiva Indisponível</h5>
                      <p className="text-muted mb-0">
                        Não há dados futuros suficientes no período para gerar previsões.
                        <br />
                        <small>A análise preditiva requer movimentos programados (contas a pagar/receber) nos próximos meses.</small>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Cards de Resumo */}
            <div className="row mb-4">
              <div className="col-lg-3 col-md-6 mb-3">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-body">
                    <div className="d-flex align-items-center mb-2">
                      <div className="text-success me-2">
                        <i className="fas fa-coins fa-2x"></i>
                      </div>
                      <div>
                        <h6 className="card-title text-muted mb-0">Total Disponível</h6>
                      </div>
                    </div>
                    <h4 className="text-success mb-1">
                      {formatarValor(dadosCompletos.analises.total_saldo_disponivel)}
                    </h4>
                    <small className="text-muted">
                      {dadosCompletos.analises.contas_ativas} de {dadosCompletos.analises.total_contas} contas
                    </small>
                  </div>
                </div>
              </div>
              
              <div className="col-lg-3 col-md-6 mb-3">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-body">
                    <div className="d-flex align-items-center mb-2">
                      <div className="text-primary me-2">
                        <i className="fas fa-chart-line fa-2x"></i>
                      </div>
                      <div>
                        <h6 className="card-title text-muted mb-0">Investimentos</h6>
                      </div>
                    </div>
                    <h4 className="text-primary mb-1">
                      {formatarValor(dadosCompletos.analises.total_investimentos || 0)}
                    </h4>
                    <small className="text-muted">
                      {dadosCompletos.analises.percentual_investido?.toFixed(1) || '0'}% do patrimônio
                    </small>
                  </div>
                </div>
              </div>
              
              <div className="col-lg-3 col-md-6 mb-3">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-body">
                    <div className="d-flex align-items-center mb-2">
                      <div className="me-2" style={{color: '#0596ab'}}>
                        <i className="fas fa-university fa-2x"></i>
                      </div>
                      <div>
                        <h6 className="card-title text-muted mb-0">Conta Corrente</h6>
                      </div>
                    </div>
                    <h4 className="mb-1" style={{color: '#0596ab'}}>
                      {formatarValor(dadosCompletos.analises.total_conta_corrente || 0)}
                    </h4>
                    <small className="text-muted">Liquidez imediata</small>
                  </div>
                </div>
              </div>
              
              <div className="col-lg-3 col-md-6 mb-3">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-body">
                    <div className="d-flex align-items-center mb-2">
                      <div className={`${obterVariacaoReal() >= 0 ? 'text-success' : 'text-danger'} me-2`}>
                        <i className="fas fa-percentage fa-2x"></i>
                      </div>
                      <div>
                        <h6 className="card-title text-muted mb-0">Variação Real</h6>
                      </div>
                    </div>
                    <h4 className={`${obterVariacaoReal() >= 0 ? 'text-success' : 'text-danger'} mb-1`}>
                      {obterVariacaoReal() >= 0 ? '+' : ''}{obterVariacaoReal().toFixed(1)}%
                    </h4>
                    <small className={obterVariacaoReal() >= 0 ? 'text-success' : 'text-danger'}>
                      {obterVariacaoReal() >= 0 ? 'Crescimento' : 'Redução'} mensal
                    </small>
                  </div>
                </div>
              </div>
            </div>

            {/* Gráficos de Barras e Pizza */}
            <div className="row mb-4">
              <div className="col-lg-8">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-header bg-white">
                    <h5 className="card-title mb-0">
                      <i className="fas fa-chart-bar me-2 text-primary"></i>
                      Saldos Atuais por Conta
                    </h5>
                  </div>
                  <div className="card-body">
                    <ResponsiveContainer width="100%" height={Math.max(260, dadosGraficoBarrasOrdenado.length * 60)}>
                      <BarChart
                        data={dadosGraficoBarrasOrdenado}
                        layout="vertical"
                        margin={{ top: 8, right: 120, left: 130, bottom: 8 }}
                        barCategoryGap="14%"
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#eef1f6" horizontal={false} />
                        <XAxis
                          type="number"
                          tickFormatter={(value) => formatarValor(value)}
                          tick={{ fontSize: 11, fill: '#7d8aa3' }}
                        />
                        <YAxis type="category" dataKey="nome" hide />
                        <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(0,32,91,0.04)' }} />
                        <Bar dataKey="saldo" radius={[0, 10, 10, 0]} minPointSize={6} className="fmp-bar-shadow" label={renderBarraContaLabel} isAnimationActive={true}>
                          {dadosGraficoBarrasOrdenado.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color || COLORS[index % COLORS.length]} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
              
              <div className="col-lg-4">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-header bg-white">
                    <h5 className="card-title mb-0">
                      <i className="fas fa-chart-pie me-2 text-success"></i>
                      Diversificação
                    </h5>
                  </div>
                  <div className="card-body">
                    {dadosGraficoPieCategoria.length > 0 ? (
                      <DonutDespesas
                        dados={dadosGraficoPieCategoria}
                        total={dadosGraficoPieCategoria.reduce((s, d) => s + (d.valor || 0), 0)}
                        cores={dadosGraficoPieCategoria.map((d, i) => d.color || CORES_PIZZA[i % CORES_PIZZA.length])}
                        rotulo="Saldo total"
                      />
                    ) : (
                      <div className="text-center py-5 text-muted">
                        <i className="fas fa-chart-pie fa-3x mb-3" style={{ color: '#cbd5e1' }}></i>
                        <p>Sem contas para exibir</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Evolução do Saldo (Histórico + Previsão IA) */}
            {insightsIA && obterDadosEvolucao().length > 0 && (
              <div className="row mb-4">
                <div className="col-12">
                  <div className="card border-0 shadow-sm">
                    <div className="card-header bg-white d-flex justify-content-between align-items-center">
                      <h5 className="card-title mb-0">
                        <i className="fas fa-chart-area me-2 text-success"></i>
                        Evolução do Saldo Acumulado (Histórico + Previsão IA)
                      </h5>
                      <div>
                        <span className="badge bg-success me-2">
                          <i className="fas fa-check me-1"></i>
                          Histórico
                        </span>
                        <span className="badge bg-warning text-dark">
                          <i className="fas fa-robot me-1"></i>
                          Previsão IA
                        </span>
                      </div>
                    </div>
                    <div className="card-body">
                      <ResponsiveContainer width="100%" height={350}>
                        <AreaChart data={obterDadosEvolucao()}>
                          <defs>
                            <linearGradient id="colorSaldo" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#00C49F" stopOpacity={0.8}/>
                              <stop offset="95%" stopColor="#00C49F" stopOpacity={0.1}/>
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                          <XAxis dataKey="nome" tick={{ fontSize: 12 }} />
                          <YAxis tickFormatter={(value) => formatarValor(value)} tick={{ fontSize: 12 }} />
                          <Tooltip 
                            formatter={(value, name, props) => [
                              formatarValor(value), 
                              props.payload.eh_previsao ? 'Previsto (IA)' : 'Saldo Real'
                            ]}
                          />
                          
                          {obterPrimeiraPrevisao() && (
                            <ReferenceLine 
                              x={obterPrimeiraPrevisao()} 
                              stroke="#ff6b6b" 
                              strokeDasharray="5 5"
                              strokeWidth={2}
                              label={{ 
                                value: '← Real | IA →', 
                                position: 'top',
                                fill: '#ff6b6b',
                                fontSize: 12
                              }}
                            />
                          )}
                          
                          <Area 
                            type="monotone" 
                            dataKey="saldo" 
                            stroke="#00C49F" 
                            fill="url(#colorSaldo)"
                            strokeWidth={3}
                            dot={<CustomDot fill="#00C49F" />}
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                      <div className="mt-2 text-center">
                        <small className="text-muted">
                          <i className="fas fa-info-circle me-1"></i>
                          Saldo consolidado de todas as contas (histórico + previsão IA)
                        </small>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Fluxo de Caixa Mensal (IA) */}
            {insightsIA && obterDadosFluxoCaixa().length > 0 && (
              <div className="row mb-4">
                <div className="col-12">
                  <div className="card border-0 shadow-sm">
                    <div className="card-header bg-white d-flex justify-content-between align-items-center">
                      <h5 className="card-title mb-0">
                        <i className="fas fa-exchange-alt me-2 text-primary"></i>
                        Fluxo de Caixa - Histórico + Previsão IA
                      </h5>
                      <div>
                        <span className="badge bg-success me-2">
                          <i className="fas fa-database me-1"></i>
                          Dados Reais
                        </span>
                        <span className="badge bg-warning text-dark">
                          <i className="fas fa-brain me-1"></i>
                          IA
                        </span>
                      </div>
                    </div>
                    <div className="card-body">
                      <ResponsiveContainer width="100%" height={400}>
                        <LineChart data={obterDadosFluxoCaixa()}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                          <XAxis dataKey="nome" tick={{ fontSize: 12 }} />
                          <YAxis tickFormatter={(value) => formatarValor(value)} tick={{ fontSize: 12 }} />
                          <Tooltip 
                            formatter={(value, name, props) => {
                              const tipo = props.payload.eh_previsao ? ' (IA)' : ' (Real)';
                              return [formatarValor(value), name + tipo];
                            }}
                          />
                          <Legend />
                          
                          {obterPrimeiraPrevisao() && (
                            <ReferenceLine 
                              x={obterPrimeiraPrevisao()} 
                              stroke="#ff6b6b" 
                              strokeDasharray="5 5"
                              label={{ value: 'Previsão IA ↓', position: 'top', fill: '#ff6b6b' }}
                            />
                          )}
                          
                          <Line 
                            type="monotone" 
                            dataKey="entradas" 
                            name="Entradas" 
                            stroke="#28a745" 
                            strokeWidth={3}
                            dot={<CustomDot fill="#28a745" />}
                          />
                          <Line 
                            type="monotone" 
                            dataKey="saidas" 
                            name="Saídas" 
                            stroke="#dc3545" 
                            strokeWidth={3}
                            dot={<CustomDot fill="#dc3545" />}
                          />
                          <Line 
                            type="monotone" 
                            dataKey="saldo_liquido" 
                            name="Saldo Líquido" 
                            stroke="#007bff" 
                            strokeWidth={3}
                            strokeDasharray="8 8"
                            dot={<CustomDot fill="#007bff" />}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* ========================================
                FLUXO DE CAIXA DIÁRIO
            ======================================== */}
            <div className="row mb-4">
              <div className="col-12">
                <div className="card border-0 shadow-sm">
                  <div className="card-header bg-white">
                    <div className="d-flex justify-content-between align-items-center">
                      <h5 className="card-title mb-0">
                        <i className="fas fa-calendar-day me-2"></i>
                        Fluxo de Caixa Diário Conta Corrente- {formatarMesAno(dadosFluxoDiario?.resumo?.mes_referencia)} 
                      </h5>
                      <div>
                        <span className="badge bg-light text-dark me-2">
                          <i className="fas fa-check-circle me-1 text-success"></i>
                          Realizado
                        </span>
                        <span className="badge bg-warning text-dark">
                          <i className="fas fa-clock me-1"></i>
                          Previsto
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="card-body">
                    {carregandoFluxoDiario ? (
                      <div className="text-center py-5">
                        <div className="spinner-border" style={{color: '#005f6b'}}></div>
                        <p className="mt-3 text-muted">Carregando Fluxo de Caixa Diário...</p>
                      </div>
                    ) : dadosFluxoDiario ? (
                      <>
                        {/* Resumo do Mês */}
                        <div className="row mb-4">
                          <div className="col-md-3 text-center border-end">
                            <h6 className="text-muted small mb-1">Saldo Inicial</h6>
                            <h6 className="text-info mb-0">
                              <i className="fas fa-wallet me-1"></i>
                              {formatarValor(dadosFluxoDiario.resumo.saldo_inicial)}
                            </h6>
                          </div>
                          <div className="col-md-3 text-center border-end">
                            <h6 className="text-muted small mb-1">Entradas Realizadas</h6>
                            <h6 className="text-success mb-0">
                              <i className="fas fa-arrow-down me-1"></i>
                              {formatarValor(dadosFluxoDiario.resumo.total_entradas_realizadas)}
                            </h6>
                            <small className="text-muted">{dadosFluxoDiario.resumo.qtd_movimentos_realizados} mov.</small>
                          </div>
                          <div className="col-md-3 text-center border-end">
                            <h6 className="text-muted small mb-1">Saídas Realizadas</h6>
                            <h6 className="text-danger mb-0">
                              <i className="fas fa-arrow-up me-1"></i>
                              {formatarValor(dadosFluxoDiario.resumo.total_saidas_realizadas)}
                            </h6>
                          </div>
                          <div className="col-md-3 text-center">
                            <h6 className="text-muted small mb-1">Saldo Final Previsto</h6>
                            <h6 className={`mb-0 ${dadosFluxoDiario.resumo.saldo_final_previsto >= 0 ? 'text-success' : 'text-danger'}`}>
                              <i className="fas fa-flag-checkered me-1"></i>
                              {formatarValor(dadosFluxoDiario.resumo.saldo_final_previsto)}
                            </h6>
                            <small className="text-muted">{dadosFluxoDiario.resumo.dias_restantes} dias restantes</small>
                          </div>
                        </div>
                        
                        {/* Gráfico de Barras - Entradas e Saídas por Dia */}
                        <h6 className="mb-3 d-flex align-items-center" style={{ fontWeight: 600, color: '#475467' }}>
                          <i className="fas fa-chart-bar me-2" style={{ color: '#00205b' }}></i>
                          Movimentação Diária
                          <span
                            className="badge ms-2"
                            style={{
                              backgroundColor: '#eaf2ff',
                              color: '#00205b',
                              fontWeight: 500,
                              cursor: 'pointer'
                            }}
                          >
                            <i className="fas fa-hand-pointer me-1"></i>
                            Clique nas barras
                          </span>
                        </h6>

                        <div style={{ height: 520 }}>
                          <FluxoCaixaDiarioChart
                            dados={dadosFluxoDiario.dados_grafico}
                            resumo={dadosFluxoDiario.resumo}
                            onClickDia={mostrarDetalhesDia}
                          />
                        </div>

                        {/* Legenda explicativa */}
                        <div className="row mt-3">
                          <div className="col-12">
                            <div className="alert alert-info border-start border-4 mb-0" style={{borderColor: '#005f6b'}}>
                              <div className="row align-items-center">
                                <div className="col-md-8">
                                  <small>
                                    <strong><i className="fas fa-info-circle me-1"></i>Como interpretar:</strong>
                                    <ul className="mb-0 mt-1">
                                      <li><span className="text-success">Barras verdes</span> = Entradas (recebimentos)</li>
                                      <li><span className="text-danger">Barras vermelhas</span> = Saídas (pagamentos)</li>
                                      <li><span className="text-primary">Linha azul</span> = Saldo acumulado (evolução do caixa)</li>
                                      <li><span className="text-danger">Linha vertical vermelha</span> = Dia atual</li>
                                      <li>Dias à direita da linha = <strong>Previsões</strong> (contas a pagar/receber)</li>
                                    </ul>
                                  </small>
                                </div>
                                <div className="col-md-4 text-end">
                                  <span className="badge bg-success me-1">
                                    <i className="fas fa-check me-1"></i>
                                    {dadosFluxoDiario.resumo.qtd_movimentos_realizados} Realizados
                                  </span>
                                  <span className="badge bg-warning text-dark">
                                    <i className="fas fa-clock me-1"></i>
                                    {dadosFluxoDiario.resumo.qtd_contas_pagar + dadosFluxoDiario.resumo.qtd_contas_receber} Previstos
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className="text-center py-5 text-muted">
                        <i className="fas fa-calendar-day fa-3x mb-3" style={{color: '#005f6b'}}></i>
                        <p>Aguarde o carregamento automático do Fluxo de Caixa Diário</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* ========================================
                ANÁLISE INTELIGENTE DO FLUXO DIÁRIO
            ======================================== */}
            {insightsIA && dadosFluxoDiario?.analise_inteligente?.dia_maior_entrada && dadosFluxoDiario?.analise_inteligente?.dia_maior_saida && dadosFluxoDiario?.analise_inteligente?.dia_melhor_saldo && dadosFluxoDiario?.analise_inteligente?.dia_pior_saldo && (
              <div className="row mb-4">
                <div className="col-12">
                  <div className="card border-0 shadow-sm">
                    <div className="card-header bg-white">
                      <h6 className="card-title mb-0">
                        <i className="fas fa-brain me-2"></i>
                        Análise Inteligente - Fluxo de Caixa Diário
                      </h6>
                    </div>
                    <div className="card-body">
                      {/* Linha 1: Dias Destaque */}
                      <div className="row mb-4">
                        <div className="col-md-3 text-center border-end">
                          <div className="mb-2">
                            <i className="fas fa-arrow-up fa-2x text-success"></i>
                          </div>
                          <h6 className="text-muted small">Maior Entrada</h6>
                          <h5 className="text-success mb-1">
                            {formatarValor(dadosFluxoDiario.analise_inteligente.dia_maior_entrada.valor)}
                          </h5>
                          <small className="text-muted d-block">
                            Dia {dadosFluxoDiario.analise_inteligente.dia_maior_entrada.dia} - {dadosFluxoDiario.analise_inteligente.dia_maior_entrada.data}
                          </small>
                          <small className="text-primary">
                            {dadosFluxoDiario.analise_inteligente.dia_maior_entrada.qtd_movimentos} movimentos
                          </small>
                        </div>

                        <div className="col-md-3 text-center border-end">
                          <div className="mb-2">
                            <i className="fas fa-arrow-down fa-2x text-danger"></i>
                          </div>
                          <h6 className="text-muted small">Maior Saída</h6>
                          <h5 className="text-danger mb-1">
                            {formatarValor(dadosFluxoDiario.analise_inteligente.dia_maior_saida.valor)}
                          </h5>
                          <small className="text-muted d-block">
                            Dia {dadosFluxoDiario.analise_inteligente.dia_maior_saida.dia} - {dadosFluxoDiario.analise_inteligente.dia_maior_saida.data}
                          </small>
                          <small className="text-warning">
                            {dadosFluxoDiario.analise_inteligente.dia_maior_saida.qtd_movimentos} movimentos
                          </small>
                        </div>

                        <div className="col-md-3 text-center border-end">
                          <div className="mb-2">
                            <i className="fas fa-thumbs-up fa-2x text-success"></i>
                          </div>
                          <h6 className="text-muted small">Melhor Saldo Diário</h6>
                          <h5 className="text-success mb-1">
                            {formatarValor(dadosFluxoDiario.analise_inteligente.dia_melhor_saldo.valor)}
                          </h5>
                          <small className="text-muted d-block">
                            Dia {dadosFluxoDiario.analise_inteligente.dia_melhor_saldo.dia} - {dadosFluxoDiario.analise_inteligente.dia_melhor_saldo.data}
                          </small>
                        </div>

                        <div className="col-md-3 text-center">
                          <div className="mb-2">
                            <i className={`fas fa-thumbs-down fa-2x ${dadosFluxoDiario.analise_inteligente.dia_pior_saldo.valor < 0 ? 'text-danger' : 'text-warning'}`}></i>
                          </div>
                          <h6 className="text-muted small">Pior Saldo Diário</h6>
                          <h5 className={`mb-1 ${dadosFluxoDiario.analise_inteligente.dia_pior_saldo.valor < 0 ? 'text-danger' : 'text-warning'}`}>
                            {formatarValor(dadosFluxoDiario.analise_inteligente.dia_pior_saldo.valor)}
                          </h5>
                          <small className="text-muted d-block">
                            Dia {dadosFluxoDiario.analise_inteligente.dia_pior_saldo.dia} - {dadosFluxoDiario.analise_inteligente.dia_pior_saldo.data}
                          </small>
                        </div>
                      </div>

                      <hr />

                      {/* Linha 2: Concentração por Período */}
                      <h6 className="text-muted mb-3">
                        <i className="fas fa-chart-pie me-2"></i>
                        Concentração de Movimentos no Mês
                      </h6>
                      <div className="row mb-4">
                        <div className="col-md-4 text-center border-end">
                          <div className="mb-2">
                            <span className="badge bg-primary" style={{fontSize: '14px'}}>
                              Início do Mês (Dias 1-10)
                            </span>
                          </div>
                          <div className="row mt-3">
                            <div className="col-6">
                              <small className="text-muted d-block">Entradas</small>
                              <h6 className="text-success mb-0">
                                {formatarValor(dadosFluxoDiario.analise_inteligente.concentracao_periodo.inicio_mes.entradas)}
                              </h6>
                              <small className="text-primary">
                                {dadosFluxoDiario.analise_inteligente.concentracao_periodo.inicio_mes.percentual_entradas}% do total
                              </small>
                            </div>
                            <div className="col-6">
                              <small className="text-muted d-block">Saídas</small>
                              <h6 className="text-danger mb-0">
                                {formatarValor(dadosFluxoDiario.analise_inteligente.concentracao_periodo.inicio_mes.saidas)}
                              </h6>
                              <small className="text-warning">
                                {dadosFluxoDiario.analise_inteligente.concentracao_periodo.inicio_mes.percentual_saidas}% do total
                              </small>
                            </div>
                          </div>
                          <div className="mt-2">
                            <small className="text-muted">Saldo: </small>
                            <strong className={dadosFluxoDiario.analise_inteligente.concentracao_periodo.inicio_mes.saldo >= 0 ? 'text-success' : 'text-danger'}>
                              {formatarValor(dadosFluxoDiario.analise_inteligente.concentracao_periodo.inicio_mes.saldo)}
                            </strong>
                          </div>
                        </div>

                        <div className="col-md-4 text-center border-end">
                          <div className="mb-2">
                            <span className="badge bg-info" style={{fontSize: '14px'}}>
                              Meio do Mês (Dias 11-20)
                            </span>
                          </div>
                          <div className="row mt-3">
                            <div className="col-6">
                              <small className="text-muted d-block">Entradas</small>
                              <h6 className="text-success mb-0">
                                {formatarValor(dadosFluxoDiario.analise_inteligente.concentracao_periodo.meio_mes.entradas)}
                              </h6>
                              <small className="text-primary">
                                {dadosFluxoDiario.analise_inteligente.concentracao_periodo.meio_mes.percentual_entradas}% do total
                              </small>
                            </div>
                            <div className="col-6">
                              <small className="text-muted d-block">Saídas</small>
                              <h6 className="text-danger mb-0">
                                {formatarValor(dadosFluxoDiario.analise_inteligente.concentracao_periodo.meio_mes.saidas)}
                              </h6>
                              <small className="text-warning">
                                {dadosFluxoDiario.analise_inteligente.concentracao_periodo.meio_mes.percentual_saidas}% do total
                              </small>
                            </div>
                          </div>
                          <div className="mt-2">
                            <small className="text-muted">Saldo: </small>
                            <strong className={dadosFluxoDiario.analise_inteligente.concentracao_periodo.meio_mes.saldo >= 0 ? 'text-success' : 'text-danger'}>
                              {formatarValor(dadosFluxoDiario.analise_inteligente.concentracao_periodo.meio_mes.saldo)}
                            </strong>
                          </div>
                        </div>

                        <div className="col-md-4 text-center">
                          <div className="mb-2">
                            <span className="badge bg-warning text-dark" style={{fontSize: '14px'}}>
                              Fim do Mês (Dias 21+)
                            </span>
                          </div>
                          <div className="row mt-3">
                            <div className="col-6">
                              <small className="text-muted d-block">Entradas</small>
                              <h6 className="text-success mb-0">
                                {formatarValor(dadosFluxoDiario.analise_inteligente.concentracao_periodo.fim_mes.entradas)}
                              </h6>
                              <small className="text-primary">
                                {dadosFluxoDiario.analise_inteligente.concentracao_periodo.fim_mes.percentual_entradas}% do total
                              </small>
                            </div>
                            <div className="col-6">
                              <small className="text-muted d-block">Saídas</small>
                              <h6 className="text-danger mb-0">
                                {formatarValor(dadosFluxoDiario.analise_inteligente.concentracao_periodo.fim_mes.saidas)}
                              </h6>
                              <small className="text-warning">
                                {dadosFluxoDiario.analise_inteligente.concentracao_periodo.fim_mes.percentual_saidas}% do total
                              </small>
                            </div>
                          </div>
                          <div className="mt-2">
                            <small className="text-muted">Saldo: </small>
                            <strong className={dadosFluxoDiario.analise_inteligente.concentracao_periodo.fim_mes.saldo >= 0 ? 'text-success' : 'text-danger'}>
                              {formatarValor(dadosFluxoDiario.analise_inteligente.concentracao_periodo.fim_mes.saldo)}
                            </strong>
                          </div>
                        </div>
                      </div>

                      <hr />

                     {/* Linha 3: Estatísticas e Insights */}
<div className="row">
  <div className="col-md-8">
    <div className="alert border-start border-4 mb-0 fmp-insight">
      <h6 className="alert-heading">
        <i className="fas fa-lightbulb me-2 text-warning"></i>
        Insights Inteligentes
      </h6>
      <small>
        <ul className="mb-0">
          <li>
            <strong>Média Diária:</strong> Ao longo do período analisado, suas entradas apresentam uma média de {formatarValor(dadosFluxoDiario.analise_inteligente.medias_diarias.entrada_media)} por dia, 
            enquanto as saídas atingem {formatarValor(dadosFluxoDiario.analise_inteligente.medias_diarias.saida_media)}. 
            Isso resulta em um saldo médio diário de {formatarValor(dadosFluxoDiario.analise_inteligente.medias_diarias.saldo_medio)}, 
            indicando o ritmo real de geração (ou consumo) de caixa no dia a dia.
          </li>

          <li>
            <strong>Taxa de Atividade:</strong> Foi registrada movimentação financeira em {dadosFluxoDiario.analise_inteligente.taxa_atividade}% dos dias do período 
            ({dadosFluxoDiario.analise_inteligente.total_dias_com_movimento} de {dadosFluxoDiario.resumo.dia_atual} dias). 
            Esse indicador demonstra o nível de consistência operacional e frequência de uso do caixa.
          </li>

          {dadosFluxoDiario.analise_inteligente.dias_criticos > 0 && (
            <li className="text-danger">
              <strong><i className="fas fa-exclamation-triangle text-warning me-1" aria-hidden="true"></i>Dias Críticos:</strong> Foram identificados {dadosFluxoDiario.analise_inteligente.dias_criticos} dia(s) com saldo diário negativo. 
              Esse comportamento pode sinalizar descasamento entre entradas e saídas, sendo importante avaliar a distribuição dos pagamentos e recebimentos.
            </li>
          )}

          <li>
            <strong>Padrão de Concentração:</strong>{' '}
            {dadosFluxoDiario.analise_inteligente.concentracao_periodo.inicio_mes.percentual_entradas > 
             dadosFluxoDiario.analise_inteligente.concentracao_periodo.meio_mes.percentual_entradas &&
             dadosFluxoDiario.analise_inteligente.concentracao_periodo.inicio_mes.percentual_entradas > 
             dadosFluxoDiario.analise_inteligente.concentracao_periodo.fim_mes.percentual_entradas
              ? 'A maior parte das entradas ocorre no início do mês, indicando possível concentração de receitas logo nos primeiros dias.'
              : dadosFluxoDiario.analise_inteligente.concentracao_periodo.meio_mes.percentual_entradas > 
                dadosFluxoDiario.analise_inteligente.concentracao_periodo.fim_mes.percentual_entradas
              ? 'As entradas se concentram no meio do mês, sugerindo um fluxo mais distribuído ao longo do período.'
              : 'O maior volume de entradas ocorre no final do mês, o que pode exigir maior controle de caixa ao longo dos dias anteriores.'
            }
          </li>
        </ul>
      </small>
    </div>
  </div>

                        <div className="col-md-4">
                          <div className="card bg-light border-0 h-100">
                            <div className="card-body text-center">
                              <h6 className="text-muted small mb-3">Resumo do Período</h6>
                              <div className="mb-2">
                                <small className="text-muted d-block">Dias Analisados</small>
                                <h4 className="text-primary mb-0">{dadosFluxoDiario.resumo.dia_atual}</h4>
                              </div>
                              <div className="mb-2">
                                <small className="text-muted d-block">Dias com Movimento</small>
                                <h5 className="text-info mb-0">{dadosFluxoDiario.analise_inteligente.total_dias_com_movimento}</h5>
                              </div>
                              {dadosFluxoDiario.analise_inteligente.dias_criticos > 0 && (
                                <div>
                                  <small className="text-danger d-block">Dias Críticos</small>
                                  <h5 className="text-danger mb-0">
                                    <i className="fas fa-exclamation-triangle me-1"></i>
                                    {dadosFluxoDiario.analise_inteligente.dias_criticos}
                                  </h5>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* ========================================
                FATURAMENTO COMPARATIVO (ANO ATUAL x ANTERIOR)
            ======================================== */}
            <div className="row mb-4">
              <div className="col-12">
                <div className="card border-0 shadow-sm">
                  <div className="card-header bg-white">
                    <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                      <h5 className="card-title mb-0">
                        <i className="fas fa-chart-column me-2" style={{ color: '#00205b' }}></i>
                        Faturamento — Comparativo Anual {fatComp.anoAnterior && fatComp.anoAtual ? `(${fatComp.anoAnterior} x ${fatComp.anoAtual})` : ''}
                      </h5>
                      <div className="d-flex align-items-center gap-2 flex-wrap">
                        <small className="text-muted me-1">Base:</small>
                        {[
                          { key: 'competencia', rotulo: 'Competência', hint: 'Pela data de registro/emissão do título' },
                          { key: 'pagamento', rotulo: 'Pagamento', hint: 'Pela data em que o valor foi recebido' },
                        ].map((b) => (
                          <button
                            key={b.key}
                            type="button"
                            className="btn btn-sm"
                            title={b.hint}
                            style={{
                              borderRadius: '999px',
                              padding: '3px 14px',
                              fontWeight: 600,
                              fontSize: '0.78rem',
                              border: '1px solid ' + (fatCompBase === b.key ? '#00205b' : '#d4e2fb'),
                              backgroundColor: fatCompBase === b.key ? '#00205b' : '#fff',
                              color: fatCompBase === b.key ? '#fff' : '#00205b',
                            }}
                            onClick={() => setFatCompBase(b.key)}
                          >
                            {b.rotulo}
                          </button>
                        ))}
                        <span className="mx-1" style={{ borderLeft: '1px solid #e2e8f4', height: 22 }}></span>
                        {[
                          { key: 'barras', icone: 'fa-chart-column', rotulo: 'Barras' },
                          { key: 'linhas', icone: 'fa-chart-line', rotulo: 'Linhas' },
                        ].map((t) => (
                          <button
                            key={t.key}
                            type="button"
                            className="btn btn-sm"
                            title={`Ver em ${t.rotulo.toLowerCase()}`}
                            style={{
                              borderRadius: '999px',
                              padding: '3px 14px',
                              fontWeight: 600,
                              fontSize: '0.78rem',
                              border: '1px solid ' + (fatCompTipo === t.key ? '#00205b' : '#d4e2fb'),
                              backgroundColor: fatCompTipo === t.key ? '#00205b' : '#fff',
                              color: fatCompTipo === t.key ? '#fff' : '#00205b',
                            }}
                            onClick={() => setFatCompTipo(t.key)}
                          >
                            <i className={`fas ${t.icone} me-1`}></i>{t.rotulo}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div className="card-body">
                    {carregandoFatComp ? (
                      <div className="text-center py-5">
                        <div className="spinner-border" style={{ color: '#00205b' }}></div>
                        <p className="mt-3 text-muted">Carregando faturamento comparativo...</p>
                      </div>
                    ) : fatComp.ok ? (
                      <>
                        <div className="row mb-3">
                          <div className="col-md-4 text-center border-end">
                            <h6 className="text-muted small mb-1">Total {fatComp.anoAnterior}</h6>
                            <h4 className="mb-0" style={{ color: '#7d8aa3' }}>{formatarValor(fatComp.totalAnterior)}</h4>
                          </div>
                          <div className="col-md-4 text-center border-end">
                            <h6 className="text-muted small mb-1">Total {fatComp.anoAtual}</h6>
                            <h4 className="mb-0" style={{ color: '#00205b' }}>{formatarValor(fatComp.totalAtual)}</h4>
                          </div>
                          <div className="col-md-4 text-center">
                            <h6 className="text-muted small mb-1">% do faturamento de {fatComp.anoAnterior} já atingido</h6>
                            {(() => {
                              const pct = fatComp.totalAnterior > 0 ? (fatComp.totalAtual / fatComp.totalAnterior) * 100 : null;
                              return (
                                <>
                                  <h4 className={`mb-1 ${pct !== null && pct >= 100 ? 'text-success' : ''}`} style={pct !== null && pct >= 100 ? {} : { color: '#00205b' }}>
                                    {pct === null ? '—' : `${pct.toFixed(1).replace('.', ',')}%`}
                                  </h4>
                                  {pct !== null && (
                                    <div className="mx-auto" style={{ maxWidth: 180, height: 7, borderRadius: 999, backgroundColor: '#e8edf6', overflow: 'hidden' }}>
                                      <div style={{ width: `${Math.min(pct, 100)}%`, height: '100%', borderRadius: 999, backgroundColor: pct >= 100 ? '#1fa971' : '#00205b', transition: 'width .4s' }}></div>
                                    </div>
                                  )}
                                </>
                              );
                            })()}
                          </div>
                        </div>

                        {/* Acumulado até a mesma data (YTD) */}
                        <div className="rounded mb-4 py-3" style={{ backgroundColor: '#f6f8fc', border: '1px solid #e8edf6' }}>
                          <div className="row">
                            <div className="col-md-4 text-center border-end">
                              <h6 className="text-muted small mb-1">Faturado até {fatComp.ytdCorte}/{fatComp.anoAnterior}</h6>
                              <h5 className="mb-0" style={{ color: '#7d8aa3' }}>{formatarValor(fatComp.ytdAnterior)}</h5>
                            </div>
                            <div className="col-md-4 text-center border-end">
                              <h6 className="text-muted small mb-1">Faturado até {fatComp.ytdCorte}/{fatComp.anoAtual}</h6>
                              <h5 className="mb-0" style={{ color: '#00205b' }}>{formatarValor(fatComp.ytdAtual)}</h5>
                            </div>
                            <div className="col-md-4 text-center">
                              <h6 className="text-muted small mb-1">Variação no período</h6>
                              <h5 className={`mb-0 ${(fatComp.ytdVariacaoPct ?? 0) >= 0 ? 'text-success' : 'text-danger'}`}>
                                {fatComp.ytdVariacaoPct === null || fatComp.ytdVariacaoPct === undefined
                                  ? '—'
                                  : `${fatComp.ytdVariacaoPct >= 0 ? '▲ +' : '▼ '}${String(fatComp.ytdVariacaoPct).replace('.', ',')}%`}
                              </h5>
                            </div>
                          </div>
                        </div>
                        {(() => {
                          const tooltipFatComp = ({ active, payload, label }) => {
                            if (!active || !payload || !payload.length) return null;
                            const row = payload[0].payload || {};
                            const ant = row.anterior || 0; const atu = row.atual || 0;
                            const varMes = ant > 0 ? ((atu - ant) / ant) * 100 : null;
                            const linha = (cor, nome, val) => (
                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 18, padding: '3px 0' }}>
                                <span style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#dde6f6', fontSize: 13 }}>
                                  <span style={{ width: 9, height: 9, borderRadius: '50%', background: cor, display: 'inline-block' }}></span>{nome}
                                </span>
                                <b style={{ color: '#fff', fontSize: 13, fontVariantNumeric: 'tabular-nums' }}>{formatarValor(val)}</b>
                              </div>
                            );
                            return (
                              <div style={{ background: '#0e1726', borderRadius: 12, padding: '12px 14px', minWidth: 230, border: '1px solid rgba(146,162,196,0.18)' }}>
                                <div style={{ color: '#9bacca', fontSize: 12, fontWeight: 600, marginBottom: 8, textTransform: 'uppercase' }}>{label}</div>
                                {linha('#00b5cc', String(fatComp.anoAnterior || ''), ant)}
                                {linha('#6b9bd8', String(fatComp.anoAtual || ''), atu)}
                                <div style={{ borderTop: '1px solid rgba(146,162,196,0.22)', marginTop: 8, paddingTop: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                  <span style={{ color: '#9bacca', fontSize: 12.5 }}>Variação</span>
                                  <b style={{ color: varMes === null ? '#9bacca' : varMes >= 0 ? '#1fa971' : '#e5484d', fontSize: 13 }}>
                                    {varMes === null ? '—' : `${varMes >= 0 ? '+' : ''}${varMes.toFixed(1).replace('.', ',')}%`}
                                  </b>
                                </div>
                              </div>
                            );
                          };
                          const eixos = (
                            <>
                              <CartesianGrid strokeDasharray="3 3" stroke="#eef1f6" vertical={false} />
                              <XAxis dataKey="nome" tick={{ fontSize: 12, fill: '#7d8aa3' }} />
                              <YAxis domain={[0, 'auto']} tickFormatter={(v) => formatarValor(v)} tick={{ fontSize: 11, fill: '#7d8aa3' }} />
                              <Legend
                                iconSize={14}
                                wrapperStyle={{ paddingTop: 10 }}
                                formatter={(v) => <span style={{ fontSize: 15, fontWeight: 600, color: '#475467', margin: '0 12px 0 4px' }}>{v}</span>}
                              />
                            </>
                          );
                          return (
                            <ResponsiveContainer width="100%" height={380}>
                              {fatCompTipo === 'linhas' ? (
                                <ComposedChart data={fatComp.meses} margin={{ top: 20, right: 16, left: 8, bottom: 4 }}>
                                  <defs>
                                    <linearGradient id="fatCompGradAnterior" x1="0" y1="0" x2="0" y2="1">
                                      <stop offset="0%" stopColor="#00b5cc" stopOpacity={0.28} />
                                      <stop offset="70%" stopColor="#00b5cc" stopOpacity={0.06} />
                                      <stop offset="100%" stopColor="#00b5cc" stopOpacity={0} />
                                    </linearGradient>
                                    <linearGradient id="fatCompGradAtual" x1="0" y1="0" x2="0" y2="1">
                                      <stop offset="0%" stopColor="#00205b" stopOpacity={0.30} />
                                      <stop offset="70%" stopColor="#00205b" stopOpacity={0.07} />
                                      <stop offset="100%" stopColor="#00205b" stopOpacity={0} />
                                    </linearGradient>
                                  </defs>
                                  {eixos}
                                  <Tooltip content={tooltipFatComp} cursor={{ stroke: '#c6d2e8', strokeDasharray: '4 4' }} />
                                  <Area type="monotone" dataKey="anterior" name={String(fatComp.anoAnterior || 'Ano anterior')}
                                    stroke="#00b5cc" strokeWidth={3.5} fill="url(#fatCompGradAnterior)"
                                    style={{ filter: 'drop-shadow(0 6px 10px rgba(0,181,204,0.35))' }}
                                    dot={{ r: 4.5, fill: '#00b5cc', stroke: '#fff', strokeWidth: 2 }}
                                    activeDot={{ r: 7, fill: '#00b5cc', stroke: '#fff', strokeWidth: 2.5 }} />
                                  <Area type="monotone" dataKey="atual" name={String(fatComp.anoAtual || 'Ano atual')}
                                    stroke="#00205b" strokeWidth={3.5} fill="url(#fatCompGradAtual)"
                                    style={{ filter: 'drop-shadow(0 6px 10px rgba(0,32,91,0.35))' }}
                                    dot={{ r: 4.5, fill: '#00205b', stroke: '#fff', strokeWidth: 2 }}
                                    activeDot={{ r: 7, fill: '#00205b', stroke: '#fff', strokeWidth: 2.5 }} />
                                </ComposedChart>
                              ) : (
                                <BarChart data={fatComp.meses} margin={{ top: 20, right: 16, left: 8, bottom: 4 }} barGap={4} barCategoryGap="26%">
                                  {eixos}
                                  <Tooltip content={tooltipFatComp} cursor={{ fill: 'rgba(0,32,91,0.06)' }} />
                                  <Bar dataKey="anterior" name={String(fatComp.anoAnterior || 'Ano anterior')} fill="#00b5cc" radius={[6, 6, 0, 0]} maxBarSize={34}
                                    style={{ cursor: 'pointer' }}
                                    onClick={(d) => abrirLancComparativo('faturamento', fatCompBase, fatComp.anoAnterior, d && d.payload ? d.payload : d)} />
                                  <Bar dataKey="atual" name={String(fatComp.anoAtual || 'Ano atual')} fill="#00205b" radius={[6, 6, 0, 0]} maxBarSize={34}
                                    style={{ cursor: 'pointer' }}
                                    onClick={(d) => abrirLancComparativo('faturamento', fatCompBase, fatComp.anoAtual, d && d.payload ? d.payload : d)} />
                                </BarChart>
                              )}
                            </ResponsiveContainer>
                          );
                        })()}
                        <div className="text-center mt-2">
                          <small className="text-muted">
                            <i className="fas fa-info-circle me-1"></i>
                            {fatCompBase === 'competencia'
                              ? 'Base: competência — valores agrupados pela data de registro/emissão do título.'
                              : 'Base: pagamento — valores agrupados pela data em que foram efetivamente recebidos.'}
                          </small>
                        </div>
                      </>
                    ) : (
                      <div className="text-center py-5 text-muted">
                        <i className="fas fa-chart-column fa-3x mb-3" style={{ color: '#cbd5e1' }}></i>
                        <p>Sem faturamento registrado nos dois anos para a base selecionada.</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* ========================================
                DESPESAS COMPARATIVAS (ANO ATUAL x ANTERIOR)
            ======================================== */}
            <div className="row mb-4">
              <div className="col-12">
                <div className="card border-0 shadow-sm">
                  <div className="card-header bg-white">
                    <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                      <h5 className="card-title mb-0">
                        <i className="fas fa-file-invoice-dollar me-2" style={{ color: '#00205b' }}></i>
                        Despesas — Comparativo Anual {despComp.anoAnterior && despComp.anoAtual ? `(${despComp.anoAnterior} x ${despComp.anoAtual})` : ''}
                      </h5>
                      <div className="d-flex align-items-center gap-2 flex-wrap">
                        <small className="text-muted me-1">Base:</small>
                        {[
                          { key: 'competencia', rotulo: 'Competência', hint: 'Pela data de competência/vencimento do título' },
                          { key: 'pagamento', rotulo: 'Pagamento', hint: 'Pela data em que o valor foi efetivamente pago' },
                        ].map((b) => (
                          <button
                            key={b.key}
                            type="button"
                            className="btn btn-sm"
                            title={b.hint}
                            style={{
                              borderRadius: '999px',
                              padding: '3px 14px',
                              fontWeight: 600,
                              fontSize: '0.78rem',
                              border: '1px solid ' + (despCompBase === b.key ? '#00205b' : '#d4e2fb'),
                              backgroundColor: despCompBase === b.key ? '#00205b' : '#fff',
                              color: despCompBase === b.key ? '#fff' : '#00205b',
                            }}
                            onClick={() => setDespCompBase(b.key)}
                          >
                            {b.rotulo}
                          </button>
                        ))}
                        <span className="mx-1" style={{ borderLeft: '1px solid #e2e8f4', height: 22 }}></span>
                        {[
                          { key: 'barras', icone: 'fa-chart-column', rotulo: 'Barras' },
                          { key: 'linhas', icone: 'fa-chart-line', rotulo: 'Linhas' },
                        ].map((t) => (
                          <button
                            key={t.key}
                            type="button"
                            className="btn btn-sm"
                            title={`Ver em ${t.rotulo.toLowerCase()}`}
                            style={{
                              borderRadius: '999px',
                              padding: '3px 14px',
                              fontWeight: 600,
                              fontSize: '0.78rem',
                              border: '1px solid ' + (despCompTipo === t.key ? '#00205b' : '#d4e2fb'),
                              backgroundColor: despCompTipo === t.key ? '#00205b' : '#fff',
                              color: despCompTipo === t.key ? '#fff' : '#00205b',
                            }}
                            onClick={() => setDespCompTipo(t.key)}
                          >
                            <i className={`fas ${t.icone} me-1`}></i>{t.rotulo}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div className="card-body">
                    {carregandoDespComp ? (
                      <div className="text-center py-5">
                        <div className="spinner-border" style={{ color: '#00205b' }}></div>
                        <p className="mt-3 text-muted">Carregando despesas comparativas...</p>
                      </div>
                    ) : despComp.ok ? (
                      <>
                        <div className="row mb-3">
                          <div className="col-md-4 text-center border-end">
                            <h6 className="text-muted small mb-1">Total {despComp.anoAnterior}</h6>
                            <h4 className="mb-0" style={{ color: '#7d8aa3' }}>{formatarValor(despComp.totalAnterior)}</h4>
                          </div>
                          <div className="col-md-4 text-center border-end">
                            <h6 className="text-muted small mb-1">Total {despComp.anoAtual}</h6>
                            <h4 className="mb-0" style={{ color: '#00205b' }}>{formatarValor(despComp.totalAtual)}</h4>
                          </div>
                          <div className="col-md-4 text-center">
                            <h6 className="text-muted small mb-1">% das despesas de {despComp.anoAnterior} já gasto</h6>
                            {(() => {
                              const pct = despComp.totalAnterior > 0 ? (despComp.totalAtual / despComp.totalAnterior) * 100 : null;
                              return (
                                <>
                                  <h4 className={`mb-1 ${pct !== null && pct >= 100 ? 'text-danger' : ''}`} style={pct !== null && pct >= 100 ? {} : { color: '#00205b' }}>
                                    {pct === null ? '—' : `${pct.toFixed(1).replace('.', ',')}%`}
                                  </h4>
                                  {pct !== null && (
                                    <div className="mx-auto" style={{ maxWidth: 180, height: 7, borderRadius: 999, backgroundColor: '#e8edf6', overflow: 'hidden' }}>
                                      <div style={{ width: `${Math.min(pct, 100)}%`, height: '100%', borderRadius: 999, backgroundColor: pct >= 100 ? '#e5484d' : '#00205b', transition: 'width .4s' }}></div>
                                    </div>
                                  )}
                                </>
                              );
                            })()}
                          </div>
                        </div>

                        {/* Acumulado até a mesma data (YTD) */}
                        <div className="rounded mb-4 py-3" style={{ backgroundColor: '#f6f8fc', border: '1px solid #e8edf6' }}>
                          <div className="row">
                            <div className="col-md-4 text-center border-end">
                              <h6 className="text-muted small mb-1">Gasto até {despComp.ytdCorte}/{despComp.anoAnterior}</h6>
                              <h5 className="mb-0" style={{ color: '#7d8aa3' }}>{formatarValor(despComp.ytdAnterior)}</h5>
                            </div>
                            <div className="col-md-4 text-center border-end">
                              <h6 className="text-muted small mb-1">Gasto até {despComp.ytdCorte}/{despComp.anoAtual}</h6>
                              <h5 className="mb-0" style={{ color: '#00205b' }}>{formatarValor(despComp.ytdAtual)}</h5>
                            </div>
                            <div className="col-md-4 text-center">
                              <h6 className="text-muted small mb-1">Variação no período</h6>
                              <h5 className={`mb-0 ${(despComp.ytdVariacaoPct ?? 0) >= 0 ? 'text-danger' : 'text-success'}`}>
                                {despComp.ytdVariacaoPct === null || despComp.ytdVariacaoPct === undefined
                                  ? '—'
                                  : `${despComp.ytdVariacaoPct >= 0 ? '▲ +' : '▼ '}${String(despComp.ytdVariacaoPct).replace('.', ',')}%`}
                              </h5>
                            </div>
                          </div>
                        </div>

                        {(() => {
                          const tooltipDespComp = ({ active, payload, label }) => {
                            if (!active || !payload || !payload.length) return null;
                            const row = payload[0].payload || {};
                            const ant = row.anterior || 0; const atu = row.atual || 0;
                            const varMes = ant > 0 ? ((atu - ant) / ant) * 100 : null;
                            const linha = (cor, nome, val) => (
                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 18, padding: '3px 0' }}>
                                <span style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#dde6f6', fontSize: 13 }}>
                                  <span style={{ width: 9, height: 9, borderRadius: '50%', background: cor, display: 'inline-block' }}></span>{nome}
                                </span>
                                <b style={{ color: '#fff', fontSize: 13, fontVariantNumeric: 'tabular-nums' }}>{formatarValor(val)}</b>
                              </div>
                            );
                            return (
                              <div style={{ background: '#0e1726', borderRadius: 12, padding: '12px 14px', minWidth: 230, border: '1px solid rgba(146,162,196,0.18)' }}>
                                <div style={{ color: '#9bacca', fontSize: 12, fontWeight: 600, marginBottom: 8, textTransform: 'uppercase' }}>{label}</div>
                                {linha('#00b5cc', String(despComp.anoAnterior || ''), ant)}
                                {linha('#6b9bd8', String(despComp.anoAtual || ''), atu)}
                                <div style={{ borderTop: '1px solid rgba(146,162,196,0.22)', marginTop: 8, paddingTop: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                  <span style={{ color: '#9bacca', fontSize: 12.5 }}>Variação</span>
                                  <b style={{ color: varMes === null ? '#9bacca' : varMes >= 0 ? '#e5484d' : '#1fa971', fontSize: 13 }}>
                                    {varMes === null ? '—' : `${varMes >= 0 ? '+' : ''}${varMes.toFixed(1).replace('.', ',')}%`}
                                  </b>
                                </div>
                              </div>
                            );
                          };
                          const eixos = (
                            <>
                              <CartesianGrid strokeDasharray="3 3" stroke="#eef1f6" vertical={false} />
                              <XAxis dataKey="nome" tick={{ fontSize: 12, fill: '#7d8aa3' }} />
                              <YAxis domain={[0, 'auto']} tickFormatter={(v) => formatarValor(v)} tick={{ fontSize: 11, fill: '#7d8aa3' }} />
                              <Legend
                                iconSize={14}
                                wrapperStyle={{ paddingTop: 10 }}
                                formatter={(v) => <span style={{ fontSize: 15, fontWeight: 600, color: '#475467', margin: '0 12px 0 4px' }}>{v}</span>}
                              />
                            </>
                          );
                          return (
                            <ResponsiveContainer width="100%" height={380}>
                              {despCompTipo === 'linhas' ? (
                                <ComposedChart data={despComp.meses} margin={{ top: 20, right: 16, left: 8, bottom: 4 }}>
                                  <defs>
                                    <linearGradient id="despCompGradAnterior" x1="0" y1="0" x2="0" y2="1">
                                      <stop offset="0%" stopColor="#00b5cc" stopOpacity={0.28} />
                                      <stop offset="70%" stopColor="#00b5cc" stopOpacity={0.06} />
                                      <stop offset="100%" stopColor="#00b5cc" stopOpacity={0} />
                                    </linearGradient>
                                    <linearGradient id="despCompGradAtual" x1="0" y1="0" x2="0" y2="1">
                                      <stop offset="0%" stopColor="#00205b" stopOpacity={0.30} />
                                      <stop offset="70%" stopColor="#00205b" stopOpacity={0.07} />
                                      <stop offset="100%" stopColor="#00205b" stopOpacity={0} />
                                    </linearGradient>
                                  </defs>
                                  {eixos}
                                  <Tooltip content={tooltipDespComp} cursor={{ stroke: '#c6d2e8', strokeDasharray: '4 4' }} />
                                  <Area type="monotone" dataKey="anterior" name={String(despComp.anoAnterior || 'Ano anterior')}
                                    stroke="#00b5cc" strokeWidth={3.5} fill="url(#despCompGradAnterior)"
                                    style={{ filter: 'drop-shadow(0 6px 10px rgba(0,181,204,0.35))' }}
                                    dot={{ r: 4.5, fill: '#00b5cc', stroke: '#fff', strokeWidth: 2 }}
                                    activeDot={{ r: 7, fill: '#00b5cc', stroke: '#fff', strokeWidth: 2.5 }} />
                                  <Area type="monotone" dataKey="atual" name={String(despComp.anoAtual || 'Ano atual')}
                                    stroke="#00205b" strokeWidth={3.5} fill="url(#despCompGradAtual)"
                                    style={{ filter: 'drop-shadow(0 6px 10px rgba(0,32,91,0.35))' }}
                                    dot={{ r: 4.5, fill: '#00205b', stroke: '#fff', strokeWidth: 2 }}
                                    activeDot={{ r: 7, fill: '#00205b', stroke: '#fff', strokeWidth: 2.5 }} />
                                </ComposedChart>
                              ) : (
                                <BarChart data={despComp.meses} margin={{ top: 20, right: 16, left: 8, bottom: 4 }} barGap={4} barCategoryGap="26%">
                                  {eixos}
                                  <Tooltip content={tooltipDespComp} cursor={{ fill: 'rgba(0,32,91,0.06)' }} />
                                  <Bar dataKey="anterior" name={String(despComp.anoAnterior || 'Ano anterior')} fill="#00b5cc" radius={[6, 6, 0, 0]} maxBarSize={34}
                                    style={{ cursor: 'pointer' }}
                                    onClick={(d) => abrirLancComparativo('despesas', despCompBase, despComp.anoAnterior, d && d.payload ? d.payload : d)} />
                                  <Bar dataKey="atual" name={String(despComp.anoAtual || 'Ano atual')} fill="#00205b" radius={[6, 6, 0, 0]} maxBarSize={34}
                                    style={{ cursor: 'pointer' }}
                                    onClick={(d) => abrirLancComparativo('despesas', despCompBase, despComp.anoAtual, d && d.payload ? d.payload : d)} />
                                </BarChart>
                              )}
                            </ResponsiveContainer>
                          );
                        })()}
                        <div className="text-center mt-2">
                          <small className="text-muted">
                            <i className="fas fa-info-circle me-1"></i>
                            {despCompBase === 'competencia'
                              ? 'Base: competência — despesas agrupadas pela data de competência/vencimento do título.'
                              : 'Base: pagamento — despesas agrupadas pela data em que foram efetivamente pagas.'}
                          </small>
                        </div>
                      </>
                    ) : (
                      <div className="text-center py-5 text-muted">
                        <i className="fas fa-file-invoice-dollar fa-3x mb-3" style={{ color: '#cbd5e1' }}></i>
                        <p>Sem despesas registradas nos dois anos para a base selecionada.</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* ========================================
                DRE - DEMONSTRAÇÃO DO RESULTADO
            ======================================== */}
            <div className="row mb-4">
              <div className="col-12">
                <div className="card border-0 shadow-sm">
                  {/* Card Header com Filtro Moderno */}
                  <div className="card-header bg-white">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h5 className="card-title mb-0">
                        <i className="fas fa-calculator me-2"></i>
                        Receitas & Despesas Realizadas
                      </h5>
                      <div>
                        {dadosDRE && (
                          <div>
                            <span className="badge bg-light text-dark me-2">
                              <i className="fas fa-university me-1"></i>
                              Sistema: {dadosCliente.sistema?.toUpperCase() || 'N/A'}
                            </span>
                            <span className="badge bg-success">
                              <i className="fas fa-chart-line me-1"></i>
                              Margem: {dadosDRE.resumo_dre.margem_liquida.toFixed(1)}%
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {/* FILTRO MODERNO */}
                    <div 
                      className="p-3 rounded" 
                      style={{
                        backgroundColor: 'rgba(216, 214, 214, 0.1)',
                        border: '1px solid rgba(255, 255, 255, 0.2)'
                      }}
                    >
                      <div className="row g-3 align-items-end">
                        {/* Botões Rápidos */}
                        <div className="col-md-4">
                          <label className="form-label text-dark small mb-2 fw-semibold">
                            <i className="fas fa-clock me-2"></i>
                            Períodos Rápidos
                          </label>
                          <div className="d-grid gap-2">
                            <div className="btn-group" role="group">
                              <button 
                                type="button" 
                                className="btn btn-sm"
                                style={{
                                  backgroundColor: 'rgba(255, 255, 255, 0.8)',
                                  border: '1px solid rgba(0, 0, 0, 0.2)',
                                  color: 'black',
                                  transition: 'all 0.2s'
                                }}
                                onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.25)'}
                                onMouseLeave={(e) => e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.15)'}
                                onClick={() => {
                                  const hoje = new Date();
                                  setDataInicioDRE(new Date(hoje.getFullYear(), hoje.getMonth(), 1).toISOString().split('T')[0]);
                                  setDataFimDRE(new Date(hoje.getFullYear(), hoje.getMonth() + 1, 0).toISOString().split('T')[0]);
                                }}
                              >
                                <i className="fas fa-calendar-day me-1"></i>
                                Mês Atual
                              </button>
                              
                              <button 
                                type="button" 
                                className="btn btn-sm"
                                style={{
                                  backgroundColor: 'rgba(255, 255, 255, 0.8)',
                                  border: '1px solid rgba(0, 0, 0, 0.2)',
                                  color: 'black',
                                  transition: 'all 0.2s'
                                }}
                                onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.25)'}
                                onMouseLeave={(e) => e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.15)'}
                                onClick={() => {
                                  const hoje = new Date();
                                  setDataInicioDRE(new Date(hoje.getFullYear(), hoje.getMonth() - 1, 1).toISOString().split('T')[0]);
                                  setDataFimDRE(new Date(hoje.getFullYear(), hoje.getMonth(), 0).toISOString().split('T')[0]);
                                }}
                              >
                                <i className="fas fa-arrow-left me-1"></i>
                                Anterior
                              </button>
                              
                              <button 
                                type="button" 
                                className="btn btn-sm"
                                style={{
                                  backgroundColor: 'rgba(255, 255, 255, 0.8)',
                                  border: '1px solid rgba(0, 0, 0, 0.2)',
                                  color: 'black',
                                  transition: 'all 0.2s'
                                }}
                                onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.25)'}
                                onMouseLeave={(e) => e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.15)'}
                                onClick={() => {
                                  const hoje = new Date();
                                  setDataInicioDRE(new Date(hoje.getFullYear(), hoje.getMonth() - 2, 1).toISOString().split('T')[0]);
                                  setDataFimDRE(new Date(hoje.getFullYear(), hoje.getMonth() + 1, 0).toISOString().split('T')[0]);
                                }}
                              >
                                <i className="fas fa-calendar-alt me-1"></i>
                                3 Meses
                              </button>
                            </div>
                          </div>
                        </div>
                        
                        {/* Data Início */}
                        <div className="col-md-3">
                          <label className="form-label text-dark small mb-2 fw-semibold">
                            <i className="fas fa-calendar-alt me-2"></i>
                            Data Início
                          </label>
                          <input 
                            type="date" 
                            className="form-control shadow-sm"
                            style={{
                              backgroundColor: 'white',
                              border: '2px solid rgba(0, 0, 0, 0.2)',
                              fontWeight: '500',
                              color: 'black'
                            }}
                            value={dataInicioDRE}
                            onChange={(e) => setDataInicioDRE(e.target.value)}
                          />
                        </div>
                        
                        {/* Data Fim */}
                        <div className="col-md-3">
                          <label className="form-label text-dark small mb-2 fw-semibold">
                            <i className="fas fa-calendar-check me-2"></i>
                            Data Fim
                          </label>
                          <input 
                            type="date" 
                            className="form-control shadow-sm"
                            style={{
                              backgroundColor: 'white',
                              border: '2px solid rgba(0, 0, 0, 0.2)',
                              fontWeight: '500',
                              color: 'black'
                            }}
                            value={dataFimDRE}
                            onChange={(e) => setDataFimDRE(e.target.value)}
                          />
                        </div>
                        
                        {/* Botões de Ação */}
                        <div className="col-md-2">
                          <div className="d-grid gap-2">
                            <button 
                              className="btn shadow-sm"
                              style={{
                                fontWeight: '600',
                                border: '2px solid rgba(0, 0, 0, 0.2)',
                                backgroundColor: 'white',
                                color: 'black'
                              }}
                              onClick={aplicarFiltroDRE}
                              disabled={!dataInicioDRE || !dataFimDRE}
                            >
                              <i className="fas fa-search me-2"></i>
                              Filtrar
                            </button>
                            
                            <button 
                              className="btn shadow-sm"
                              style={{
                                fontWeight: '500',
                                border: '2px solid rgba(0, 0, 0, 0.2)',
                                backgroundColor: 'white',
                                color: 'black'
                              }}
                              onClick={limparFiltroDRE}
                            >
                              <i className="fas fa-redo me-2"></i>
                              Resetar
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* Filtro por categoria */}
                      <div className="row g-3 mt-1 align-items-end">
                        <div className="col-md-8">
                          <label className="form-label text-dark small mb-2 fw-semibold">
                            <i className="fas fa-filter me-2"></i>
                            Categoria
                          </label>
                          <select
                            className="form-select shadow-sm"
                            style={{ backgroundColor: 'white', border: '2px solid rgba(0, 0, 0, 0.2)', fontWeight: '500', color: 'black' }}
                            value={categoriaFiltro}
                            onChange={(e) => setCategoriaFiltro(e.target.value)}
                          >
                            <option value="">Todas as categorias</option>
                            <optgroup label="Despesas">
                              {catsOptDespesas.slice().sort((a, b) => b.valor - a.valor).map((c) => (
                                <option key={`fd-${c.categoria_completa}`} value={c.categoria_completa}>
                                  {limparCategoria(c.categoria_completa || c.categoria)}
                                </option>
                              ))}
                            </optgroup>
                            <optgroup label="Receitas">
                              {catsOptReceitas.slice().sort((a, b) => b.valor - a.valor).map((c) => (
                                <option key={`fr-${c.categoria_completa}`} value={c.categoria_completa}>
                                  {limparCategoria(c.categoria_completa || c.categoria)}
                                </option>
                              ))}
                            </optgroup>
                          </select>
                        </div>
                        {categoriaFiltro && (
                          <div className="col-md-4">
                            <button
                              className="btn shadow-sm w-100"
                              style={{ fontWeight: '500', border: '2px solid rgba(0, 0, 0, 0.2)', backgroundColor: 'white', color: 'black' }}
                              onClick={() => setCategoriaFiltro('')}
                            >
                              <i className="fas fa-times me-2"></i>
                              Limpar categoria
                            </button>
                          </div>
                        )}
                      </div>

                      {/* Badge Informativo */}
                      {dataInicioDRE && dataFimDRE && (
                        <div className="text-center mt-3">
                          <span 
                            className="badge" 
                            style={{
                              backgroundColor: 'rgba(255, 255, 255, 0.8)',
                              color: 'black',
                              fontSize: '0.85rem',
                              padding: '0.5rem 1rem',
                              border: '1px solid rgba(0, 0, 0, 0.2)'
                            }}
                          >
                            <i className="fas fa-info-circle me-2"></i>
                            Período: {new Date(dataInicioDRE + 'T00:00:00').toLocaleDateString('pt-BR')} 
                            {' até '}
                            {new Date(dataFimDRE + 'T00:00:00').toLocaleDateString('pt-BR')}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="card-body">
                    {carregandoDRE ? (
                      <div className="text-center py-5">
                        <div className="spinner-border" style={{color: '#ff6b35'}}></div>
                        <p className="mt-3 text-muted">Processando DRE...</p>
                      </div>
                    ) : dadosDRE ? (
                      <>
                        {/* Resumo DRE */}
                        <div className="row mb-4">
                          <div className="col-md-3 text-center border-end">
                            <h6 className="text-muted small mb-1">Total Receitas</h6>
                            <h5 className="text-success mb-0">
                              <i className="fas fa-plus-circle me-1"></i>
                              {formatarValor(dadosDRE.resumo_dre.total_receitas)}
                            </h5>
                            <small className="text-muted">{dadosDRE.resumo_dre.total_categorias_receita} categorias</small>
                          </div>
                          <div className="col-md-3 text-center border-end">
                            <h6 className="text-muted small mb-1">Total Despesas</h6>
                            <h5 className="text-danger mb-0">
                              <i className="fas fa-minus-circle me-1"></i>
                              {formatarValor(dadosDRE.resumo_dre.total_despesas)}
                            </h5>
                            <small className="text-muted">{dadosDRE.resumo_dre.total_categorias_despesa} categorias</small>
                          </div>
                          <div className="col-md-3 text-center border-end">
                            <h6 className="text-muted small mb-1">Resultado Líquido</h6>
                            <h5 className={`mb-0 ${dadosDRE.resumo_dre.resultado_liquido >= 0 ? 'text-success' : 'text-danger'}`}>
                              <i className={`fas ${dadosDRE.resumo_dre.resultado_liquido >= 0 ? 'fa-arrow-up' : 'fa-arrow-down'} me-1`}></i>
                              {formatarValor(dadosDRE.resumo_dre.resultado_liquido)}
                            </h5>
                            <small className={dadosDRE.resumo_dre.resultado_liquido >= 0 ? 'text-success' : 'text-danger'}>
                              {dadosDRE.resumo_dre.resultado_liquido >= 0 ? 'Lucro' : 'Prejuízo'}
                            </small>
                          </div>
                          <div className="col-md-3 text-center">
                            <h6 className="text-muted small mb-1">Margem Líquida</h6>
                            <h5 className={`mb-0 ${dadosDRE.resumo_dre.margem_liquida >= 0 ? 'text-success' : 'text-danger'}`}>
                              <i className="fas fa-percentage me-1"></i>
                              {dadosDRE.resumo_dre.margem_liquida.toFixed(1)}%
                            </h5>
                            <small className="text-muted">Eficiência operacional</small>
                          </div>
                        </div>

                        {/* Gráficos DRE */}
                        <div className="row mb-4">
                          {/* Gráfico de Despesas */}
                          <div className="col-lg-6">
                            <h6 className="text-muted mb-3">
                              <i className="fas fa-chart-bar me-2"></i>
                              Top 5 Despesas
                              <span 
                                className="badge ms-2"
                                style={{
                                  backgroundColor: '#f32b2b',
                                  color: 'white',
                                  animation: 'pulse 2s infinite',
                                  cursor: 'pointer'
                                }}
                              >
                                <i className="fas fa-hand-pointer me-1"></i>
                                Clique nas Barras
                              </span>
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-secondary ms-2"
                                style={{ fontSize: '0.72rem', padding: '2px 9px', borderRadius: '999px' }}
                                onClick={() => abrirRelatorio('despesas')}
                              >
                                <i className="fas fa-list me-1"></i>Ver todo o relatório
                              </button>
                            </h6>
                            
                            {despesasGraficoView.length > 0 ? (
                              <ResponsiveContainer width="100%" height={Math.max(220, linhasView * 56)}>
                                <BarChart
                                  data={despesasGraficoView}
                                  layout="vertical"
                                  margin={{ top: 6, right: 96, left: 8, bottom: 6 }}
                                  barCategoryGap="20%"
                                >
                                  <CartesianGrid strokeDasharray="3 3" stroke="#eef1f6" horizontal={false} />
                                  <XAxis type="number" tickFormatter={(value) => formatarValor(value)} tick={{ fontSize: 10, fill: '#7d8aa3' }} />
                                  <YAxis type="category" dataKey="categoria" hide />
                                  <Tooltip
                                    contentStyle={{ backgroundColor: '#002058', border: '1px solid #002058', borderRadius: '8px', color: '#ffffff' }}
                                    labelStyle={{ color: '#ffffff', fontWeight: 'bold' }}
                                    itemStyle={{ color: '#ffffff' }}
                                    formatter={(value, name, props) => [formatarValor(value), `${props.payload.quantidade} movimento(s)`]}
                                    labelFormatter={(label) => `Categoria: ${limparCategoria(label)}`}
                                    cursor={{ fill: 'rgba(229,72,77,0.06)' }}
                                  />
                                  <Bar
                                    dataKey="valor"
                                    radius={[0, 10, 10, 0]}
                                    minPointSize={6}
                                    className="fmp-bar-shadow"
                                    label={criarRotuloDRE(despesasGraficoView)}
                                    onClick={(data) => {
                                      if (dadosDRE?.detalhes_por_categoria?.[data.categoria_completa]) {
                                        const totalDespesas = dadosDRE?.resumo_dre?.total_despesas || 1;
                                        setCategoriaSelecionada({
                                          categoria: data.categoria_completa,
                                          total: data.valor,
                                          quantidade: data.quantidade,
                                          percentual: ((data.valor / totalDespesas) * 100).toFixed(1)
                                        });
                                        setModalCategoria(true);
                                      }
                                    }}
                                    style={{ cursor: 'pointer' }}
                                  >
                                    {despesasGraficoView.map((entry, index) => (
                                      <Cell key={`cell-d-${index}`} fill={entry._placeholder ? 'transparent' : `hsl(2, 70%, ${44 + index * 4}%)`} />
                                    ))}
                                  </Bar>
                                </BarChart>
                              </ResponsiveContainer>
                            ) : (
                              <div className="text-center py-5 text-muted">
                                <i className="fas fa-inbox fa-3x mb-3" style={{color: '#e5484d'}}></i>
                                <p>Nenhuma despesa categorizada no período</p>
                              </div>
                            )}
                          </div>

                        {/* Gráfico de Receitas por Categoria */}
<div className="col-lg-6">
  <h6 className="text-muted mb-3">
    <i className="fas fa-chart-bar me-2"></i>
    Top 5 Receitas
    <span 
      className="badge ms-2"
      style={{
        backgroundColor: '#4caf50',
        color: 'white',
        animation: 'pulse 2s infinite',
        cursor: 'pointer'
      }}
    >
      <i className="fas fa-hand-pointer me-1"></i>
      Clique nas Barras
    </span>
    <button
      type="button"
      className="btn btn-sm btn-outline-secondary ms-2"
      style={{ fontSize: '0.72rem', padding: '2px 9px', borderRadius: '999px' }}
      onClick={() => abrirRelatorio('receitas')}
    >
      <i className="fas fa-list me-1"></i>Ver todo o relatório
    </button>
  </h6>
  
  {receitasGraficoView.length > 0 ? (
    <ResponsiveContainer width="100%" height={Math.max(220, linhasView * 56)}>
      <BarChart
        data={receitasGraficoView}
        layout="vertical"
        margin={{ top: 6, right: 96, left: 8, bottom: 6 }}
        barCategoryGap="20%"
      >
        <CartesianGrid strokeDasharray="3 3" stroke="#eef1f6" horizontal={false} />
        <XAxis type="number" tickFormatter={(value) => formatarValor(value)} tick={{ fontSize: 10, fill: '#7d8aa3' }} />
        <YAxis type="category" dataKey="categoria" hide />
        <Tooltip
          contentStyle={{ backgroundColor: '#002058', border: '1px solid #002058', borderRadius: '8px', color: '#ffffff' }}
          labelStyle={{ color: '#ffffff', fontWeight: 'bold' }}
          itemStyle={{ color: '#ffffff' }}
          formatter={(value, name, props) => [formatarValor(value), `${props.payload.quantidade} movimento(s)`]}
          labelFormatter={(label) => `Categoria: ${limparCategoria(label)}`}
          cursor={{ fill: 'rgba(31,169,113,0.06)' }}
        />
        <Bar
          dataKey="valor"
          radius={[0, 10, 10, 0]}
          minPointSize={6}
          className="fmp-bar-shadow"
          label={criarRotuloDRE(receitasGraficoView)}
          onClick={(data) => {
            if (data && data.categoria_completa && dadosDRE?.detalhes_por_categoria_receita?.[data.categoria_completa]) {
              setCategoriaSelecionada({
                categoria: data.categoria_completa,
                total: data.valor,
                quantidade: data.quantidade,
                percentual: ((data.valor / dadosDRE.resumo_dre.total_receitas) * 100).toFixed(1)
              });
              setModalCategoria(true);
            }
          }}
          style={{ cursor: 'pointer' }}
        >
          {receitasGraficoView.map((entry, index) => (
            <Cell key={`cell-r-${index}`} fill={entry._placeholder ? 'transparent' : `hsl(152, 58%, ${34 + index * 4}%)`} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  ) : (
    <div className="text-center py-5 text-muted">
      <i className="fas fa-inbox fa-3x mb-3" style={{color: '#1fa971'}}></i>
      <p>Nenhuma receita categorizada no período</p>
    </div>
  )}
</div>
                        </div>

                        {/* Resultado (evolução) à esquerda + Donut de categorias à direita */}
                        <div className="row mb-4">
                          <div className="col-lg-6 mb-3">
                            <h6 className="text-muted mb-3">
                              <i className="fas fa-chart-line me-2"></i>
                              Evolução Mensal - Receitas vs Despesas
                            </h6>
                            {evolucaoView && evolucaoView.length > 0 ? (
                              <ResponsiveContainer width="100%" height={360}>
                                <ComposedChart data={evolucaoView} margin={{ top: 24, right: 16, left: 8, bottom: 4 }} barGap={10} barCategoryGap="12%">
                                  <CartesianGrid strokeDasharray="3 3" stroke="#eef1f6" vertical={false} />
                                  <XAxis dataKey="mes" tick={{ fontSize: 12, fill: '#7d8aa3' }} />
                                  <YAxis domain={[0, 'auto']} tickFormatter={(value) => formatarValor(value)} tick={{ fontSize: 11, fill: '#7d8aa3' }} />
                                  <Tooltip
                                    formatter={(value, name) => {
                                      const nomes = {
                                        'receitas': 'Receitas',
                                        'despesas': 'Despesas',
                                        'resultado': 'Resultado Líquido'
                                      };
                                      return [formatarValor(value), nomes[name] || name];
                                    }}
                                  />
                                  <Legend />

                                  <Bar dataKey="receitas" name="receitas" fill="#1fa971" radius={[6, 6, 0, 0]} maxBarSize={120}
                                    label={evolucaoView.length <= 4 ? { position: 'top', formatter: (v) => formatarValor(v), fontSize: 15, fill: '#1fa971', fontWeight: 700 } : false} />
                                  <Bar dataKey="despesas" name="despesas" fill="#e5484d" radius={[6, 6, 0, 0]} maxBarSize={120}
                                    label={evolucaoView.length <= 4 ? { position: 'top', formatter: (v) => formatarValor(v), fontSize: 15, fill: '#e5484d', fontWeight: 700 } : false} />
                                  <Line
                                    type="monotone"
                                    dataKey="resultado"
                                    name="resultado"
                                    stroke="#00205b"
                                    strokeWidth={3}
                                    dot={{ r: 5, fill: '#00205b', stroke: '#fff', strokeWidth: 2 }}
                                  />
                                </ComposedChart>
                              </ResponsiveContainer>
                            ) : (
                              <div className="text-center py-5 text-muted">
                                <i className="fas fa-chart-line fa-3x mb-3" style={{ color: '#cbd5e1' }}></i>
                                <p>Sem evolução mensal no período selecionado</p>
                              </div>
                            )}
                          </div>

                          <div className="col-lg-6 mb-3">
                            <h6 className="text-muted mb-3">
                              <i className="fas fa-chart-pie me-2"></i>
                              {catSel
                                ? `${limparCategoria(catSel.categoria_completa || catSel.categoria)} vs. demais`
                                : 'Composição das Despesas'}
                            </h6>
                            <DonutDespesas
                              dados={pizzaView}
                              total={totalTipoSel}
                              cores={CORES_PIZZA}
                            />
                          </div>
                        </div>

                     {/* Insight DRE */}
<div className="alert border-start border-4 mb-0 fmp-insight">
  <div className="row align-items-center">
    <div className="col-md-8">
      <h6 className="alert-heading">
        <i className="fas fa-lightbulb me-2"></i>
        Análise Inteligente - Despesas e Receitas
      </h6>
      <div style={{fontSize: '15px', lineHeight: 1.65, color: '#334c73'}}>
        <strong style={{color: '#00205b'}}>Performance Financeira:</strong>{' '}
        {dadosDRE.resumo_dre.margem_liquida > 20
          ? `A empresa apresenta uma excelente eficiência operacional, com margem líquida de ${dadosDRE.resumo_dre.margem_liquida.toFixed(1)}%. Esse resultado indica forte controle de custos e alta capacidade de geração de lucro. O cenário é favorável para expansão, reinvestimentos ou fortalecimento do caixa.`

          : dadosDRE.resumo_dre.margem_liquida > 10
          ? `A margem líquida de ${dadosDRE.resumo_dre.margem_liquida.toFixed(1)}% demonstra uma operação saudável e sustentável. Ainda assim, existem oportunidades para ganho de eficiência, especialmente na otimização de custos e aumento da rentabilidade.`

          : dadosDRE.resumo_dre.margem_liquida > 0
          ? `A empresa opera com margem líquida positiva de ${dadosDRE.resumo_dre.margem_liquida.toFixed(1)}%, porém em um nível mais apertado. Isso indica que, embora haja lucro, pequenas variações de custos ou receitas podem impactar significativamente o resultado. Recomenda-se atenção na gestão de despesas e precificação.`

          : `A margem líquida negativa de ${dadosDRE.resumo_dre.margem_liquida.toFixed(1)}% indica que a operação está gerando prejuízo no período analisado. Esse cenário exige ação imediata, com revisão detalhada de custos, despesas e estrutura de receitas para reequilibrar o resultado financeiro.`
        }
        {' '}Para uma análise mais detalhada, clique nas barras do gráfico e explore os lançamentos que compõem o resultado.
      </div>
    </div>

    <div className="col-md-4 text-end">
      <span className="badge mb-1 d-block" style={{backgroundColor: '#0043c2', color: 'white'}}>
        <i className="fas fa-calculator me-1"></i>
        {dadosDRE.resumo_dre.periodo_dias} dias analisados
      </span>
      <span className="badge" style={{backgroundColor: '#00b5cc', color: 'white'}}>
        <i className="fas fa-database me-1"></i>
        {dadosDRE.resumo_dre.total_movimentos} movimentos
      </span>
    </div>
  </div>
</div>
                      </>
                    ) : (
                      <div className="text-center py-5 text-muted">
                        <i className="fas fa-chart-pie fa-3x mb-3" style={{color: '#ff6b35'}}></i>
                        <p>Aguarde o carregamento automático do DRE</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* ========================================
                IMPOSTOS - FATURAMENTO x IMPOSTOS
            ======================================== */}
            <div className="row mb-4">
              <div className="col-12">
                <div className="card border-0 shadow-sm">
                  <div className="card-header bg-white d-flex justify-content-between align-items-center flex-wrap gap-2">
                    <h5 className="card-title mb-0">
                      <i className="fas fa-file-invoice-dollar me-2 text-primary"></i>
                      Impostos pagos por tipo
                      <span className="badge ms-2" style={{ backgroundColor: '#eef4ff', color: '#00205b', fontWeight: 600, fontSize: '0.7rem', border: '1px solid #d4e2fb' }}>
                        <i className="fas fa-hand-pointer me-1"></i>Clique nas barras
                      </span>
                    </h5>
                    <div className="d-flex align-items-center gap-2 flex-wrap">
                      {[3, 6, 12].map((n) => (
                        <button
                          key={n}
                          type="button"
                          className="btn btn-sm"
                          onClick={() => {
                            const [ini, fim] = rangeUltimosMeses(n);
                            setImpostosIni(ini); setImpostosFim(fim); setImpostosMeses(n);
                            carregarImpostos(ini, fim);
                          }}
                          style={{
                            borderRadius: '999px',
                            fontSize: '0.78rem',
                            fontWeight: 600,
                            border: '1px solid ' + (impostosMeses === n ? '#00205b' : '#d4e2fb'),
                            backgroundColor: impostosMeses === n ? '#00205b' : '#fff',
                            color: impostosMeses === n ? '#fff' : '#00205b',
                          }}
                        >
                          {n} meses
                        </button>
                      ))}
                      <span className="text-muted small ms-1">De</span>
                      <input
                        type="month"
                        className="form-control form-control-sm"
                        style={{ width: 'auto', border: '1px solid #d4e2fb', color: '#00205b', fontWeight: 500 }}
                        value={impostosIni}
                        max={impostosFim || undefined}
                        onChange={(e) => {
                          const ini = e.target.value;
                          setImpostosIni(ini); setImpostosMeses(0);
                          if (ini && impostosFim) carregarImpostos(ini, impostosFim);
                        }}
                      />
                      <span className="text-muted small">até</span>
                      <input
                        type="month"
                        className="form-control form-control-sm"
                        style={{ width: 'auto', border: '1px solid #d4e2fb', color: '#00205b', fontWeight: 500 }}
                        value={impostosFim}
                        min={impostosIni || undefined}
                        onChange={(e) => {
                          const fim = e.target.value;
                          setImpostosFim(fim); setImpostosMeses(0);
                          if (impostosIni && fim) carregarImpostos(impostosIni, fim);
                        }}
                      />
                    </div>
                  </div>
                  <div className="card-body">
                    {carregandoImpostos ? (
                      <div className="text-center py-5">
                        <div className="spinner-border" style={{ color: '#00205b' }}></div>
                        <p className="mt-3 text-muted">Carregando impostos...</p>
                      </div>
                    ) : impostosChart.ok ? (
                      <>
                        <div className="row mb-4">
                          <div className="col-md-6 text-center border-end">
                            <h6 className="text-muted small mb-1">Total de impostos no período</h6>
                            <h4 className="mb-0" style={{ color: '#e5484d' }}>{formatarValor(impostosChart.total)}</h4>
                          </div>
                          <div className="col-md-6 text-center">
                            <h6 className="text-muted small mb-1">Tipos de imposto</h6>
                            <h4 className="mb-0" style={{ color: '#00205b' }}>{impostosChart.tipos.length}</h4>
                          </div>
                        </div>

                        <div className="row">
                          <div className="col-lg-7 mb-3">
                            <h6 className="text-muted small mb-2" style={{ fontWeight: 600 }}>Impostos por tipo</h6>
                            <GraficoImpostos
                              linhas={impostosChart.linhas}
                              tipos={impostosChart.tipos}
                              onSegmentClick={abrirLancImpostos}
                            />
                          </div>
                          <div className="col-lg-5 mb-3">
                            <h6 className="text-muted small mb-2" style={{ fontWeight: 600 }}>Evolução do total pago</h6>
                            <EvolucaoImpostos
                              linhas={impostosChart.linhas}
                              tipos={impostosChart.tipos}
                            />
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className="text-center py-5 text-muted">
                        <i className="fas fa-file-invoice-dollar fa-3x mb-3" style={{ color: '#cbd5e1' }}></i>
                        <p>{impostosChart?.erro ? impostosChart.erro : 'Não foi possível identificar as contas de impostos no DRE do período.'}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* ========================================
                FATURADO x RECEBIDO
            ======================================== */}
            <div className="row mb-4">
              <div className="col-12">
                <div className="card border-0 shadow-sm">
                  <div className="card-header bg-white d-flex justify-content-between align-items-center flex-wrap gap-2">
                    <h5 className="card-title mb-0">
                      <i className="fas fa-file-invoice me-2 text-primary"></i>
                      Faturado (emitido) x Recebido
                      <span className="badge ms-2" style={{ backgroundColor: '#eef4ff', color: '#00205b', fontWeight: 600, fontSize: '0.7rem', border: '1px solid #d4e2fb' }}>
                        <i className="fas fa-hand-pointer me-1"></i>Clique nas barras
                      </span>
                    </h5>
                    <div className="d-flex align-items-center gap-2 flex-wrap">
                      {[3, 6, 12].map((n) => (
                        <button
                          key={n}
                          type="button"
                          className="btn btn-sm"
                          onClick={() => {
                            const [ini, fim] = rangeUltimosMeses(n);
                            setFatRecIni(ini); setFatRecFim(fim); setFatRecMeses(n);
                            carregarFatRec(ini, fim);
                          }}
                          style={{
                            borderRadius: '999px', fontSize: '0.78rem', fontWeight: 600,
                            border: '1px solid ' + (fatRecMeses === n ? '#00205b' : '#d4e2fb'),
                            backgroundColor: fatRecMeses === n ? '#00205b' : '#fff',
                            color: fatRecMeses === n ? '#fff' : '#00205b',
                          }}
                        >
                          {n} meses
                        </button>
                      ))}
                      <span className="text-muted small ms-1">De</span>
                      <input type="month" className="form-control form-control-sm" style={{ width: 'auto', border: '1px solid #d4e2fb', color: '#00205b', fontWeight: 500 }}
                        value={fatRecIni} max={fatRecFim || undefined}
                        onChange={(e) => { const ini = e.target.value; setFatRecIni(ini); setFatRecMeses(0); if (ini && fatRecFim) carregarFatRec(ini, fatRecFim); }} />
                      <span className="text-muted small">até</span>
                      <input type="month" className="form-control form-control-sm" style={{ width: 'auto', border: '1px solid #d4e2fb', color: '#00205b', fontWeight: 500 }}
                        value={fatRecFim} min={fatRecIni || undefined}
                        onChange={(e) => { const fim = e.target.value; setFatRecFim(fim); setFatRecMeses(0); if (fatRecIni && fim) carregarFatRec(fatRecIni, fim); }} />
                    </div>
                  </div>
                  <div className="card-body">
                    {carregandoFatRec ? (
                      <div className="text-center py-5">
                        <div className="spinner-border" style={{ color: '#00205b' }}></div>
                        <p className="mt-3 text-muted">Carregando...</p>
                      </div>
                    ) : fatRec.ok ? (
                      <>
                        <div className="row mb-4">
                          <div className="col-md-3 text-center border-end">
                            <h6 className="text-muted small mb-1">Faturado (emitido)</h6>
                            <h4 className="mb-0" style={{ color: '#00205b' }}>{formatarValor(fatRec.totalFat)}</h4>
                          </div>
                          <div className="col-md-3 text-center border-end">
                            <h6 className="text-muted small mb-1">Recebido</h6>
                            <h4 className="mb-0" style={{ color: '#1fa971', cursor: 'pointer' }} title="Ver lançamentos recebidos do período"
                              onClick={() => abrirLancFatRecPeriodo('recebido')}>
                              {formatarValor(fatRec.totalRec)}
                              <i className="fas fa-up-right-from-square ms-2" style={{ fontSize: 11, opacity: 0.55 }}></i>
                            </h4>
                          </div>
                          <div className="col-md-3 text-center border-end">
                            <h6 className="text-muted small mb-1">Em atraso</h6>
                            <h4 className="mb-0" style={{ color: '#e5484d', cursor: 'pointer' }} title="Ver lançamentos em atraso do período"
                              onClick={() => abrirLancFatRecPeriodo('atrasado')}>
                              {formatarValor(fatRec.totalAtraso || 0)}
                              <i className="fas fa-up-right-from-square ms-2" style={{ fontSize: 11, opacity: 0.55 }}></i>
                            </h4>
                          </div>
                          <div className="col-md-3 text-center">
                            <h6 className="text-muted small mb-1">% recebido</h6>
                            <h4 className="mb-0" style={{ color: '#b5891f' }}>
                              {(fatRec.totalFat > 0 ? (fatRec.totalRec / fatRec.totalFat) * 100 : 0).toFixed(1)}%
                            </h4>
                          </div>
                        </div>

                        <div className="row">
                          <div className="col-lg-7 mb-3">
                            <ResponsiveContainer width="100%" height={360}>
                              <BarChart data={fatRec.linhas.map((l) => {
                                const f = l.faturado || 0; const r = l.recebido || 0; const alvo = Math.max(f, r);
                                return { ...l, gapFat: Math.max(0, alvo - f), gapRec: Math.max(0, alvo - r) };
                              })} margin={{ top: 24, right: 16, left: 8, bottom: 4 }} barGap={8} barCategoryGap="24%">
                                <CartesianGrid strokeDasharray="3 3" stroke="#eef1f6" vertical={false} />
                                <XAxis dataKey="mes" tick={{ fontSize: 12, fill: '#7d8aa3' }} />
                                <YAxis tickFormatter={(value) => formatarValor(value)} tick={{ fontSize: 11, fill: '#7d8aa3' }} />
                                <Tooltip content={renderTooltipFatRec} cursor={{ fill: 'rgba(0,32,91,0.06)' }} />
                                <Legend formatter={(key) => (key === 'faturado' ? 'Faturado (emitido)' : 'Recebido')} payload={[{ value: 'faturado', type: 'square', color: '#00205b' }, { value: 'recebido', type: 'square', color: '#1fa971' }]} />
                                <Bar dataKey="faturado" stackId="fat" name="faturado" fill="#00205b" radius={[6, 6, 0, 0]} maxBarSize={80}
                                  style={{ cursor: 'pointer' }}
                                  onClick={(d) => abrirLancFatRec('faturado', d && d.payload ? d.payload : d)}
                                  label={fatRec.linhas.length <= 4 ? { position: 'top', formatter: (v) => formatarValor(v), fontSize: 11, fill: '#00205b', fontWeight: 600 } : false} />
                                <Bar dataKey="gapFat" stackId="fat" fill="#e6eaf0" radius={[6, 6, 0, 0]} maxBarSize={80} legendType="none" isAnimationActive={false} />
                                <Bar dataKey="recebido" stackId="rec" name="recebido" fill="#1fa971" radius={[6, 6, 0, 0]} maxBarSize={80}
                                  style={{ cursor: 'pointer' }}
                                  onClick={(d) => abrirLancFatRec('recebido', d && d.payload ? d.payload : d)}
                                  label={fatRec.linhas.length <= 4 ? { position: 'top', formatter: (v) => formatarValor(v), fontSize: 11, fill: '#1fa971', fontWeight: 600 } : false} />
                                <Bar dataKey="gapRec" stackId="rec" fill="#e6eaf0" radius={[6, 6, 0, 0]} maxBarSize={80} legendType="none" isAnimationActive={false} />
                              </BarChart>
                            </ResponsiveContainer>
                          </div>
                          <div className="col-lg-5 mb-3">
                            <h6 className="text-muted mb-3 text-center">
                              <i className="fas fa-chart-pie me-2"></i>
                              Recebido · A vencer · Em atraso
                            </h6>
                            <DonutDespesas
                              dados={[
                                { nome: 'Recebido', valor: fatRec.totalRec },
                                { nome: 'A vencer', valor: Math.max(fatRec.totalFat - fatRec.totalRec - (fatRec.totalAtraso || 0), 0) },
                                { nome: 'Em atraso', valor: fatRec.totalAtraso || 0 },
                              ]}
                              total={fatRec.totalFat}
                              cores={['#1fa971', '#f1be48', '#e5484d']}
                              rotulo="Faturado"
                            />
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className="text-center py-5 text-muted">
                        <i className="fas fa-file-invoice fa-3x mb-3" style={{ color: '#cbd5e1' }}></i>
                        <p>{fatRec?.erro ? fatRec.erro : 'Sem notas emitidas no período.'}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* ========================================
                ANÁLISE DE INVESTIMENTOS
            ======================================== */}
           {dadosInvestimentos && dadosInvestimentos.resumo && dadosInvestimentos.contas && dadosInvestimentos.contas.length > 0 && (
  <div className="row mb-4">
    <div className="col-12">
      <div className="card border-0 shadow-sm">
        <div className="card-header bg-white">
          <h5 className="card-title mb-0">
            <i className="fas fa-chart-line me-2"></i>
            Análise de Investimentos
          </h5>
        </div>
        
        <div className="card-body">
          {carregandoInvestimentos ? (
            <div className="text-center py-5">
              <div className="spinner-border" style={{color: '#00205b'}}></div>
              <p className="mt-3 text-muted">Carregando Investimentos...</p>
            </div>
          ) : (
            <>
              {/* Resumo Investimentos */}
              <div className="row mb-4">
                <div className="col-md-3 text-center border-end">
                  <h6 className="text-muted small mb-1">Total Investido</h6>
                  <h5 className="text-primary mb-0">
                    <i className="fas fa-coins me-1"></i>
                    {formatarValor(dadosInvestimentos.resumo.total_investido || 0)}
                  </h5>
                  <small className="text-muted">{dadosInvestimentos.resumo.total_contas || 0} contas</small>
                </div>
                <div className="col-md-3 text-center border-end">
                  <h6 className="text-muted small mb-1">Rendimento Acumulado</h6>
                  <h5 className="text-success mb-0">
                    <i className="fas fa-arrow-trend-up me-1"></i>
                    {formatarValor(dadosInvestimentos.resumo.rendimento_total || 0)}
                  </h5>
                  <small className="text-success">+{(dadosInvestimentos.resumo.rentabilidade_media || 0).toFixed(2)}%</small>
                </div>
                <div className="col-md-3 text-center border-end">
                  <h6 className="text-muted small mb-1">Maior Rendimento</h6>
                  <h5 className="text-success mb-0">
                    {dadosInvestimentos.resumo.maior_rendimento?.conta || 'N/A'}
                  </h5>
                  <small className="text-success">+{(dadosInvestimentos.resumo.maior_rendimento?.percentual || 0).toFixed(2)}%</small>
                </div>
                <div className="col-md-3 text-center">
                  <h6 className="text-muted small mb-1">% do Patrimônio</h6>
                  <h5 className="text-info mb-0">
                    <i className="fas fa-percentage me-1"></i>
                    {(dadosInvestimentos.resumo.percentual_patrimonio || 0).toFixed(1)}%
                  </h5>
                  <small className="text-muted">Diversificação</small>
                </div>
              </div>

              {/* Cards Investimentos */}
              <div className="row">
                {dadosInvestimentos.contas.map((conta, index) => (
                  <div key={index} className="col-lg-4 col-md-6 mb-3">
                    <div 
                      className="card border-0 shadow-sm h-100"
                      style={{ cursor: 'pointer' }}
                      onClick={() => mostrarDetalhesInvestimento(conta)}
                    >
                      <div className="card-header" style={{backgroundColor: (conta.cor || '#00205b') + '15', borderLeft: `4px solid ${conta.cor || '#00205b'}`}}>
                        <h6 className="mb-0 d-flex align-items-center">
                          <i className={`${conta.icone || 'fas fa-chart-line'} me-2`} style={{color: conta.cor || '#00205b'}}></i>
                          <div className="flex-grow-1">
                            <div style={{fontSize: '13px', fontWeight: '600'}}>
                              {conta.nome_curto || conta.nome || 'Investimento'}
                            </div>
                            <small className="text-muted" style={{fontSize: '10px'}}>
                              {conta.tipo || 'Investimento'}
                            </small>
                          </div>
                        </h6>
                      </div>
                      <div className="card-body">
                        <div className="text-center mb-3">
                          <small className="text-muted d-block mb-1">Saldo Disponível</small>
                          <h5 className="text-primary mb-0">
                            {formatarValor(conta.saldo_disponivel || 0)}
                          </h5>
                        </div>

                        <div className="row text-center">
                          <div className="col-6 border-end">
                            <small className="text-muted d-block">Rendimento</small>
                            <div className="text-success">
                              {formatarValor(conta.rendimento_acumulado || 0)}
                            </div>
                            <small className="text-success">+{(conta.rentabilidade || 0).toFixed(2)}%</small>
                          </div>
                          <div className="col-6">
                            <small className="text-muted d-block">% Total Inv.</small>
                            <div className="text-info">
                              {(conta.percentual_total_investido || 0).toFixed(1)}%
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

             {/* Insight Investimentos */}
<div className="alert border-start border-4 mb-0 mt-3 fmp-insight">
  <div className="row align-items-center">
    <div className="col-md-9">
      <h6 className="alert-heading">
        <i className="fas fa-lightbulb me-2 text-warning"></i>
        Análise Inteligente - Investimentos
      </h6>
      <small>
        <strong>Performance dos Investimentos:</strong>{' '}
        A carteira apresenta uma rentabilidade média de {(dadosInvestimentos.resumo.rentabilidade_media || 0).toFixed(2)}%, 
        acumulando um resultado total de {formatarValor(dadosInvestimentos.resumo.rendimento_total || 0)} no período analisado. 
        Esse desempenho reflete a eficiência da alocação atual e o comportamento dos ativos ao longo do tempo.

        {dadosInvestimentos.resumo.maior_rendimento && (
          <>
            {' '}O principal destaque da carteira é <strong>{dadosInvestimentos.resumo.maior_rendimento.conta}</strong>, 
            com rentabilidade de {(dadosInvestimentos.resumo.maior_rendimento.percentual || 0).toFixed(2)}%, 
            indicando um ativo ou estratégia com performance superior aos demais.
          </>
        )}

        {' '}De forma geral, é importante acompanhar a consistência desses resultados e avaliar oportunidades de 
        rebalanceamento da carteira, buscando otimizar a relação entre risco e retorno, além de evitar concentração excessiva em um único ativo ou conta.
      </small>
    </div>

    <div className="col-md-3 text-end">
      <span className="badge" style={{backgroundColor: '#00205b', color: 'white'}}>
        <i className="fas fa-chart-line me-1"></i>
        {dadosInvestimentos.resumo.total_contas || 0} Contas Monitoradas
      </span>
    </div>
  </div>
</div>
            </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {/* ========================================
            FOOTER - COPYRIGHT
        ======================================== */}
        <div className="row mt-2 mb-4">
          <div className="col-12">
            <div className="card border-0" style={{ background: 'radial-gradient(120% 140% at 80% -10%, #0a2a6b 0%, #04122c 55%, #030d22 100%)' }}>
              <div className="card-body py-3">
                <div className="text-center">
                  <p className="mb-0" style={{color: '#ffffff', fontSize: '0.9rem'}}>
                    <i className="fas fa-shield-alt me-2"></i>
                    <strong>Financial Management Platform - FMP</strong>
                    <span className="mx-2">|</span>
                    Todos os direitos reservados © 2025
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ========================================
            MODAIS
        ======================================== */}
        
        {/* Modal Detalhes Dia (Fluxo Diário) */}
        {modalFluxoDiario && diaDetalhes && (
          <div 
            className="modal fade show d-block" 
            style={{
              backgroundColor: 'rgba(0,0,0,0.7)', 
              zIndex: 1060
            }} 
            onClick={() => setModalFluxoDiario(false)}
          >
            <div className="modal-dialog modal-lg modal-dialog-scrollable" onClick={(e) => e.stopPropagation()}>
              <div className="modal-content">
                <div className="modal-header" style={{backgroundColor: '#ffffff', borderBottom: '1px solid #e9ecef'}}>
                  <h5 className="modal-title mb-0 fw-bold" style={{color: '#212529'}}>
                    <i className="fas fa-calendar-alt me-2" style={{color: '#212529'}}></i>
                    Detalhes - {diaDetalhes.data_completa || diaDetalhes.data}
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setModalFluxoDiario(false)}
                  ></button>
                </div>
                
                <div className="modal-header bg-light">
                  <div className="w-100">
                    <div className="row text-center">
                      {diaDetalhes.dia_semana && diaDetalhes.dia_semana !== 'Período Mensal' && (
                        <div className="col-md-3">
                          <small className="text-muted d-block">Dia da Semana</small>
                          <h6 className="text-primary mb-0">
                            <i className="fas fa-calendar me-1"></i>
                            {diaDetalhes.dia_semana}
                          </h6>
                        </div>
                      )}
                      <div className={`${diaDetalhes.dia_semana === 'Período Mensal' ? 'col-md-4' : 'col-md-3'}`}>
                        <small className="text-muted d-block">Entradas</small>
                        <h6 className="text-success mb-0">
                          <i className="fas fa-plus-circle me-1"></i>
                        </h6>
                                             </div>
                                             <div className={`${diaDetalhes.dia_semana === 'Período Mensal' ? 'col-md-4' : 'col-md-3'}`}>
                                               <small className="text-muted d-block">Resgates</small>
                                               <h6 className="text-warning mb-0">
                                                 <i className="fas fa-minus-circle me-1"></i>
                                                 {formatarValor(diaDetalhes.saidas)}
                                               </h6>
                                             </div>
                                             <div className={`${diaDetalhes.dia_semana === 'Período Mensal' ? 'col-md-4' : 'col-md-3'}`}>
                                               <small className="text-muted d-block">Saldo Líquido</small>
                                               <h6 className={`mb-0 ${diaDetalhes.saldo_dia >= 0 ? 'text-success' : 'text-danger'}`}>
                                                 <i className={`fas ${diaDetalhes.saldo_dia >= 0 ? 'fa-plus' : 'fa-minus'} me-1`}></i>
                                                 {formatarValor(diaDetalhes.saldo_dia)}
                                               </h6>
                                             </div>
                                           </div>
                                         </div>
                                       </div>
                                       <div className="modal-body" style={{maxHeight: '500px', overflowY: 'auto'}}>
              {diaDetalhes.movimentos && diaDetalhes.movimentos.length > 0 ? (
                <>
                  <div className="alert alert-info border-start border-4 mb-3" style={{borderColor: '#005f6b'}}>
                    <small>
                      <strong><i className="fas fa-info-circle me-1"></i>Total de movimentos:</strong> {diaDetalhes.qtd_movimentos}
                      <span className="ms-3">
                        {diaDetalhes.eh_futuro ? (
                          <span className="badge bg-warning text-dark">
                            <i className="fas fa-clock me-1"></i>Previsão
                          </span>
                        ) : (
                          <span className="badge bg-success">
                            <i className="fas fa-check me-1"></i>Realizado
                          </span>
                        )}
                      </span>
                    </small>
                  </div>
                  
                  <div className="table-responsive">
                    <table className="table table-hover table-sm">
                      <thead className="table-light sticky-top">
                        <tr>
                          <th width="35%">Descrição</th>
                          <th width="20%">Categoria</th>
                          <th width="13%" className="text-end">Valor</th>
                          <th width="12%" className="text-center">Tipo</th>
                          <th width="20%" className="text-center">Classificação</th>
                        </tr>
                      </thead>
                      <tbody>
                        {diaDetalhes.movimentos.map((movimento, index) => (
                          <tr key={index}>
                            <td>
                              <small title={movimento.descricao_completa || movimento.descricao}>
                                {movimento.descricao}
                              </small>
                            </td>
                            <td>
                              <small className="text-muted">{movimento.categoria}</small>
                            </td>
                            <td className="text-end">
                              <strong className={movimento.tipo === 'ENTRADA' || movimento.tipo === 'RECEITA' ? 'text-success' : 'text-danger'}>
                                {formatarValor(Math.abs(movimento.valor))}
                              </strong>
                            </td>
                            <td className="text-center">
                              <span className={`badge badge-sm ${movimento.tipo === 'ENTRADA' || movimento.tipo === 'RECEITA' ? 'bg-success' : 'bg-danger'}`}>
                                <i className={`fas ${movimento.tipo === 'ENTRADA' || movimento.tipo === 'RECEITA' ? 'fa-arrow-down' : 'fa-arrow-up'} me-1`}></i>
                                {movimento.tipo === 'ENTRADA' || movimento.tipo === 'RECEITA' ? 'ENT' : 'SAÍ'}
                              </span>
                            </td>
                            <td className="text-center">
                              {movimento.badge_tipo ? (
                                <span className={`badge badge-sm ${movimento.cor_badge}`}>
                                  {movimento.badge_tipo}
                                </span>
                              ) : (
                                <span className={`badge badge-sm ${movimento.status === 'REALIZADO' ? 'bg-success' : 'bg-warning text-dark'}`}>
                                  {movimento.status === 'REALIZADO' ? 'Real' : 'Prev'}
                                </span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              ) : (
                <div className="text-center py-5 text-muted">
                  <i className="fas fa-inbox fa-3x mb-3" style={{color: '#002058'}}></i>
                  <p>Nenhum movimento registrado para este dia</p>
                </div>
              )}
            </div>
            
            <div className="modal-footer">
              <button 
                type="button" 
                className="btn btn-secondary" 
                onClick={() => setModalFluxoDiario(false)}
              >
                <i className="fas fa-times me-1"></i>
                Fechar
              </button>
            </div>
          </div>
        </div>
      </div>
    )}

    {/* Modal Detalhes Categoria DRE */}
    {modalCategoria && categoriaSelecionada && (
      <div 
        className="modal fade show d-block" 
        style={{backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1070}}
        onClick={() => setModalCategoria(false)}
      >
        <div className="modal-dialog modal-lg modal-dialog-scrollable" onClick={(e) => e.stopPropagation()}>
          <div className="modal-content">
            <div className="modal-header" style={{backgroundColor: '#ffffff', borderBottom: '1px solid #e9ecef'}}>
              <h5 className="modal-title mb-0 fw-bold" style={{color: '#212529'}}>
                <i className="fas fa-list me-2" style={{color: '#212529'}}></i>
                Detalhes: {categoriaSelecionada.categoria}
              </h5>
              <button
                type="button"
                className="btn-close"
                onClick={() => setModalCategoria(false)}
              ></button>
            </div>
            
            <div className="modal-header bg-light">
              <div className="w-100 text-center">
                <div className="row">
                  <div className="col-md-4">
                    <small className="text-muted d-block">Total</small>
                    <h5 className={`mb-0 ${
                      (dadosDRE?.detalhes_por_categoria?.[categoriaSelecionada.categoria]?.resumo?.tipo === 'RECEITA') ||
                      (dadosDRE?.detalhes_por_categoria_receita?.[categoriaSelecionada.categoria]?.resumo?.tipo === 'RECEITA')
                      ? 'text-success' : 'text-danger'
                    }`}>
                      {formatarValor(categoriaSelecionada.total)}
                    </h5>
                  </div>
                  <div className="col-md-4">
                    <small className="text-muted d-block">Quantidade</small>
                    <h5 className="text-primary mb-0">
                      {categoriaSelecionada.quantidade} movimentos
                    </h5>
                  </div>
                  <div className="col-md-4">
                    <small className="text-muted d-block">Tipo</small>
                    <span className={`badge ${
                      (dadosDRE?.detalhes_por_categoria?.[categoriaSelecionada.categoria]?.resumo?.tipo === 'RECEITA') ||
                      (dadosDRE?.detalhes_por_categoria_receita?.[categoriaSelecionada.categoria]?.resumo?.tipo === 'RECEITA')
                      ? 'bg-success' : 'bg-danger'
                    }`}>
                      {(dadosDRE?.detalhes_por_categoria?.[categoriaSelecionada.categoria]?.resumo?.tipo) ||
                       (dadosDRE?.detalhes_por_categoria_receita?.[categoriaSelecionada.categoria]?.resumo?.tipo) ||
                       'N/A'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="modal-body" style={{maxHeight: '400px', overflowY: 'auto'}}>
              {(() => {
                const detalhes = dadosDRE?.detalhes_por_categoria?.[categoriaSelecionada.categoria] ||
                                dadosDRE?.detalhes_por_categoria_receita?.[categoriaSelecionada.categoria];
                
                if (detalhes?.movimentos && detalhes.movimentos.length > 0) {
                  return (
                    <div className="table-responsive">
                      <table className="table table-hover table-sm">
                        <thead className="table-light sticky-top">
                          <tr>
                            <th width="15%">Data</th>
                            <th width="55%">Descrição</th>
                            <th width="15%" className="text-end">Valor</th>
                            <th width="15%" className="text-center">Tipo</th>
                          </tr>
                        </thead>
                        <tbody>
                          {detalhes.movimentos.map((movimento, index) => (
                            <tr key={index}>
                              <td>
                                <small className="text-muted">{movimento.data}</small>
                              </td>
                              <td>
                                <small>{movimento.descricao}</small>
                                {movimento.categoria && (
                                  <div>
                                    <small className="text-muted fst-italic">
                                      {movimento.categoria}
                                    </small>
                                  </div>
                                )}
                              </td>
                              <td className="text-end">
                                <strong className={movimento.tipo === 'RECEITA' ? 'text-success' : 'text-danger'}>
                                  {formatarValor(Math.abs(movimento.valor))}
                                </strong>
                              </td>
                              <td className="text-center">
                                <span className={`badge badge-sm ${movimento.tipo === 'RECEITA' ? 'bg-success' : 'bg-danger'}`}>
                                  {movimento.tipo === 'RECEITA' ? 'REC' : 'DESP'}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  );
                } else {
                  return (
                    <div className="text-center py-5 text-muted">
                      <i className="fas fa-inbox fa-3x mb-3" style={{color: '#ff6b35'}}></i>
                      <p>Nenhum movimento encontrado</p>
                    </div>
                  );
                }
              })()}
            </div>
            
            <div className="modal-footer">
              <button 
                type="button" 
                className="btn btn-secondary" 
                onClick={() => setModalCategoria(false)}
              >
                <i className="fas fa-times me-1"></i>
                Fechar
              </button>
            </div>
          </div>
        </div>
      </div>
    )}

    {/* Modal Relatório Completo (todas as categorias, do menor ao maior) */}
    {modalRelatorio && (
      <div
        className="modal fade show d-block"
        style={{ backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 1060 }}
        onClick={() => setModalRelatorio(false)}
      >
        <div className="modal-dialog modal-lg modal-dialog-scrollable" onClick={(e) => e.stopPropagation()}>
          <div className="modal-content">
            <div className="modal-header" style={{ backgroundColor: '#ffffff', borderBottom: '1px solid #e9ecef' }}>
              <h5 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}>
                <i className="fas fa-list me-2" style={{ color: '#212529' }}></i>
                {relatorioDados.titulo}
              </h5>
              <button type="button" className="btn-close" onClick={() => setModalRelatorio(false)}></button>
            </div>

            <div className="modal-header bg-light">
              <div className="w-100 d-flex justify-content-between align-items-center">
                <small className="text-muted">
                  <i className="fas fa-sort-amount-down-alt me-1"></i>
                  {relatorioDados.itens.length} categorias · do maior ao menor
                </small>
                <strong
                  className={relatorioDados.tipo === 'despesas' ? 'text-danger' : 'text-success'}
                  style={{ fontVariantNumeric: 'tabular-nums' }}
                >
                  {formatarValor(relatorioDados.itens.reduce((s, it) => s + (it.valor || 0), 0))}
                </strong>
              </div>
            </div>

            <div className="modal-body" style={{ maxHeight: '500px', overflowY: 'auto' }}>
              {relatorioDados.itens.length > 0 ? (
                <div className="table-responsive">
                  <table className="table table-hover table-sm align-middle">
                    <thead className="table-light sticky-top">
                      <tr>
                        <th style={{ width: '7%' }}>#</th>
                        <th>Categoria</th>
                        <th className="text-center" style={{ width: '14%' }}>Movs.</th>
                        <th className="text-end" style={{ width: '24%' }}>Valor</th>
                      </tr>
                    </thead>
                    <tbody>
                      {relatorioDados.itens.map((it, i) => (
                        <tr
                          key={i}
                          style={{ cursor: 'pointer' }}
                          title="Clique para ver os lançamentos"
                          onClick={() => {
                            const totalRef = relatorioDados.tipo === 'despesas'
                              ? (dadosDRE?.resumo_dre?.total_despesas || 1)
                              : (dadosDRE?.resumo_dre?.total_receitas || 1);
                            setCategoriaSelecionada({
                              categoria: it.categoria_completa,
                              total: it.valor,
                              quantidade: it.quantidade,
                              percentual: ((it.valor / totalRef) * 100).toFixed(1)
                            });
                            setModalCategoria(true);
                          }}
                        >
                          <td className="text-muted">{i + 1}</td>
                          <td>
                            <strong>{limparCategoria(it.categoria_completa || it.categoria)}</strong>
                            <div><small className="text-muted">{it.categoria_completa || it.categoria}</small></div>
                          </td>
                          <td className="text-center">
                            <span className="badge bg-light text-dark">{it.quantidade}</span>
                          </td>
                          <td className="text-end">
                            <strong
                              className={relatorioDados.tipo === 'despesas' ? 'text-danger' : 'text-success'}
                              style={{ fontVariantNumeric: 'tabular-nums' }}
                            >
                              {formatarValor(it.valor)}
                            </strong>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-5 text-muted">
                  <i className="fas fa-inbox fa-3x mb-3"></i>
                  <p>Nenhuma categoria no período</p>
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={() => setModalRelatorio(false)}>
                <i className="fas fa-times me-1"></i>Fechar
              </button>
            </div>
          </div>
        </div>
      </div>
    )}

    {/* Modal Lançamentos do card de Impostos */}
    {impostosLanc.aberto && (
      <div
        className="modal fade show d-block"
        style={{ backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 1060 }}
        onClick={() => setImpostosLanc((prev) => ({ ...prev, aberto: false }))}
      >
        <div className="modal-dialog modal-dialog-scrollable" style={{ maxWidth: '1100px', width: '95%' }} onClick={(e) => e.stopPropagation()}>
          <div className="modal-content">
            <div className="modal-header" style={{ backgroundColor: '#ffffff', borderBottom: '1px solid #e9ecef' }}>
              <h5 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}>
                <i className="fas fa-list me-2" style={{ color: '#212529' }}></i>
                {impostosLanc.titulo}
              </h5>
              <button type="button" className="btn-close" onClick={() => setImpostosLanc((prev) => ({ ...prev, aberto: false }))}></button>
            </div>
            <div className="modal-header bg-light">
              <div className="w-100 d-flex justify-content-between align-items-center">
                <small className="text-muted"><i className="fas fa-receipt me-1"></i>{impostosLanc.itens.length} lançamento(s)</small>
                <strong style={{ fontVariantNumeric: 'tabular-nums', color: '#00205b' }}>{formatarValor(impostosLanc.total)}</strong>
              </div>
            </div>
            <div className="modal-body" style={{ maxHeight: '500px', overflowY: 'auto' }}>
              {impostosLanc.carregando ? (
                <div className="text-center py-5">
                  <div className="spinner-border" style={{ color: '#00205b' }}></div>
                  <p className="mt-3 text-muted">Carregando lançamentos...</p>
                </div>
              ) : impostosLanc.itens.length > 0 ? (
                <div className="table-responsive">
                  <table className="table table-hover table-sm align-middle">
                    <thead className="table-light sticky-top">
                      <tr>
                        <th style={{ width: '11%' }}>Data</th>
                        {impLancTemTributo && <th style={{ width: '16%' }}>Tributo</th>}
                        <th>Cliente / Fornecedor</th>
                        <th style={{ width: '12%' }}>{impLancTemNF ? 'NF / Doc' : 'Documento'}</th>
                        {impLancTemVenc && <th style={{ width: '11%' }}>Vencimento</th>}
                        <th style={{ width: '13%' }}>Status</th>
                        <th className="text-end" style={{ width: '16%' }}>Valor</th>
                      </tr>
                    </thead>
                    <tbody>
                      {impostosLanc.itens.map((lc, i) => (
                        <tr key={i}>
                          <td>{lc.data ? new Date(String(lc.data).slice(0, 10) + 'T00:00:00').toLocaleDateString('pt-BR') : '—'}</td>
                          {impLancTemTributo && (
                            <td>
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                <span style={{ width: 9, height: 9, borderRadius: '50%', background: lc.cor || '#888', flex: 'none' }}></span>
                                {lc.tributo || '—'}
                              </span>
                            </td>
                          )}
                          <td>{lc.nome || '—'}</td>
                          <td className="text-muted">{lc.nf || lc.numero_documento || '—'}</td>
                          {impLancTemVenc && <td>{lc.vencimento ? new Date(String(lc.vencimento).slice(0, 10) + 'T00:00:00').toLocaleDateString('pt-BR') : '—'}</td>}
                          <td><span className="badge bg-light text-dark">{lc.status || '—'}</span></td>
                          <td className="text-end fw-bold" style={{ color: (lc.valor || 0) < 0 ? '#e5484d' : '#1fa971', fontVariantNumeric: 'tabular-nums' }}>
                            {formatarValor(lc.valor)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-5 text-muted">
                  <i className="fas fa-inbox fa-3x mb-3"></i>
                  <p>Nenhum lançamento neste mês.</p>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={() => setImpostosLanc((prev) => ({ ...prev, aberto: false }))}>
                <i className="fas fa-times me-1"></i>Fechar
              </button>
            </div>
          </div>
        </div>
      </div>
    )}

    {/* Modal Detalhes Investimento */}
    {modalInvestimento && contaSelecionada && (
      <div 
        className="modal fade show d-block" 
        style={{
          backgroundColor: 'rgba(0,0,0,0.5)', 
          zIndex: 1050
        }} 
        onClick={() => setModalInvestimento(false)}
      >
        <div className="modal-dialog modal-xl modal-dialog-scrollable" onClick={(e) => e.stopPropagation()}>
          <div className="modal-content">
            <div className="modal-header" style={{backgroundColor: '#ffffff', borderBottom: '1px solid #e9ecef'}}>
              <h5 className="modal-title mb-0 fw-bold" style={{color: '#212529'}}>
                <i className={`${contaSelecionada.icone || 'fas fa-chart-line'} me-2`} style={{color: '#212529'}}></i>
                {contaSelecionada.nome_curto || contaSelecionada.nome || 'Investimento'}
              </h5>
              <button
                type="button"
                className="btn-close"
                onClick={() => setModalInvestimento(false)}
              ></button>
            </div>
            
            <div className="modal-body" style={{ background: '#f7f8fb' }}>
              {(() => {
                const ymSet5 = new Set(janelaMeses(5).map((w) => w.ym));
                const per = agregarJanelaInvestimento(contaSelecionada, ymSet5);
                return (
                  <>
                    {/* KPIs principais (Saldo e % são de saldo; Rendimento e Movimentos = últimos 5 meses) */}
                    <div className="row g-3 mb-3">
                      {[
                        { rot: 'Saldo Total', val: formatarValor(contaSelecionada.saldo_disponivel || 0), cor: '#00205b', sub: contaSelecionada.tipo || 'Investimento' },
                        { rot: 'Rendimento (5m)', val: formatarValor(per.rendLiquido), cor: '#1fa971', sub: `+${per.rentab.toFixed(2)}% no período` },
                        { rot: '% do total investido', val: `${(contaSelecionada.percentual_total_investido || 0).toFixed(1)}%`, cor: '#00b5cc', sub: 'da carteira' },
                        { rot: 'Movimentos (5m)', val: per.qtdTotal, cor: '#b5891f', sub: 'lançamentos' },
                      ].map((k, i) => (
                        <div className="col-md-3 col-6" key={i}>
                          <div style={{ background: '#fff', border: '1px solid #eef1f6', borderRadius: 12, padding: '14px 16px', height: '100%' }}>
                            <div style={{ fontSize: 11.5, color: '#7d8aa3', marginBottom: 4 }}>{k.rot}</div>
                            <div style={{ fontSize: 20, fontWeight: 700, color: k.cor, fontVariantNumeric: 'tabular-nums', lineHeight: 1.1 }}>{k.val}</div>
                            <div style={{ fontSize: 11, color: '#98a4b8', marginTop: 3 }}>{k.sub}</div>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Resumo por tipo de movimento (últimos 5 meses) */}
                    <div style={{ background: '#fff', border: '1px solid #eef1f6', borderRadius: 12, padding: '14px 16px' }} className="mb-3">
                      <div className="row text-center g-2">
                        {[
                          { rot: 'Aplicações', val: per.totalEntradas, qtd: per.qtdEntradas, cor: '#00b5cc' },
                          { rot: 'Resgates', val: per.totalSaidas, qtd: per.qtdSaidas, cor: '#f1be48' },
                          { rot: 'Rendimentos', val: per.totalRendimentos, qtd: per.qtdRendimentos, cor: '#1fa971' },
                          { rot: 'Impostos', val: per.totalImpostos, qtd: per.qtdImpostos, cor: '#e5484d' },
                        ].map((m, i) => (
                          <div className="col-md-3 col-6" key={i}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                              <span style={{ width: 8, height: 8, borderRadius: '50%', background: m.cor, display: 'inline-block' }}></span>
                              <small className="text-muted">{m.rot}</small>
                            </div>
                            <div style={{ fontWeight: 700, color: m.cor, fontSize: 15, fontVariantNumeric: 'tabular-nums' }}>{formatarValor(m.val)}</div>
                            <small className="text-muted" style={{ fontSize: 11 }}>{m.qtd} mov.</small>
                          </div>
                        ))}
                      </div>
                      <div style={{ borderTop: '1px solid #eef1f6', marginTop: 12, paddingTop: 10, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span className="text-muted small">Rendimento líquido (rendimentos − impostos)</span>
                        <b style={{ color: per.rendLiquido >= 0 ? '#1fa971' : '#e5484d', fontSize: 16, fontVariantNumeric: 'tabular-nums' }}>
                          {formatarValor(per.rendLiquido)}
                        </b>
                      </div>
                    </div>
                  </>
                );
              })()}

              {/* Gráfico de Barras MENSAL — janela contínua dos últimos 5 meses */}
              {contaSelecionada.dados_grafico_mensal && contaSelecionada.dados_grafico_mensal.length > 0 && (() => {
                const mapaMes = {};
                contaSelecionada.dados_grafico_mensal.forEach((d) => { mapaMes[d.mes] = d; });
                // Últimos 5 meses a partir da data atual (mês corrente e 4 anteriores)
                const dados5m = janelaMeses(5).map((w) => {
                  const d = mapaMes[w.ym] || {};
                  return { mes: w.label, mesRaw: w.ym, entradas: d.entradas || 0, saidas: d.saidas || 0, rendimentos: d.rendimentos || 0, impostos: d.impostos || 0, rendimento_liquido: d.rendimento_liquido || 0 };
                });
                const abrirMes = (data, soRend) => {
                  const [ano, mes] = (data.mesRaw || '').split('-');
                  const mesAnoGrafico = `${mes}/${ano}`;
                  const movimentosMes = contaSelecionada.movimentos.lista.filter((m) => {
                    const dataMov = m.data.split('/');
                    const bate = `${dataMov[1]}/${dataMov[2]}` === mesAnoGrafico;
                    return soRend ? (bate && (m.tipo_movimento === 'Rendimentos' || m.tipo_movimento === 'Impostos')) : bate;
                  });
                  setDiaDetalhes({
                    data: `Mês ${data.mes}${soRend ? ' - Rendimentos' : ''}`,
                    dia_semana: 'Período Mensal',
                    entradas: soRend ? data.rendimentos : data.entradas,
                    saidas: soRend ? data.impostos : data.saidas,
                    saldo_dia: soRend ? data.rendimento_liquido : (data.entradas - data.saidas + data.rendimento_liquido),
                    qtd_movimentos: movimentosMes.length,
                    movimentos: movimentosMes,
                  });
                  setModalFluxoDiario(true);
                };
                return (
                  <div style={{ background: '#fff', border: '1px solid #eef1f6', borderRadius: 12, padding: '16px' }} className="mb-3">
                    <div className="d-flex align-items-center justify-content-between mb-3">
                      <h6 className="mb-0" style={{ fontWeight: 600, color: '#475467' }}>
                        <i className="fas fa-chart-bar me-2" style={{ color: '#00205b' }}></i>
                        Movimentação mensal — últimos 5 meses
                      </h6>
                      <span className="badge" style={{ backgroundColor: '#eaf2ff', color: '#00205b', fontWeight: 500 }}>
                        <i className="fas fa-hand-pointer me-1"></i>Clique nas barras
                      </span>
                    </div>

                    <ResponsiveContainer width="100%" height={340}>
                      <ComposedChart data={dados5m} margin={{ top: 8, right: 12, left: 4, bottom: 4 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#eef1f6" vertical={false} />
                        <XAxis dataKey="mes" tick={{ fontSize: 11, fill: '#7d8aa3' }} axisLine={false} tickLine={false} />
                        <YAxis tickFormatter={(value) => `${(value / 1000).toFixed(0)}k`} tick={{ fontSize: 11, fill: '#7d8aa3' }} axisLine={false} tickLine={false} />
                        <Tooltip
                          formatter={(value, name) => {
                            const nomes = { entradas: 'Aplicações', saidas: 'Resgates', rendimentos: 'Rendimentos', rendimento_liquido: 'Rendimento Líquido' };
                            return [formatarValor(value), nomes[name] || name];
                          }}
                          labelFormatter={(mes) => `Período: ${mes}`}
                        />
                        <Legend
                          formatter={(value) => {
                            const nomes = { entradas: 'Aplicações', saidas: 'Resgates', rendimentos: 'Rendimentos', rendimento_liquido: 'Rendimento Líquido' };
                            return nomes[value] || value;
                          }}
                        />

                        <Bar dataKey="entradas" name="entradas" fill="#00b5cc" radius={[4, 4, 0, 0]} maxBarSize={46} style={{ cursor: 'pointer' }} onClick={(data) => abrirMes(data, false)} />
                        <Bar dataKey="saidas" name="saidas" fill="#f1be48" radius={[4, 4, 0, 0]} maxBarSize={46} style={{ cursor: 'pointer' }} onClick={(data) => abrirMes(data, false)} />
                        <Bar dataKey="rendimentos" name="rendimentos" fill="#1fa971" radius={[4, 4, 0, 0]} maxBarSize={46} style={{ cursor: 'pointer' }} onClick={(data) => abrirMes(data, true)} />
                        <Line type="monotone" dataKey="rendimento_liquido" name="rendimento_liquido" stroke="#00205b" strokeWidth={3} dot={{ r: 4, fill: '#fff', stroke: '#00205b', strokeWidth: 2 }} activeDot={{ r: 6, fill: '#00205b', stroke: '#fff', strokeWidth: 2 }} />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </div>
                );
              })()}

              {/* Tabela de movimentação (últimos 5 meses) */}
              {contaSelecionada.movimentos?.lista && contaSelecionada.movimentos.lista.length > 0 && (() => {
                const corTipo = { Entradas: '#00b5cc', 'Saídas': '#f1be48', Rendimentos: '#1fa971', Impostos: '#e5484d' };
                const rotTipo = { Entradas: 'Aplicação', 'Saídas': 'Resgate', Rendimentos: 'Rendimento', Impostos: 'Imposto' };
                const parseData = (d) => { const p = (d || '').split('/'); return p.length === 3 ? `${p[2]}${p[1]}${p[0]}` : ''; };
                // Últimos 5 meses a partir da data atual (mesma janela do gráfico)
                const janela5 = new Set(janelaMeses(5).map((w) => w.ym));
                const noPeriodo = (d) => { const p = (d || '').split('/'); return p.length === 3 && janela5.has(`${p[2]}-${p[1]}`); };
                const linhasMov = [...contaSelecionada.movimentos.lista]
                  .filter((m) => noPeriodo(m.data))
                  .sort((a, b) => parseData(b.data).localeCompare(parseData(a.data)));
                return (
                  <div style={{ background: '#fff', border: '1px solid #eef1f6', borderRadius: 12, padding: '16px' }}>
                    <div className="d-flex align-items-center justify-content-between mb-3">
                      <h6 className="mb-0" style={{ fontWeight: 600, color: '#475467' }}>
                        <i className="fas fa-list me-2" style={{ color: '#00205b' }}></i>
                        Movimentação — últimos 5 meses
                      </h6>
                      <span className="text-muted small">{linhasMov.length} lançamentos</span>
                    </div>
                    <div style={{ maxHeight: 360, overflowY: 'auto' }}>
                      <table className="table table-sm table-hover align-middle mb-0">
                        <thead style={{ position: 'sticky', top: 0, background: '#fff', zIndex: 1 }}>
                          <tr style={{ color: '#7d8aa3', fontSize: 12 }}>
                            <th style={{ borderBottom: '2px solid #eef1f6' }}>Data</th>
                            <th style={{ borderBottom: '2px solid #eef1f6' }}>Descrição</th>
                            <th style={{ borderBottom: '2px solid #eef1f6' }}>Tipo</th>
                            <th className="text-end" style={{ borderBottom: '2px solid #eef1f6' }}>Valor</th>
                          </tr>
                        </thead>
                        <tbody>
                          {linhasMov.map((m, i) => {
                            const cor = corTipo[m.tipo_movimento] || '#7d8aa3';
                            const positivo = (m.valor || 0) >= 0;
                            return (
                              <tr key={i}>
                                <td style={{ fontSize: 13, whiteSpace: 'nowrap', color: '#475467' }}>{m.data}</td>
                                <td style={{ fontSize: 13, color: '#475467' }}>
                                  {m.descricao}
                                  {m.categoria && m.categoria !== 'Sem categoria' && (
                                    <small className="text-muted d-block" style={{ fontSize: 11 }}>{m.categoria}</small>
                                  )}
                                </td>
                                <td>
                                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: cor, fontWeight: 600 }}>
                                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: cor, display: 'inline-block' }}></span>
                                    {rotTipo[m.tipo_movimento] || m.tipo_movimento}
                                  </span>
                                </td>
                                <td className="text-end" style={{ fontSize: 13, fontWeight: 600, color: positivo ? '#1fa971' : '#e5484d', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                                  {formatarValor(m.valor || 0)}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                );
              })()}
            </div>
            <div className="modal-footer">
              <button 
                type="button" 
                className="btn btn-secondary" 
                onClick={() => setModalInvestimento(false)}
              >
                <i className="fas fa-times me-1"></i>
                Fechar
              </button>
            </div>
          </div>
        </div>
      </div>
    )}
        </div>
  </div>
  <AssistenteFinanceiro clienteId={cliente_id} />
</div>
);
};
export default DashboardCliente;